import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Multilanguage (#123): lightweight i18n. A LanguageProvider holds the active language
// (persisted in localStorage so it survives sessions, synced across tabs) and exposes a
// t(key, fallback) lookup. Strings live in the dictionaries below — adding coverage is just
// adding keys. User-generated content (chat names, messages, filenames) is NEVER translated.

const STORAGE_KEY = 'omnis:language';
export const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
];

// Exported so `i18n.test.js` can assert the en/es contract and cross-check every
// t() call site's fallback against the English value (see that file's header).
export const STRINGS = {
    en: {
        newChat: 'New Chat',
        fileManager: 'File Manager',
        select: 'Select',
        // ── SHARED vocabulary ("Spanish" full-localization pass) ────────────
        // One key per label that recurs across many surfaces. Use these ONLY when
        // the English is byte-identical at the call site — a near-match gets its
        // own feature-scoped key instead, so English can never silently drift.
        commonCancel: 'Cancel',
        commonSave: 'Save',
        commonSaving: 'Saving…',
        commonDelete: 'Delete',
        commonConfirm: 'Confirm',
        commonClose: 'Close',
        commonEdit: 'Edit',
        commonRename: 'Rename',
        commonRemove: 'Remove',
        commonAdd: 'Add',
        commonBack: 'Back',
        commonNext: 'Next',
        commonOpen: 'Open',
        commonShare: 'Share',
        commonDownload: 'Download',
        commonPreview: 'Preview',
        commonUpload: 'Upload',
        commonRetry: 'Retry',
        statusConnected: 'Connected',
        statusLoading: 'Loading model...',
        statusError: 'Connection error',
        statusChecking: 'Connecting...',
        // Error Clean up #439 — the ONE sentence any technical failure renders as.
        errorRetry: 'An error occurred, please retry.',
        errorRefLabel: 'Ref: {ref}',
        commonProfileUnavailable: "We couldn't load your account. Check your connection and try again.",
        commonSearch: 'Search',
        commonName: 'Name',
        commonDate: 'Date',
        commonTime: 'Time',
        commonSize: 'Size',
        commonType: 'Type',
        commonStatus: 'Status',
        commonDescription: 'Description',
        commonTitle: 'Title',
        commonAmount: 'Amount',
        commonCurrency: 'Currency',
        commonCategory: 'Category',
        commonNotes: 'Notes',
        commonLocation: 'Location',
        commonEmail: 'Email',
        commonPassword: 'Password',
        commonCopy: 'Copy',
        // ── Composer / ChatInput chrome ────────────────────────────────────
        // Shared VERBATIM by the Main Office and the Telegram Bucket Day
        // composers — both render the same <ChatInput>.
        composerExpenseFallback: 'Expense',
        composerRemoveReference: 'Remove reference',
        composerRemoveSegment: 'Remove this segment',
        composerTranscribingAudio: 'Transcribing audio…',
        composerHideTranscription: 'Hide transcription',
        composerShowTranscription: 'Show transcription',
        composerEditedTitle: 'This transcription was edited — the corrected text is what PAI will act on',
        composerEdited: 'Edited',
        composerTranscriptionFailed: 'Transcription failed — {reason}',
        composerTranscriptionFailedFallback: 'try removing and re-recording',
        composerSilent: '[silent]',
        composerAddAttachment: 'Add attachment',
        composerAttachmentsCount: '{n} attachments',
        composerRemoveAllAttachments: 'Remove all attachments',
        composerClearAllAttachments: 'Clear all attachments',
        composerClearAll: 'Clear all',
        composerReplyingTo: 'Replying to {n} message{s}',
        composerCancelAllReplies: 'Cancel all replies',
        composerRemoveReply: 'Remove this reply',
        composerYou: 'You',
        composerVoiceMessagesCount: '{n} voice messages',
        // The recording bar (#276) — the PO's headline example on the "Spanish" card.
        composerRecordingLabel: 'Recording...',
        composerDiscardTitle: 'Discard this recording',
        composerAcceptTitle: 'Stop, queue this segment, and keep composing',
        composerPlaceholder: 'Type your message... (Shift+Enter for new line)',
        // Ui Clipping (#366) — narrower tiers the composer swaps to as its own
        // measured width shrinks, so the placeholder never wraps and clips to a
        // stray fragment ("Type your") at the chat's minimum split width. Picked
        // by REAL canvas text measurement against the live font — see
        // `utils/composerPlaceholder.js` — never by a guessed character count.
        composerPlaceholderShort: 'Type your message',
        composerPlaceholderMinimal: 'Message...',
        composerStopGenerating: 'Stop generating response',
        composerRecordVoice: 'Record voice message',
        // Dictation (#541) — the Main Chat mic transcribes into the message box.
        dictationStartTitle: 'Dictate',
        dictationStopTitle: 'Stop and insert the text into your message',
        dictationTranscribing: 'Transcribing…',
        dictationDone: 'Done',
        dictationEmpty: "Didn't catch that — try again",
        composerFinalizing: 'Finalizing {n} transcription{s}…',
        composerSend: 'Send',
        // ── Firebase auth failures (useAuth) ───────────────────────────────
        // The login screen is the first thing a new user sees, so these are
        // localized at the source. `error.code` is never translated.
        authLoginFailed: 'Login failed. Please try again.',
        authInvalidEmail: 'Invalid email address.',
        authAccountDisabled: 'This account has been disabled.',
        authNoAccount: 'No account found with this email.',
        authWrongPassword: 'Incorrect password.',
        authInvalidCredential: 'Invalid email or password.',
        authTooManyAttempts: 'Too many failed attempts. Please try again later.',
        authGoogleFailed: 'Google login failed. Please try again.',
        authLoginCancelled: 'Login cancelled.',
        authPopupBlocked: 'Popup was blocked. Please allow popups for this site.',
        authSignupFailed: 'Signup failed. Please try again.',
        authEmailInUse: 'An account with this email already exists.',
        authWeakPassword: 'Password should be at least 6 characters.',
        // ── Main Chat invite card (event_invite_request) ───────────────────
        // PRE-EXISTING call sites in cards/cardConfigs.jsx that never had dictionary
        // entries — they rendered their English fallback in Spanish. Surfaced by
        // i18n.test.js's "only references keys that exist" check.
        eventInviteCardTitle: 'Invite to “{title}”?',
        eventInviteRecipients: 'Inviting',
        eventInviteDropRecipient: 'Remove from invite',
        eventInviteNeedRecipient: 'Keep at least one recipient to invite.',
        eventInviteConfirmBtn: 'Confirm',
        eventInviteConfirmBusy: 'Sending…',
        eventInviteDeclineBtn: 'Decline',
        eventInviteEditBtn: 'Edit',
        eventInviteEditDone: 'Done',
        eventInviteDone: 'Event created and {count} invitation(s) sent.',
        eventInviteDeclined: 'Invitation cancelled.',
        // #535 — invitations to a repeating series (one per person).
        eventInviteSeriesDone: 'Series created — {events} events, {count} invitation(s) sent (one per person).',
        eventInviteSeriesExistingDone: '{count} invitation(s) sent for {events} events (one per person).',
        eventInviteExistingDone: '{count} invitation(s) sent.',
        caleInvitationSeries: 'Repeating · {summary}',
        caleInvitationPackage: '{n} events: {titles}',
        invitePkgAcceptAll: 'Accept all',
        invitePkgCount: '{n} of {total} events pending',
        invitePkgDeclineAll: 'Decline all',
        invitePkgHint: "Press Accept all to accept all, or Decline all to decline all. If you only want some of them, just write it, for example: 'Accept 3 and decline the rest' or 'Decline 4 and 9, accept the others'.",
        invitePkgTitle: 'Invitation from {name}',
        invitePkgSomeone: 'someone',
        invitePkgPendingCount: '{n} of {total} pending',
        invitePkgAccepted: 'Accepted — they are on your calendar.',
        invitePkgDeclined: 'Declined.',
        invitePkgAnswered: 'This invitation has already been answered.',
        eventConflictTitle: 'This overlaps an event you have already confirmed:',
        eventConflictForce: 'Book anyway',
        eventConflictCancel: 'Cancel',
        // Card row chrome + the column-move detail sentence (rendered via tNodes so
        // the bolded values can move with Spanish word order).
        cardDocumentFallback: 'document',
        cardDocumentFallbackCap: 'Document',
        cardTransactionFallback: 'Transaction',
        cardPreviewReceipt: 'Preview receipt',
        cardDownloadReceipt: 'Download receipt',
        columnMoveDetail: 'Moving the {column} column with {n} ticket{s} to {board} at {position}.',
        columnMoveEnd: 'the end',
        // ── Contacts / Agenda / day-channel chrome ────────────────────────
        auxDeleteNote: 'Delete note',
        auxLoadingEllipsis: 'Loading…',
        auxContactNameRequired: 'Name *',
        auxClearSearch: 'Clear search',
        auxContactsMenu: 'Contacts menu',
        auxClosePanelEsc: 'Close panel (Esc)',
        auxDayOptions: 'Day options',
        auxSearchInThisDay: 'Search in this day',
        auxShareConversation: 'Share conversation',
        auxCloseDay: 'Close day',
        auxSocialHandles: 'Social handles',
        auxAddToFavorites: 'Add to favorites',
        // {label} is an already-translated field name ("email" / "phone").
        auxRemoveMethod: 'Remove {label} {n}',
        auxAddMethod: 'Add {label}',
        auxQrUnavailable: 'QR unavailable',
        msgSharedReadOnly: 'This is a shared conversation. You have read-only access.',
        // Per-item "remove from this batch" hover tooltips on the confirmation
        // cards. {name} is USER CONTENT (a filename, merchant, board…) and is never
        // translated — only the sentence around it is.
        cardKeepItem: 'Keep {name} — remove from deletion',
        cardKeepItemBatch: 'Keep {name} — remove from the batch',
        cardKeepRename: 'Keep {name} — remove from the rename batch',
        cardKeepComment: 'Keep this comment — remove from deletion',
        cardRemoveFromSelection: 'Remove {name} from the selection',
        cardDontShareWith: "Don't share with {name}",
        cardNewNameFor: 'New name for {name}',
        cardThisFile: 'this file',
        cardThisTransaction: 'this transaction',
        cardThisEvent: 'this event',
        cardThisNote: 'this note',
        cardThisReminder: 'this reminder',
        cardThisBoard: 'this board',
        cardThisDocument: 'this document',
        cardFileNoun: 'file',
        // "Search all chats" channel headers ("Conversation misplaced"). The
        // Telegram header reuses the existing rail labels below.
        searchGroupMainChat: 'Main Chat',
        // Voice composer (audio parity #173) — shared across channels/Main Office
        voiceRecord: 'Record voice',
        voiceRecording: 'Recording',
        voiceDiscard: 'Discard',
        voiceAccept: 'Accept',
        voiceTranscribing: 'Transcribing…',
        voiceRemove: 'Remove',
        voiceTranscriptPlaceholder: 'Transcription — edit before sending',
        // Transcription box expand/collapse toggle (#296 re-open — replaced the
        // drag-to-resize handle and its "restore" arrow)
        voiceTranscriptExpand: 'Show the full transcription',
        voiceTranscriptCollapse: 'Collapse the transcription',
        // IconRail / panels
        mainChat: 'Main Chat',
        launcherNotes: 'Notes & Task List',
        launcherReminders: 'Reminders & Notifications',
        // #534 — the PAI Map teaser tile (title is the same in both languages;
        // never "Coming soon", never a date)
        launcherPaiMapStatus: 'In development',
        navLauncher: 'PAI — all features',
        navAccount: 'Account',
        documents: 'File Manager',
        calendar: 'Calendar',
        // #193: the module tracks income AND expenses — its user-visible identity is
        // "Finance" (calling it "Expenses" caused income blindness). Internal name/
        // testids/route stay 'expenses'; only this display label changed.
        expenses: 'Finance',
        agenda: 'Contacts',
        boards: 'Boards',
        workspace: 'Workspace',
        notes: 'Notes & Task List',
        reminders: 'Reminders & Notifications',
        // PAI Rulebook (#146)
        rulebook: 'Rulebook',
        rulebookFolio: 'The Folio',
        rulebookFolioPlaceholder: 'Write the rules PAI must follow…',
        rulebookDesk: 'The Assistant Desk',
        rulebookDeskPlaceholder: 'Describe a rule you want PAI to follow…',
        rulebookSaving: 'Saving…',
        rulebookSaved: 'Saved',
        rulebookSaveFailed: "Couldn't save",
        rulebookPreview: 'Preview',
        rulebookEdit: 'Edit',
        rulebookDeskError: "Something went wrong. Try again?",
        // The Add Rule confirmation card (#146, sub-feature 4).
        ruleAddTitle: 'Add this rule to your Folio?',
        ruleAddConfirmBtn: 'Add Rule',
        ruleAddBusy: 'Adding…',
        ruleAddEditBtn: 'Edit',
        ruleAddEditingHint: 'Keep refining it below — nothing has been added yet.',
        ruleAddDone: 'Added to your Folio: {rule}',
        ruleAddCancelled: 'Draft dismissed — nothing was added.',
        ruleAddFailed: "Couldn't add the rule. Please try again.",
        // ── Energy Hub (Energy HUB part 1) ──────────────────────────────────
        energy: 'Energy Tank',
        energyHub: 'Energy Hub',
        energyHubDesc: 'See exactly where your energy goes.',
        energyOpenHub: 'View Energy Hub',
        // The five pillars. This closed set is the Pizza Chart's vocabulary.
        energyPillar_listening: 'Listening',
        energyPillar_viewing: 'Viewing',
        energyPillar_writing: 'Writing',
        energyPillar_reasoning: 'Reasoning',
        energyPillar_executing: 'Executing',
        // Tank
        energyTankTitle: 'Energy Tank',
        energyBalance: 'Balance',
        energyGray: 'Gray points',
        energyAction: 'Action points',
        energyCycle: 'Cycle',
        energyEmpty: "PAI's energy is empty.",
        energyLockdownBody: 'New requests are paused until your next cycle or a top-up. Your data is untouched.',
        energyStorage: 'Storage',
        energyStorageReserved: '{n} days reserved',
        energyCleanSlate: 'Clean slate',
        // Gray Stack breakdown (#302) — the receipt behind the reservation.
        energyStorageBreakdown: 'Storage breakdown',
        energyStorageBreakdownHint: 'What your Gray Stack is reserved against',
        energyStorageTotal: 'Total per month',
        energyStorageCatDocuments: 'Documents & PDFs',
        energyStorageCatDocumentsHint: 'Files in your Bunker',
        energyStorageCatImages: 'Images & Screenshots',
        energyStorageCatImagesHint: 'Photos, receipts and screenshots',
        energyStorageCatVoice: 'Voice notes',
        energyStorageCatVoiceHint: 'Voice messages from chat and Telegram',
        energyStorageCatData: 'Data Structures',
        energyStorageCatDataHint: 'Conversations, notes, boards, finances, contacts',
        energyStorageCatOverhead: 'System Overhead',
        energyStorageCatOverheadHint: 'Older versions, deleted files and indexes',
        // Graystack — Consumed vs Reserved (#465 live Gray points).
        energyGraystack: 'Graystack',
        energyGraystackHint: 'Points held for your storage. They grow and shrink the moment you add or delete a file.',
        energyGraystackConsumed: 'Consumed',
        energyGraystackConsumedHint: 'The part of this cycle already behind us',
        energyGraystackReserved: 'Reserved',
        energyGraystackReservedHint: '{days} days left in the cycle + {buffer}-day grace',
        energyGraystackProtected: 'Your reserved points cover your storage until {date}.',
        energyUploadRefused: 'This file needs {needed} points of storage; you have {available} available.',
        // Range picker
        // Cycle pager (#311). Reporting follows the user's salary cycle, so the
        // old fixed 7d/30d boxes — which straddle two cycles — are gone.
        energyRangeCycle: 'This cycle',
        energyCyclePrev: 'Previous cycle',
        energyCycleNext: 'Next cycle',
        energyRangeCustom: 'Custom',
        energyRangeFrom: 'From',
        energyRangeTo: 'To',
        // Chart + drill levels
        energyPizzaLabel: 'Energy by pillar',
        energyTotalPoints: 'Total points',
        energyOverview: 'Overview',
        energyBreadcrumb: 'Energy navigation',
        energyBack: 'Back to overview',
        energyCategories: 'Categories',
        energyItems: 'Activity',
        energyNoCategories: 'No activity in this pillar for the selected range.',
        energyNoItems: 'Nothing recorded here for the selected range.',
        // Daily chart + day view (#385). Days are UTC days, like the backend.
        energyDailyTitle: 'Daily energy',
        energyDailyBarLabel: '{date}: {pts} points',
        energyDailyFutureLabel: '{date}: not yet',
        energyDailyToday: 'Today',
        energyDailyNotYet: 'not yet',
        energyDailyRangeTotal: 'Range total',
        energyDailyError: "Couldn't load your daily energy.",
        energyDailyEmpty: 'No energy was used in this range.',
        energyDayBack: 'Back to the range',
        energyDayEyebrow: 'Energy used on',
        energyDayNone: 'No energy was used on this day.',
        energyDayDesc_listening: 'Turning what you said or uploaded into something PAI can use: voice transcription and indexing.',
        energyDayDesc_viewing: 'Reading images and scans: OCR, receipts and business cards.',
        energyDayDesc_writing: 'Producing text for you: documents, edits, summaries and reports.',
        energyDayDesc_reasoning: 'Thinking: understanding requests, researching, searching the web and composing answers.',
        energyDayDesc_executing: 'Acting on your data: creating, updating, moving and deleting records.',
        energyLoadMore: 'Load more',
        energyLoading: 'Loading…',
        energyError: "Couldn't load your energy data.",
        energyPtsSuffix: 'pts',
        energyInputTokens: 'Input Tokens',
        energyThinkingTokens: 'Thinking Tokens',
        energyOutputTokens: 'Output Tokens',
        // Source attribution (Labelling energy #315). The channel keys mirror
        // conversations.channel; an unlisted one falls back to the BE's label.
        energyChannel_web: 'Main Chat',
        energyChannel_telegram: 'Telegram',
        energyChannel_telegram_convo: 'Telegram (Conversations)',
        // #530: origin label for a charge from a channel hidden in the web app.
        energyChannelOtherMessaging: 'Messaging',
        energyChannel_rulebook: 'Rulebook',
        energyFromConversation: 'From conversation: {title}',
        // Energy Receipt + Context Thermostat (#349). The receipt's total is
        // POINTS ONLY — tokens are never summed across the lines.
        receiptTitle: 'Energy Receipt',
        receiptSubtitle: 'What this message cost you',
        receiptLineTranscription: 'Transcription',
        receiptLineTranscriptionHint: 'Audio and image input turned into text',
        // #378: this line is the MEMORY the thermostat governs — and ONLY that.
        // The fixed per-call prefix is receiptLineSystemOverhead; summarising the
        // thread is receiptLineMemoryCompression (split out by the #378 re-open,
        // because a summarization prompt is the WHOLE thread and lumping it into
        // the fixed prefix made that line read 236,000 against a 30–35K baseline).
        receiptLineInputContext: 'Input Context (Conversation History)',
        receiptLineInputContextHint: 'The conversation history PAI was holding',
        receiptLineMemoryCompression: 'Memory Compression',
        receiptLineMemoryCompressionHint: 'Summarising the thread when it outgrew its memory budget',
        receiptLineSystemOverhead: 'System Prompt',
        receiptLineSystemOverheadHint: 'PAI\u2019s instructions and tool definitions, re-sent on every step',
        // #376: the line covers generating the message text, not just thinking.
        // #388 splits it into the two sub-items below.
        receiptLineReasoning: 'PAI Reasoning & Output',
        receiptLineReasoningHint: 'Thinking, and generating the message text',
        receiptPartThinking: 'PAI Thinking',
        receiptPartOutput: 'PAI Output',
        receiptLineTools: 'Tools and Creation',
        receiptLineToolsHint: 'Web searches, documents, extraction',
        receiptTokens: '{n} Tokens',
        // #378 + its re-open: the per-step figure is what proves the thermostat's
        // cap held on EVERY step, and what stops a summed overhead line reading
        // like one impossible payload. Deliberately NOT phrased as `per × steps =
        // total`: the per-step number is a measured estimate, the total is the
        // authoritative billed figure.
        receiptTokensPerStep: '{n} Tokens ({calls} steps × ~{per} re-sent each step)',
        receiptPerStepDetail: '{calls} steps × ~{per} re-sent each step',
        receiptHistoryTokens: 'History {n}',
        receiptMessageTokens: 'This message {n}',
        receiptCompositionHint: 'The memory slider limits history only — your new message always goes in whole.',
        receiptPartAudio: 'Audio Transcription',
        receiptPartOcr: 'OCR / Image Processing',
        receiptPartToolTokens: 'Tool and creation tokens',
        receiptPartSearchFees: 'Web search fees',
        receiptSearchFeeDetail: '{n} web searches × {rate}',
        receiptStepGrounded: 'Google Search ({n} queries)',
        receiptPartPlacesFees: 'Google Maps place lookups',
        receiptPlacesFeeDetail: '{n} place lookups × {rate}',
        receiptStepPlaces: 'Google Maps place lookup',
        receiptStepsSummary: '{n} model calls in this turn',
        receiptLinePrefixCache: 'Prefix cache set-up',
        receiptLinePrefixCacheHint: 'Saving PAI’s instructions once so the next messages re-send them for far less',
        receiptPartCacheCreate: 'Set-up (makes the next turns cheaper)',
        receiptPartCacheExtend: 'Kept alive for the next turns',
        receiptStepCacheCreate: 'Prefix cache set-up',
        receiptStepCacheExtend: 'Prefix cache kept alive',
        receiptStoppedMessageCost: 'Stopped message cost',
        receiptStoppedMessageSteps: '{n} steps before you stopped it',
        msgStoppedBy: 'PAI was stopped by {name}',
        msgStoppedByYou: 'PAI was stopped',
        msgTurnPending: 'PAI is working on this…',
        msgTurnPendingStale: 'No reply arrived for this message.',
        msgTurnSending: 'Sending…',
        msgTurnResend: 'Resend',
        receiptStepTool: 'Tool round',
        receiptStepFinal: 'Writing the answer',
        receiptStepVerify: 'Verification re-check',
        receiptStepSynthesis: 'Answering from what was gathered',
        receiptStepAnomaly: 'Retry after a stream fault',
        receiptStepsNote: 'Every tool round is one model call, and writing the final answer is one more — so a turn always bills one more call than the tools you see.',
        receiptPoints: '{n} Energy Points',
        // #388 — cached vs active, and the counterfactual behind a saving.
        receiptCached: '{n} Cached',
        receiptActive: '{n} Active',
        receiptIfActive: '{n} if 100% Active',
        receiptSaved: '· Saved {n}%',
        receiptCacheRate: 'Cache Utilization Rate: {n}%',
        receiptCacheRateDetail: '{cached} of {total} input tokens served from cache',
        receiptCacheInfoLabel: 'What is cached input?',
        // ⚠️ VERBATIM from the ticket, and pinned byte-for-byte by
        // EnergyReceiptModal.test.jsx — do not reword.
        receiptCacheInfo: 'Cached input refers to information from your previous messages, documents, or system instructions that PAI has already processed. The system holds this information in an active state for a period of time so it does not have to be re-read from scratch. This is why cached information costs significantly fewer energy points in the final calculation.',
        receiptTotal: 'Total Turn Cost',
        receiptTankPct: '{n}% of your Energy Tank',
        receiptTechnicalDetails: 'Technical details',
        thermostatTitle: 'Memory Budget',
        thermostatPages: '{n} Pages',
        thermostatTotalRecall: 'Total Recall',
        thermostatWords: '~{n} words of memory',
        thermostatWholeThread: 'The whole conversation',
        thermostatInForce: 'In force: {v}',
        thermostatApplyChat: 'Apply to this chat only',
        thermostatApplyGlobal: 'Set as Global Default',
        thermostatGlobalHint: 'Applies to new conversations. Each chat can override it from its Energy Receipt.',
        thermostatSaveFailed: "Couldn't save that budget.",
        thermostatSavedChat: 'Saved for this chat only.',
        thermostatSavedGlobal: 'Saved as your default for new chats and this one.',
        thermostatSavedDefault: 'Saved as your default for new chats.',
        thermostatInfoAria: "About PAI's memory budget",
        thermostatInfoBody: "This is PAI's memory budget. If you want PAI to remember a longer thread of your conversation, he has to hold more information in his head and 'think' through more details. This makes him smarter but uses more Energy per message.",
        thermostatInfoPage: '1 Page = ~560 words of memory.',
        thermostatInfoLowLabel: 'Low Budget:',
        thermostatInfoLow: "PAI stays focused on 'now.' Saves Energy.",
        thermostatInfoHighLabel: 'High Budget:',
        thermostatInfoHigh: 'PAI holds the whole thread. Smarter, but costs more.',
        thermostatWarnTitle: 'High Energy Usage Warning',
        thermostatWarnBody: 'This will significantly increase the cost per message. Proceed?',
        // Conversation Data card (#301). The two energy rows answer different
        // questions: what the thread COST to produce vs what it costs to KEEP.
        convDataUserMessages: 'User Messages',
        convDataAiAnswers: 'AI Answers',
        convDataInteractionEnergy: 'Total Interaction Energy',
        // #354 — real priced Energy Points, not the raw token count.
        convDataInteractionEnergyHint: 'Spent once, to produce this thread',
        convDataPtsUnit: 'pts',
        convDataTokensUnit: 'tokens',
        convDataStorageCost: 'Memory Storage Cost',
        convDataStorageCostHint: 'Reserved per month (Gray Stack)',
        convDataStartingDate: 'Starting Date',
        convDataLastUsed: 'Last Used',
        settings: 'Settings',
        accountMenu: 'Account',
        // Mobile app-like overlay menu (Mobile Visual #171)
        menu: 'Menu',
        close: 'Close',
        logout: 'Logout',
        noConversations: 'No conversations yet',
        untitledChat: 'New conversation',
        telegram: 'Telegram',
        telegramConnected: 'Connected',
        telegramSearchPlaceholder: 'Search all days…',
        telegramNoDays: 'No Telegram days yet.',
        telegramNoResults: 'No days match your search.',
        telegramItems: '{n} Items',
        telegramUpdated: 'Updated {ago}',
        telegramAgoNow: 'just now',
        telegramAgoMin: '{n}m ago',
        telegramAgoHour: '{n}h ago',
        telegramAgoDay: '{n}d ago',
        telegramRenameAction: 'Rename',
        telegramShareAction: 'Share Conversation',
        telegramDeleteAction: 'Delete Day Conversation',
        telegramDeletePrompt: 'Are you sure you want to delete the day conversation, the Telegram conversation of the day {date}?',
        telegramDeleteYes: 'Yes',
        telegramDeleteNo: 'No',
        telegramSelectAction: 'Select',
        telegramSelectAllAction: 'Select All',
        telegramDeselectAllAction: 'Deselect All',
        telegramSelectionCount: '{n} selected',
        telegramSelectionHint: 'Tap days to select them',
        telegramExitSelection: 'Done',
        telegramBulkDeleteAction: 'Delete selected days',
        telegramBulkDeletePrompt: 'Delete {n} day conversations? Their messages and voice notes are removed for good; documents stay in your File Manager.',
        telegramBulkDeleteYes: 'Delete',
        telegramBulkDeleteNo: 'Cancel',
        telegramBulkDeleteError: 'Some days couldn\'t be deleted. Please try again.',
        telegramCardCheckboxLabel: 'Select {date}',
        telegramBack: 'Back to library',
        telegramSearchInDay: 'Search in this day…',
        telegramContinuePlaceholder: 'Continue this day in your Main Office…',
        telegramSend: 'Send',
        // Telegram QR #327 — the tools bot is branded "PAI Tools" everywhere it
        // is named, matching its sibling "PAI Conversations".
        telegramToolsAccount: 'PAI Tools',
        settingsInfoAbout: 'About {name}',
        telegramAccountDesc: 'The full PAI on Telegram — documents, calendar, finances, no limits',
        telegramConnect: 'Connect PAI Tools',
        telegramLinkedLabel: 'Linked',
        telegramUnlink: 'Unlink',
        telegramOpenHint: 'Tap "Start" in Telegram to finish linking — this updates automatically.',
        telegramConnectTitle: 'Connect PAI Tools',
        telegramQrIntro: 'Scan this code with your phone to connect PAI Tools. Telegram opens on your phone; tap Start and this page updates itself — no desktop app needed.',
        telegramQrAlt: 'Scan to connect PAI Tools',
        telegramQrOpen: 'Open PAI Tools in Telegram',
        telegramConvoConnectTitle: 'Connect PAI Conversations',
        telegramConvoQrIntro: 'Scan this code with your phone to connect PAI Conversations — the fast track. Telegram opens on your phone; tap Start and this page updates itself.',
        telegramConvoQrAlt: 'Scan to connect PAI Conversations',
        telegramConvoQrOpen: 'Open PAI Conversations in Telegram',
        telegramQrLoading: 'Generating your code…',
        telegramQrExpiresIn: 'Code expires in {time}',
        telegramQrExpired: 'This code has expired. Refresh it to get a new one.',
        telegramQrExpiredBadge: 'Expired',
        telegramQrAwaiting: 'Waiting for you to tap "Start" in Telegram — this page updates itself.',
        telegramQrRefresh: 'Refresh code',
        telegramQrRefreshing: 'Refreshing…',
        // PAI Conversations vs. PAI Tools — the Dual-Bucket System.
        telegramWorldToggleLabel: 'Choose which Telegram world to browse',
        telegramWorldConversations: 'PAI Conversations',
        telegramWorldTools: 'PAI Tools',
        telegramConvoAccount: 'PAI Conversations',
        telegramConvoAccountDesc: 'The fast track: quick questions and web search, answered in seconds',
        telegramConvoConnect: 'Connect PAI Conversations',
        telegramConvoLinkedLabel: 'Connected',
        telegramConvoUnlink: 'Disconnect',
        telegramConvoOpenHint: 'Tap "Start" in Telegram to finish connecting — this updates automatically.',
        // Settings modal
        settingsTitle: 'Settings',
        // Settings Update (#236) — two structural views
        userSettings: 'User Settings',
        systemSettings: 'System Settings',
        userSettingsTitle: 'User Settings',
        systemSettingsTitle: 'System Settings',
        accountSection: 'Account',
        logoutDesc: 'Sign out of your account on this device.',
        appearance: 'Appearance',
        darkMode: 'Dark Mode',
        darkModeDesc: 'Switch between light and dark themes',
        language: 'Language',
        languageDesc: 'Choose the interface language',
        planSection: 'Plan & Energy',
        plan_free: 'Free',
        // Canonical tier vocabulary (Energy HUB). The legacy names below stay
        // valid aliases — prod rows still carry them.
        plan_base: 'Entry Salary',
        plan_executive: 'Associate Salary',
        plan_enterprise: 'Partner Salary',
        plan_normal: 'Entry Salary',
        plan_premium: 'Associate Salary',
        plan_max: 'Partner Salary',
        planOf: 'of',
        planUsedThisCycle: 'used this cycle',
        planNoEnergy: 'No energy included — upgrade to use PAI',
        currency: 'Currency',
        baseCurrency: 'Default Currency',
        baseCurrencyDesc: 'Base currency for totals and new entries',
        timezone: 'Timezone',
        timezoneDesc: 'Reference timezone for calendar, reminders and dates',
        preferenceSaved: 'Saved',
        recentlyDeleted: 'Recently Deleted',
        retentionPeriod: 'Retention period',
        retentionPeriodDesc: 'How long deleted files stay before being purged',
        restore: 'Restore',
        deletePermanently: 'Delete permanently',
        recentlyDeletedEmpty: 'No recently deleted documents',
        // Privacy — Ghost Mode (Agenda TO Contacts — Phase A, T7)
        privacy: 'Privacy',
        ghostMode: 'Ghost Mode',
        ghostModeDesc: 'Hide your account from contact discovery by email.',
        // common
        logOut: 'Log out',
        // Confirmation cards (ResolutionCard family — H-3.2)
        cardYes: 'Yes',
        cardNo: 'No',
        cardCancel: 'Cancel',
        cardPreview: 'Preview',
        cardDownload: 'Download',
        cardHide: 'Hide',
        cardCantSaveDecision: "Couldn't save your decision — it may not persist after a reload.",
        // #545 — a card replaced by a later, corrected card for the same request.
        cardSuperseded: 'Replaced by the updated card',
        // document deletion
        docDeleteBusy: 'Deleting…',
        docDeleteConfirmTitle: 'Confirm permanent deletion of {n} file{s}',
        docDeleteCannotUndo: 'This action cannot be undone.',
        docDeleteDone: 'Deleted {n} file{s}.',
        docDeleteCancelled: 'Deletion cancelled — file{s} kept.',
        docDeleteCancelledPlural: 'Deletion cancelled — files kept.',
        // expense deletion
        expDeleteTitle: 'Delete {n} transaction{s}?',
        expDeleteBtn: 'Delete {n} item{s}',
        expDeleteBusy: 'Deleting…',
        expDeleteDone: 'Deleted {n} transaction{s}.',
        expDeleteCancelled: 'Deletion cancelled — transaction{s} kept.',
        cardCannotUndo: 'This cannot be undone.',
        // calendar event deletion
        calDeleteTitle: 'Delete this event?',
        calDeleteBusy: 'Deleting…',
        calDeleteConfirmBtn: 'Confirm Deletion',
        calDeleteRemoves: 'This permanently removes the event.',
        calDeleteDone: 'Deleted the event "{title}".',
        calDeleteCancelled: 'Deletion cancelled — the event "{title}" was kept.',
        // calendar events batch deletion
        calBatchTitle: 'Delete {n} event{s}?',
        calBatchBtn: 'Delete {n} event{s}',
        calBatchDone: 'Deleted {n} event{s}.',
        calBatchCancelled: 'Deletion cancelled — event{s} kept.',
        // Notes & Reminders (Fase 1) — panel
        notesTitle: 'Notes',
        notesColumnTitle: 'Notes',
        remindersColumnTitle: 'Reminders',
        notificationsColumnTitle: 'Notifications',
        notificationsEmpty: 'No notifications.',
        taskListTitle: 'Task List',
        taskMenuLabel: 'Task List menu',
        // Notes & Task List layout (#488)
        splitDragHandle: 'Drag to reorder',
        splitMoveUp: 'Move {name} up',
        splitMoveDown: 'Move {name} down',
        splitMoveLeft: 'Move {name} left',
        splitMoveRight: 'Move {name} right',
        splitExpand: 'Expand {name}',
        splitCollapse: 'Collapse {name}',
        taskClearCompleted: 'Delete completed tasks',
        taskDeleteAll: 'Delete all tasks',
        taskAddPlaceholder: 'Add a task…',
        taskAdd: 'Add task',
        taskNewTitle: 'New task',
        taskSearchPlaceholder: 'Search tasks…',
        taskNoResults: 'No tasks match your search.',
        taskEmpty: 'No tasks yet. Add one above, or ask PAI.',
        taskConfirmDeleteAll: 'Delete all {n} tasks? This cannot be undone.',
        taskConfirmDeleteCompleted: 'Delete {n} completed tasks? This cannot be undone.',
        taskMarkDone: 'Mark "{label}" as done',
        taskMarkNotDone: 'Mark "{label}" as not done',
        taskEditHint: 'Click to edit',
        taskDelete: 'Delete task',
        taskSaveFailed: "Couldn't save the task.",
        taskDeleteTitle: 'Delete {n} task{s}?',
        taskDeleteTitleAll: 'Delete your entire Task List ({n} task{s})?',
        taskDeleteBtn: 'Delete {n} task{s}',
        taskDeleteBusy: 'Deleting…',
        taskDeleteDone: 'Deleted {n} task{s}.',
        taskDeleteCancelled: 'Deletion cancelled — task{s} kept.',
        cardThisTask: 'this task',
        noteNew: 'New Note',
        reminderNew: 'New Reminder',
        notesSearchPlaceholder: 'Search notes…',
        notesEmpty: 'No notes yet. Capture an idea to get started.',
        notesNoResults: 'No notes match your search.',
        remindersEmpty: 'No reminders yet.',
        noteUntitled: 'Untitled note',
        notesSelect: 'Select',
        notesSelectDone: 'Cancel',
        notesBulkDelete: 'Delete ({n})',
        reminderOverdue: 'Overdue',
        // #279 — the column kebab menus (Select now lives inside them).
        notesMenuLabel: 'Notes menu',
        remindersMenuLabel: 'Reminders menu',
        remindersBack: 'Back',
        // #284 — Past Reminders (fired reminders, kept 7 days).
        remindersPastView: 'Past Reminders',
        remindersActiveView: 'Active Reminders',
        remindersPastTitle: 'Past Reminders',
        remindersPastEmpty: 'No past reminders.',
        remindersPastNote: 'These reminders will auto-eliminate in 7 days.',
        reminderFired: 'Notified',
        // Recurring reminders — the modal's Repeat field + the card's strip.
        // The strip renders LOCALLY from the structured rule (the API's English
        // `recurrence_text` is for PAI / API consumers, not for the UI).
        reminderRepeatLabel: 'Repeat',
        reminderRepeatNone: 'None',
        reminderRepeatDaily: 'Daily',
        reminderRepeatWeekly: 'Weekly on specific days',
        reminderRepeatEveryNDays: 'Every X days',
        reminderRepeatMonthly: 'Monthly',
        reminderRepeatDaysRequired: 'Pick at least one day of the week.',
        reminderRepeatIntervalRequired: 'Enter how many days between reminders.',
        reminderRepeatIntervalUnit: 'days',
        reminderRecurrenceDaily: 'Daily',
        reminderRecurrenceEveryNDays: 'Every {n} days',
        reminderRecurrenceMonthly: 'Monthly on the {day}',
        reminderRecurrenceLabel: 'Repeats',
        // Expiration Reminders (#463) — the modal's "Ends" field + the card's
        // remaining count.
        reminderEndsLabel: 'Ends',
        reminderEndsNever: 'Never',
        reminderEndsAfterCount: 'After a number of times',
        reminderEndsOnDate: 'On a date',
        reminderEndsCountUnit: 'times in total',
        reminderEndsFiredSoFar: '{n} done',
        reminderEndsCountRequired: 'Enter how many times it should go off.',
        reminderEndsCountTooLow: 'It has already gone off {n} times — enter a bigger number.',
        reminderEndsUntilRequired: 'Pick an end date on or after the first reminder.',
        reminderEndsUntil: 'until {date}',
        reminderRemainingCount: '{n} of {total} left',
        reminderRemainingLeft: '{n} left',
        reminderDay0: 'Mon',
        reminderDay1: 'Tue',
        reminderDay2: 'Wed',
        reminderDay3: 'Thu',
        reminderDay4: 'Fri',
        reminderDay5: 'Sat',
        reminderDay6: 'Sun',
        // Notes & Reminders — note edit modal
        noteModalNewTitle: 'New Note',
        noteModalEditTitle: 'Edit Note',
        noteModalViewTitle: 'Note',
        noteEditLabel: 'Edit note',
        noteTitlePlaceholder: 'Title (optional)',
        noteContentPlaceholder: 'Write your note…',
        noteContentRequired: 'A note needs some content.',
        noteSaveError: 'Failed to save the note.',
        // Note attached document update (#283) — attachments in the title bar.
        noteAttachLabel: 'Attach a file',
        noteAttachError: 'Failed to attach that file.',
        noteAttachmentRemove: 'Remove attachment',
        noteAttachmentUnnamed: 'file',
        // Attached documents to reminders (#386).
        reminderAttachmentCount: '{n} attachments',
        reminderAttachmentRemove: 'Remove attachment from this reminder',
        reminderAttachmentUnavailable: 'Unavailable (in Recently Deleted)',
        reminderAttachmentRemoveHint: 'Removing an attachment never deletes the file.',
        attachUploadDevice: 'Upload from Device',
        attachFromFileManager: 'Select from File Manager',
        // PAI Shared notes (#259) — collaboration.
        noteShareTitle: 'Share Note',
        noteShareCollaborators: 'Shared with',
        noteShareEmpty: 'Not shared yet. Only you can see this note.',
        noteShareCanEdit: 'Can view and edit',
        noteShareRemove: 'Remove access',
        noteSharePlaceholder: 'Search contact or team...',
        noteShareNoResults: 'No contacts or teams match. Connect in Contacts first.',
        noteShareTeamLabel: 'Team',
        noteShareBtn: 'Share',
        noteShareContactNoun: 'contact',
        noteShareTeamNoun: 'team',
        noteShareWarning: 'You are about to share this note. This will give them full editing privileges to modify your note.',
        noteShareError: 'Could not share this note.',
        noteUnshareError: 'Could not update sharing.',
        noteSharedBy: 'Shared by {name}',
        noteSharedWith: 'Shared with {n}',
        noteShareCardTitle: 'Share "{note}" with {n} person{s}?',
        noteShareCardBtn: 'Share with {n} person{s}',
        noteShareCardBusy: 'Sharing...',
        noteShareCardWarning: 'This will give them full editing privileges to modify your note.',
        noteShareCardDone: 'Shared with {n} person{s} — they can now edit it.',
        noteShareCardCancelled: 'Sharing cancelled — the note stays private.',
        // Notes & Reminders — reminder edit modal
        reminderModalNewTitle: 'New Reminder',
        reminderModalEditTitle: 'Edit Reminder',
        reminderContentPlaceholder: 'What should I remind you about?',
        reminderDateLabel: 'Date',
        reminderTimeLabel: 'Time',
        reminderTitleRequired: 'A reminder needs a title.',
        reminderWhenRequired: 'Pick a date and time.',
        reminderSaveError: 'Failed to save the reminder.',
        modalDelete: 'Delete',
        modalCancel: 'Cancel',
        // Notes & Reminders — chat delete cards
        noteDeleteTitle: 'Delete {n} note{s}?',
        noteDeleteBtn: 'Delete {n} note{s}',
        noteDeleteBusy: 'Deleting…',
        noteDeleteDone: 'Deleted {n} note{s}.',
        noteDeleteCancelled: 'Deletion cancelled — note{s} kept.',
        reminderDeleteTitle: 'Delete {n} reminder{s}?',
        reminderDeleteBtn: 'Delete {n} reminder{s}',
        reminderDeleteBusy: 'Deleting…',
        reminderDeleteDone: 'Deleted {n} reminder{s}.',
        reminderDeleteCancelled: 'Deletion cancelled — reminder{s} kept.',

        // action revocation / undo (Double tap W/T #210)
        actionRevokeTitle: 'Undo — remove the {label}?',
        actionRevokeBody: 'This permanently removes the {label} PAI created earlier.',
        actionRevokeDocNote: 'The document moves to Recently Deleted, so you can still restore it.',
        actionRevokeConfirmBtn: 'Confirm Undo',
        actionRevokeBusy: 'Undoing…',
        actionRevokeNote: 'Only what this action created is removed — nothing else is touched.',
        actionRevokeDone: 'Undone — the {label} was removed.',
        actionRevokeCancelled: 'Undo cancelled — the {label} was kept.',
        // Comment section PAI #242 — chat delete card for ticket comments
        commentDeleteTitle: 'Delete {n} comment{s}?',
        commentDeleteBtn: 'Delete {n} comment{s}',
        commentDeleteBusy: 'Deleting…',
        commentDeleteDone: 'Deleted {n} comment{s}.',
        commentDeleteCancelled: 'Deletion cancelled — comment{s} kept.',
        // book deletion
        bookDeleteTitle: 'Delete the "{name}" book?',
        bookDeleteBusy: 'Deleting…',
        bookDeleteConfirmBtn: 'Confirm Deletion',
        bookDeleteNote: 'This permanently deletes the book and its data.',
        bookDeleteTotalTickets: 'Total Tickets',
        bookDeleteItemizedLines: 'Itemized Lines',
        bookDeleteNetBalance: 'Net Balance',
        bookDeleteLinkedFiles: 'Linked files',
        bookDeleteDone: 'Deleted the "{name}" book{tx}.',
        bookDeleteDoneTx: ' and its {n} transaction{s}',
        bookDeleteCancelled: 'Deletion cancelled — the "{name}" book was kept.',
        bookRenameTitle: 'Rename this book?',
        bookRenameCurrent: 'Current name',
        bookRenameNew: 'New name',
        bookRenameBody: 'Every transaction in this book stays in it under the new name.',
        bookRenameConfirmBtn: 'Confirm',
        bookRenameBusy: 'Renaming…',
        bookRenameDone: 'Renamed "{old}" to "{name}".',
        bookRenameCancelled: 'Rename cancelled — the book is still called "{old}".',
        // Rulebook in Telegram (#503) — edit_rulebook's rule_change_request card.
        ruleChangeTitleAdd: 'Add Rule {n} to your Rulebook?',
        ruleChangeTitleUpdate: 'Change Rule {n} of your Rulebook?',
        ruleChangeTitleDelete: 'Remove Rule {n} from your Rulebook?',
        ruleChangeCurrent: 'Current text',
        ruleChangeNew: 'New text',
        ruleChangeConfirmBtn: 'Confirm',
        ruleChangeBusy: 'Saving…',
        ruleChangeDoneAdd: 'Added Rule {n} to your Rulebook.',
        ruleChangeDoneUpdate: 'Rule {n} updated.',
        ruleChangeDoneDelete: 'Removed Rule {n} — the rules below it moved up.',
        ruleChangeCancelled: 'Cancelled — your Rulebook was not changed.',
        // ticket batch update
        ticketUpdateTitle: '{action} — {n} ticket{s}?',
        ticketUpdateBtn: 'Update {n} ticket{s}',
        ticketUpdateBusy: 'Updating…',
        ticketUpdateDone: '{action} — applied to {n} ticket{s}.',
        ticketUpdateCancelled: 'Batch update cancelled — ticket{s} unchanged.',
        // ticket batch deletion
        ticketDeleteTitle: 'Delete {n} ticket{s}?',
        ticketDeleteBtn: 'Eliminate {n} ticket{s}',
        ticketDeleteBusy: 'Eliminating…',
        ticketDeleteDone: 'Eliminated {n} ticket{s}.',
        ticketDeleteCancelled: 'Deletion cancelled — ticket{s} kept.',
        ticketNoDescription: 'No description.',
        // board batch deletion
        boardDeleteTitle: 'Delete {n} board{s}?',
        boardDeleteBtn: 'Eliminate {n} board{s}',
        boardDeleteBusy: 'Eliminating…',
        boardDeleteDone: 'Eliminated {n} board{s}.',
        boardDeleteCancelled: 'Deletion cancelled — board{s} kept.',
        // Leave board (#275)
        boardLeaveTitle: 'Leave {name}?',
        boardLeaveQuestion: 'Are you sure you want to exit? This action cannot be undone.',
        boardLeaveBody: 'You will lose access to this board — only a board owner can invite you back.',
        boardLeaveConfirmBtn: 'Confirm Exit',
        boardLeaveBusy: 'Leaving…',
        boardLeaveDone: 'You left {name}.',
        boardLeaveCancelled: 'Exit cancelled — you are still a member of {name}.',
        boardOpen: 'Open Board',
        // column move
        columnMoveTitle: 'Move column',
        columnMoveBusy: 'Moving…',
        columnMoveConfirmBtn: 'Confirm Move',
        columnMoveDone: 'Moved the "{name}" column to {target}.',
        columnMoveCancelled: 'Move cancelled.',
        // label deletion
        labelDeleteTitle: 'Delete the "{name}" label?',
        labelDeleteBoard: 'on {board}',
        labelDeleteAffected: 'This removes it from {n} ticket{s}.',
        labelDeleteNoTickets: 'It isn\'t on any tickets yet.',
        labelDeleteMore: '…and {n} more',
        labelDeleteConfirmBtn: 'Confirm Deletion',
        labelDeleteBusy: 'Deleting…',
        labelDeleteNote: 'This permanently removes the label from the palette and every tagged ticket.',
        labelDeleteDone: 'Deleted the "{name}" label.',
        labelDeleteCancelled: 'Deletion cancelled — the "{name}" label was kept.',

        columnDeleteTitle: 'Delete the "{name}" column?',
        columnDeleteBoard: 'on {board}',
        columnDeleteAffected: 'This column has {n} ticket{s} — deleting it removes the column and all its tickets.',
        columnDeleteEmpty: 'This column is empty — no tickets will be deleted.',
        columnDeleteMore: '…and {n} more',
        columnDeleteConfirmBtn: 'Confirm Deletion',
        columnDeleteBusy: 'Deleting…',
        columnDeleteNote: 'This permanently removes the column and every ticket inside it.',
        columnDeleteDone: 'Deleted the "{name}" column.',
        columnDeleteCancelled: 'Deletion cancelled — the "{name}" column was kept.',
        // ticket attach
        ticketAttachTitle: 'Attach {n} document{s} to the ticket?',
        ticketAttachBtn: 'Upload to Ticket',
        ticketAttachBusy: 'Uploading…',
        ticketAttachDone: 'Attached {n} document{s} to the ticket.',
        ticketAttachCancelled: 'Attachment cancelled.',
        // document rename
        docRenameTitle: 'Rename {n} file{s}?',
        docRenameBtn: 'Confirm Changes',
        docRenameBusy: 'Renaming…',
        docRenameDone: 'Renamed {n} file{s}.',
        docRenameCancelled: 'Rename cancelled.',
        docRenameInputTitle: 'Edit the new file name',
        docRenameEmptyName: 'A file name cannot be empty.',
        // contact creation
        contactConfirmTitle: 'Create contact "{name}"?',
        contactConfirmBtn: 'Confirm',
        contactConfirmBusy: 'Creating…',
        contactDeclineBtn: 'Decline',
        contactEditBtn: 'Edit',
        contactEditDoneBtn: 'Done',
        contactCreatedDone: 'Contact {name} created.',
        contactDeclined: 'Contact creation declined.',
        contactNameRequired: 'A name is required.',
        // Duplicate Guard (#260) — the three choices the backend's 409 offers.
        contactDuplicateSaveAnyway: 'Save duplicate anyway',
        contactDuplicateUpdate: 'Update existing entry',
        contactDuplicateCancel: 'Cancel',
        // ── Contacts Hub (Agenda TO Contacts — Phase B) ──────────────────
        contactsTitle: 'Contacts',
        contactsSearchPlaceholder: 'Search name, team, or email...',
        contactsSortRecent: 'Recent',
        contactsSortAlpha: 'A–Z',
        contactsSortTooltip: 'Toggle sort order',
        contactsPending: 'Pending Requests',
        contactsMenuBlockList: 'Block List',
        contactsMenuNewContact: 'New contact',
        contactsLoadFailed: 'Failed to load contacts',
        contactsEmptyAll: 'No contacts yet — add one from the menu.',
        contactsNoMatch: 'No contacts match your search.',
        tierNetwork: 'Network',
        tierTeams: 'Teams',
        tierRolodex: 'Rolodex',
        tierNetworkEmpty: 'No network contacts yet.',
        tierTeamsEmpty: 'No teams yet.',
        tierRolodexEmpty: 'No saved contacts yet.',
        contactInviteToNetwork: 'Invite to Network',
        contactRequestSent: 'Request Sent',
        contactFieldEmail: 'Email',
        contactFieldPhone: 'Phone',
        contactFieldCompany: 'Company',
        contactFieldIndustry: 'Industry',
        contactFieldPosition: 'Position',
        contactFieldBirthday: 'Birthday',
        contactEditAction: 'Edit',
        // Interaction Bar pillars (nav labels, per-section total, empty states)
        pillarLabelDocs: 'Files',
        pillarLabelChats: 'Conversations',
        pillarLabelEvents: 'Events',
        pillarLabelFinancials: 'Finance',
        pillarTotal: 'Total',
        // Shared-event view mode (read-only; Edit only for the organizer)
        contactEventViewTitle: 'Event',
        contactEventUntitled: 'Untitled event',
        contactEventEdit: 'Edit',
        contactEventYou: 'You',
        contactEventGuest: 'Guest',
        contactEventStatus_confirmed: 'Confirmed',
        contactEventStatus_pending: 'Pending',
        contactEventStatus_declined: 'Declined',
        pillarDocsEmpty: 'No shared documents.',
        pillarChatsEmpty: 'No shared conversations.',
        pillarEventsEmpty: 'No shared events.',
        pillarFinancialsEmpty: 'No shared books.',
        // Ghost Card + discovery
        ghostPaiUser: 'PAI User',
        ghostSendRequest: 'Send Contact Request',
        ghostRequestSent: 'Request Sent',
        ghostCancelRequest: 'Cancel Request',
        ghostPendingReceived: 'This user already sent YOU a request.',
        ghostAccept: 'Accept',
        ghostDecline: 'Decline',
        ghostLinked: 'Already in your Network',
        ghostNotFound: 'No PAI user found.',
        ghostSelf: "Wait, that's you! You cannot send a handshake to yourself.",
        discoverErrAlreadyRequested: "You've already sent this user a request.",
        discoverErrAlreadyConnected: 'You are already connected with this user.',
        discoverErrTheyRequestedYou: 'This user already sent you a request — check Pending Requests.',
        discoverErrGeneric: 'Something went wrong. Please try again.',
        // Pending Requests portal
        pendingTitle: 'Pending Requests',
        pendingReceivedHeading: 'Received',
        pendingSentHeading: 'Sent',
        pendingAcceptBtn: 'Accept',
        pendingDeclineBtn: 'Decline',
        pendingCancelBtn: 'Cancel Request',
        pendingStatusLabel: 'Pending',
        pendingNoReceived: 'No incoming requests.',
        pendingNoSent: 'No outgoing requests.',
        // Block List
        blockListTitle: 'Block List',
        blockUnblockBtn: 'Unblock',
        blockAddPlaceholder: 'Block an email…',
        blockAddBtn: 'Block',
        blockListEmpty: 'No blocked users.',
        // Relationship Management (edit overlay + danger zone)
        editContactTitle: 'Edit contact',
        editSaveBtn: 'Save',
        editCancelBtn: 'Cancel',
        editSaving: 'Saving…',
        contactTimeline: 'Timeline',
        contactAddNotePlaceholder: 'Add a quick note…',
        contactAddNoteBtn: 'Add',
        contactNoNotes: 'No interactions logged yet.',
        dangerZone: 'Danger zone',
        uncontactBtn: 'Uncontact',
        uncontactConfirmTitle: 'Uncontact this connection?',
        uncontactDisclaimer: 'Are you sure? This action cannot be reversed. You will immediately lose all access to shared data you do not own, and the other user will be removed from all data you own. This severance is permanent and immediate.',
        uncontactConfirmBtn: 'Yes, uncontact',
        uncontactDone: 'Connection severed.',
        blockContactBtn: 'Block contact',
        blockContactConfirmTitle: 'Block this contact?',
        blockContactDisclaimer: 'Blocking severs any active connection and hides you and this person from each other everywhere. You can unblock later from the Block List.',
        blockContactConfirmBtn: 'Yes, block',
        deleteContactBtn: 'Delete contact',
        deleteContactConfirmTitle: 'Delete {name} permanently? This also removes the timeline of notes.',
        deleteContactConfirmBtn: 'Yes, delete',
        // ── THE TEAMS tier (Agenda TO Contacts — Phase C / T5) ───────────
        contactsMenuNewTeam: 'Create New Team',
        teamRoleCreator: 'Creator',
        teamRoleOwner: 'Owner',
        teamRoleMember: 'Member',
        teamRoleFor: 'Role for {name}',
        teamMemberSearchPlaceholder: 'Search your network by name or email...',
        teamMemberSearchNoMatch: 'No contacts match that search.',
        teamMemberStranger: 'Not in your network',
        teamInviteMember: 'Invite',
        teamKickMember: 'Remove from team',
        teamAddMember: 'Add member',
        teamAddMemberEmpty: 'All your network contacts are already members.',
        teamAddMemberFailed: 'Could not add that member.',
        teamAddMemberMenu: 'Add a network contact',
        teamLoadFailed: 'Failed to load the team.',
        teamKickFailed: 'Could not remove that member.',
        teamLeaveFailed: 'Could not leave the team.',
        teamDeleteFailed: 'Could not delete the team.',
        teamRenameFailed: 'Could not save the changes.',
        teamNameRequired: 'A team name is required.',
        teamNoMembers: 'No members yet.',
        teamEditBtn: 'Edit',
        teamLeaveBtn: 'Leave team',
        teamDeleteBtn: 'Delete team',
        teamDeleteConfirmTitle: 'Delete "{name}"?',
        teamDeleteDisclaimer: 'This dissolves the team for everyone and immediately revokes every shared document, conversation, book and event. Members should download anything they need first. This cannot be undone.',
        teamDeleteConfirmBtn: 'Delete team',
        // Create-team dialog
        teamCreateTitle: 'Create a team',
        teamCreateConfirmTitle: 'Confirm the roster',
        teamFieldName: 'Team name',
        teamFieldNamePlaceholder: 'e.g. Product Squad',
        teamFieldDescription: 'Description (optional)',
        teamFieldMembers: 'Members (from your network)',
        teamCreateNoNetwork: 'You have no network contacts yet — connect with someone first.',
        teamCreateConfirmPrompt: "You are creating '{name}' with {n} member(s). Do you confirm?",
        teamCreateRosterEmpty: 'No members — just you.',
        teamCreateBack: 'Back',
        teamCreateNext: 'Next',
        teamCreateConfirmBtn: 'Create team',
        teamCreateNotLinked: 'These members are not in your network: {names}',
        teamCreateFailed: 'Could not create the team.',
        // Team chat cards
        teamCardCreateTitle: "Create the team '{name}'?",
        teamCardCreateNoMembers: 'No members — just you.',
        teamCardCreateBtn: 'Create Team',
        teamCardCreateBusy: 'Creating…',
        teamCardCreateDone: "Created the team '{name}'.",
        teamCardCreateCancelled: 'Team creation cancelled.',
        teamCardShareTitle: 'Share to {team}?',
        teamCardShareDocBody: "I will add this to the {team} team's shared documents. Do you confirm?",
        teamCardShareBookBody: "I will add this to the {team} team's shared books. Do you confirm?",
        teamCardShareEventBody: "I will add this event to the {team} team's calendar. Do you confirm?",
        teamCardShareAssetFallback: 'this item',
        teamCardShareEventFallback: 'this event',
        teamCardShareBtn: 'Confirm',
        teamCardShareBusy: 'Sharing…',
        teamCardShareDone: 'Shared to {team}.',
        teamCardShareCancelled: 'Share cancelled.',
        teamCardDeleteTitle: "Delete the team '{name}'?",
        teamCardDeleteMembers: 'Members',
        teamCardDeleteShares: 'Shared items',
        teamCardDeleteBtn: 'Delete Team',
        teamCardDeleteBusy: 'Deleting…',
        teamCardDeleteDone: "Deleted the team '{name}'.",
        teamCardDeleteCancelled: 'Deletion cancelled — the team was kept.',
        // Settings — Danger Zone (Agenda TO Contacts Phase D / T1 "Smithereens").
        // NOTE: `dangerZone` is already defined above (Contacts uncontact overlay) —
        // reused here, not redefined, to avoid a duplicate object key.
        deleteAccount: 'Delete Account',
        deleteAccountDesc: 'Permanently delete your account and everything in it',
        deleteAccountModalTitle: 'Delete your account?',
        deleteAccountWarnErase: 'Every document, conversation, contact, board, and ledger you own is erased immediately.',
        // #415 (PO Option B). Two rules, and they pull in opposite directions, so
        // both are stated: a space you CREATED dies with you, while work you
        // contributed inside someone else's space stays with them (tombstoned).
        deleteAccountWarnShared: 'A ticket you created inside someone else’s shared space stays with them, shown as authored by “Deleted user”; comments you wrote there are deleted with your account.',
        deleteAccountWarnBooks: 'Boards, financial books, notes and teams you created are destroyed for everyone in them.',
        deleteAccountWarnBilling: 'Any active subscription is cancelled immediately and your saved payment method is removed.',
        deleteAccountWarnFinal: 'This cannot be undone.',
        // The honest residue (Kill Switch). The promise is "everything, immediately",
        // and it is kept — but three things outlive the wipe and saying so is part of
        // keeping it. See BE/ACCOUNT_DELETION_AUDIT.md §6.
        deleteAccountResidueTitle: 'What briefly remains:',
        deleteAccountResidueBackups: 'Disaster-recovery backups are purged on routine rotation; diagnostic and security logs are kept briefly, then deleted automatically. Neither is used to restore an account.',
        deleteAccountResidueLegal: 'Records the law requires us to keep stay: payment invoices held by our payment processor for tax, and the record of the consent you gave when you signed up. Anonymous statistical totals may also be kept.',
        deleteAccountConfirmLabel: 'Type DELETE to confirm',
        deleteAccountConfirmBtn: 'Delete My Account',
        deleteAccountBusy: 'Deleting…',
        deleteAccountCancel: 'Cancel',
        deleteAccountError: 'Could not delete your account. Please try again.',
        // User Settings (#184) — profile + salary/billing + data export
        profileSection: 'Profile',
        userName: 'User Name',
        userNameDesc: 'The name PAI uses to address you.',
        registeredEmail: 'Registered Email',
        // Salary and Payment (#312). The product calls a plan a SALARY: it is
        // what PAI is paid to work with, not a subscription to content.
        salarySection: 'Salary and Payment',
        salaryType: 'Salary type',
        changeSalary: 'Change Salary',
        salaryCurrent: 'Current',
        changeSalaryNote: 'You will be redirected to our secure Stripe checkout. Prices exclude VAT — tax is added at checkout.',
        salaryManage: 'Manage billing',
        salaryRedirecting: 'Redirecting to secure checkout…',
        salaryError: 'Could not start checkout — please try again.',
        salaryNoBilling: 'No billing account yet — your current tier was granted, not purchased. Pick a tier to hire PAI.',
        // Salary Selection — step 3 of onboarding (#374).
        salarySelectTitle: 'Choose PAI’s salary',
        salarySelectSubtitle: 'You are hiring PAI. Pick what you pay it to work with you — you can change this any time in Settings.',
        salarySelectSaving: 'Saving…',
        salarySelectNote: 'Every salary continues to our secure Stripe checkout, which shows the exact amount. Prices exclude VAT — tax is added at checkout.',
        salarySelectError: "We couldn't save that choice — please try again.",
        salarySelectCancelled: 'Checkout cancelled — nothing was charged. Pick a salary to finish setting up your account.',
        salaryAwaitingTitle: 'Confirming your payment…',
        salaryAwaitingBody: 'Your account opens as soon as the payment is confirmed. This usually takes a few seconds — you can leave this page open.',
        salaryAwaitingSlow: 'This is taking longer than expected. Your payment is safe — refresh this page, or contact support if it does not clear shortly.',
        salaryAwaitingChange: 'Choose a different plan',
        salaryUnpaidTitle: "Your payment hasn't been completed",
        salaryUnpaidBody: 'The payment for this salary was not finished. Continue to payment to open your account.',
        salaryUnpaidPlan: 'Selected salary: {plan}',
        salaryUnpaidPay: 'Continue to payment',
        // Salary page (#435) — the marketing site's salary section, copy
        // verbatim from pai-website-alien src/content/en.js (pricing.*).
        salaryPageEyebrow: 'Salaries',
        salaryPageTitle: 'What your PAI earns.',
        salaryPageIntro: 'Every salary includes every capability, so nothing is locked. Like any salary, what changes is how much work you can ask of it each month.',
        salaryTierTaglineBase: 'Your day-to-day, off your mind.',
        salaryTierTaglineExecutive: 'Your life and your work, same brain.',
        salaryTierTaglineEnterprise: 'For people whose day is other people.',
        salaryMostPopular: 'MOST POPULAR',
        salaryPerMonth: '/ month',
        salaryVatForCountry: '+ VAT for your country',
        salaryEnergy: 'Energy',
        salaryPointsPerMonth: 'points / mo',
        salaryPerkEveryCapability: 'Every capability, nothing locked',
        salaryPerkRollOver: 'Unused points roll over',
        salaryPerkTelegramWeb: 'Telegram and the web',
        salaryPerkStandardSupport: 'Standard support',
        salaryPerkFasterSupport: 'Reduced support response time',
        salaryPerkEarlyAccess: 'Early access to new features',
        salaryPerkPrioritySupport: 'Priority support',
        salaryHireCta: 'Hire your own PAI',
        salaryRollOverNote: 'Unused points roll over to next month, so you never lose them. A point is one unit of thinking. Filing a receipt costs a handful, a deep research run costs a few thousand. Let your PAI go any time, no notice period, and take all your data with you.',
        billingSuccessPending: 'Payment received — activating your salary…',
        billingSuccessDone: 'Your salary is active.',
        billingCancelled: 'Checkout cancelled — no changes made.',
        energyConsumedPct: 'Percentage of energy points consumed',
        paymentMethod: 'Payment Method',
        paymentMethodNone: 'No payment method on file',
        paymentMethodStripe: 'Card and invoices managed securely by Stripe',
        paymentHistory: 'Payment history',
        verifyTitle: 'Confirm your email',
        verifySubtitle: 'Enter the 6-digit code we sent to',
        verifyConfirm: 'Confirm email',
        verifyResend: 'Resend code',
        verifyCodeSent: 'We sent a 6-digit code to your email.',
        verifyCodeAlreadySent: 'A code is already on its way — check your inbox.',
        verifyCodeIncomplete: 'Enter the 6-digit code from your email.',
        verifyInvalid: 'That code is not correct.',
        verifyInvalidWithAttempts: 'That code is not correct.',
        verifyExpired: 'That code has expired — request a new one.',
        verifyTooManyAttempts: 'Too many attempts. Request a new code.',
        verifyNoCode: 'No code has been sent yet — request one first.',
        verifySendFailed: "We couldn't send the email just now. Please try again in a moment.",
        verifyDone: 'Email confirmed — opening PAI…',
        planFree: 'Free',
        save: 'Save',
        saved: 'Saved',
        edit: 'Edit',
        done: 'Done',
        nameEmptyError: 'Name cannot be empty',
        nameSaveError: 'Failed to save name',
        exportModalTitle: 'Download all your data',
        exportModalBody: 'Before deleting your account, download a complete copy of your data. The ZIP contains every conversation and note (as markdown), your uploaded documents and images, your boards, and your accounting, calendar, reminder and contact records.',
        exportDownloadBtn: 'Download All Data (ZIP)',
        exportBusy: 'Generating archive…',
        exportBusyHint: 'Collecting your conversations, documents and records. This can take a minute for a large account — keep this window open.',
        exportDone: 'Your data has been downloaded.',
        exportError: "We couldn't build your data archive.",
        exportRetry: 'Retry',
        exportContinueBtn: 'Continue to delete',
        back: 'Back',
        // ShareDialog — granular write permission (Agenda TO Contacts Phase D / T6)
        shareCanWrite: 'Can write',
        shareCanWriteHint: 'Let them send messages in this conversation',
        shareReadOnly: 'Read-only',
        // Chat card — PAI-to-PAI direct send (Agenda TO Contacts Phase D / T6 + T4)
        directSendTitle: 'Send to {contact}?',
        directSendDocLabel: 'Document',
        directSendCardLabel: 'Contact card',
        directSendConfirmBtn: 'Confirm Send',
        directSendBusy: 'Sending…',
        directSendDone: 'Sent {asset} to {contact}.',
        directSendCancelled: 'Send cancelled — nothing was sent.',
        directSendSent: 'Delivered to their PAI',
        directSendBellOnly: "Waiting in their PAI's notifications.",
        // ── Full-localization pass ("Spanish" bug card) ─────────────────────
        // 1041 keys covering the panels, menus, modals and status text that
        // #123 left hardcoded. English values here are byte-identical to the
        // t() fallback at each call site — see src/i18n/i18n.test.js.
        auxAllUsers: 'All Users',
        auxApply: 'Apply',
        auxBillingCyclePT: 'Billing Cycle (PT)',
        auxBillingPT: 'Billing (PT)',
        auxBillingPTTitle: 'Align date boundaries to Google Cloud billing cycle (midnight Pacific Time)',
        auxCacheHitRate: 'Cache Hit Rate',
        auxCacheHitRateTitle: 'Prompt-cache hit rate (cached ÷ input tokens)',
        auxIncludesDeletedAccounts: 'Includes anonymised totals carried over from deleted accounts. Those figures cannot be attributed to any user and are excluded from per-user views.',
        auxCacheHitSub: 'cached input bills at 1/10th',
        auxCacheSuffix: '{pct} cache',
        auxCachedSuffix: '{n} cached',
        auxColCachePct: 'Cache %',
        auxColCached: 'Cached',
        auxColCalls: 'Calls',
        auxColCost: 'Cost',
        auxColDate: 'Date',
        auxColInput: 'Input',
        auxColOutput: 'Output',
        auxColTotal: 'Total',
        auxConfirmPassword: 'Confirm Password',
        auxCustomDateRangeTitle: 'Custom date range',
        auxCustomRange: 'Custom',
        auxDailyUsage: 'Daily Usage',
        auxModuleFinance: 'Finance',
        auxModuleReminders: 'Reminders',
        auxModuleNotes: 'Notes',
        auxModuleBoards: 'Boards',
        auxModuleCalendar: 'Calendar',
        auxModuleFiles: 'File Manager',
        auxModuleChat: 'Messages',
        auxTelemetryExpenses: 'Expenses',
        auxTelemetryIncome: 'Income',
        auxTelemetryTickets: 'Tickets',
        auxTelemetryMainChat: 'Main Chat',
        auxTelemetryTelegram: 'Telegram',
        auxTelemetryUnavailable: 'Module telemetry is not available yet.',
        auxTelemetryPrivacyNote: 'Aggregate figures only: no identity or content is attached to any number.',
        auxTelemetryEnergy: 'Energy Points consumed',
        auxTelemetryEnergyTotal: 'Total',
        auxTelemetryEnergyAxis: 'Points',
        auxTelemetryEnergyHint: 'Hover or tap a day for the pillar split.',
        auxTelemetryEnergyDayAria: '{date}: {total} Energy Points',
        auxTelemetryLeaderboard: 'Module leaderboard',
        auxTelemetryNoData: 'No data for this period yet.',
        auxTelemetryDailyTokens: 'Daily tokens (input / output)',
        auxTelemetryModules: 'Modules',
        auxTelemetryModule: 'Module',
        auxTelemetryCreated: 'Created in period',
        auxTelemetryAvgPerUser: 'Avg per user',
        auxTelemetryBreakdown: 'Breakdown',
        auxTelemetryTicketDensity: 'Tickets per board',
        auxTelemetryChannels: 'Channels',
        auxTelemetryChannel: 'Channel',
        auxTelemetryMessages: 'Messages',
        auxTelemetryLinked: 'Linked accounts',
        auxTelemetryStorage: 'Storage',
        auxTelemetryStorageTotal: 'Total',
        auxTelemetryGrowth: 'Growth in period',
        auxTelemetryTokenInput: 'Input',
        auxTelemetryTokenOutput: 'Output',
        auxTelemetryTokenCached: 'Cached',
        auxTelemetryTokenTotalOf: 'Total {series}',
        auxTelemetryTokenHint: 'Hover or tap a day for exact figures.',
        auxTelemetryTokenAxis: 'Tokens',
        auxTelemetryTokenDayAria: '{date}: {input} input, {output} output, {cached} cached tokens',
        auxRefresh: 'Refresh',
        auxDashboardTitle: 'Usage Dashboard',
        auxDateRangeTo: '{start} to {end}',
        auxDaysSub: '{n} days',
        auxEmailAddress: 'Email address',
        auxEstCost: 'Est. Cost',
        auxFailedAdjustEnergy: 'Failed to adjust energy',
        auxForgotPasswordBackToLogin: 'Back to login',
        auxForgotPasswordConfirmation: 'If an account exists for this email, a reset link has been sent.',
        auxForgotPasswordSpamHint: 'If you do not see the email in your inbox within a few minutes, please check your spam folder.',
        auxForgotPasswordInvalidEmail: 'Please enter a valid email address.',
        auxForgotPasswordLink: 'Forgot password?',
        auxForgotPasswordNetworkError: 'Something went wrong. Please check your connection and try again.',
        auxForgotPasswordSending: 'Sending...',
        auxForgotPasswordSubmit: 'Send reset link',
        auxForgotPasswordSubtitle: "Enter your email address and we'll send you a link to reset your password.",
        auxForgotPasswordTitle: 'Reset your password',
        auxFrom: 'From',
        auxGoogleSearchGrounding: 'Google Search Grounding',
        auxGroundingActuallyUsed: 'Actually Used',
        auxGroundingEnabled: 'Enabled',
        auxGroundingSearchQueries: 'Search Queries',
        auxInOutCalls: '{in} in / {out} out ({n} calls)',
        auxInputTokens: 'Input Tokens',
        auxLast24Hours: 'Last 24 hours',
        auxLastNDays: 'Last {n} days',
        auxLogIn: 'Log in',
        auxLoginContinueGoogle: 'Continue with Google',
        auxLoginGoogleFailed: 'Google login failed.',
        auxLoginInvalidCreds: 'Invalid email or password.',
        auxLoginNoAccount: "Don't have an account?",
        auxLoginOrEmail: 'Or continue with email',
        auxLoginSigningIn: 'Signing in...',
        auxLoginSubmit: 'Sign in with Email',
        auxLoginTitle: 'Login',
        auxOutputTokens: 'Output Tokens',
        auxShowingDataFor: 'Showing data for:',
        auxSignUp: 'Sign up',
        auxSignupCreatingAccount: 'Creating account...',
        auxSignupFailed: 'Signup failed. Please try again.',
        auxSignupGoogleFailed: 'Google sign up failed.',
        auxSignupHaveAccount: 'Already have an account?',
        auxSignupOrEmail: 'Or sign up with email',
        auxSignupPasswordMismatch: 'Passwords do not match',
        auxSignupPasswordTooShort: 'Password must be at least 6 characters long',
        auxSignupSigningUp: 'Signing up...',
        auxSignupSubmit: 'Sign up with Email',
        auxTabOverview: 'Overview',
        auxTo: 'to',
        auxTokenUsageByStep: 'Token Usage by Pipeline Step',
        auxTotalCalls: 'Total Calls',
        auxUnexpectedError: 'An unexpected error occurred. Please try again.',
        auxUsername: 'Username',
        brdAddColumn: 'Add column',
        brdAddDescriptionPlaceholder: 'Add a description...',
        brdAddTicket: 'Add ticket',
        brdAssignWord: 'Assign',
        brdAssigneesHeader: 'Assignees',
        brdAttachFile: 'Attach file',
        brdAttachingEllipsis: 'Attaching…',
        brdAttachmentsHeader: 'Attachments',
        brdBackToBoards: 'Back to boards',
        brdBoardFallback: 'Board',
        brdBoardLabel: 'Board',
        brdBoardMembers: 'Board members',
        brdBoardNamePlaceholder: 'e.g. Apartment move, Q3 roadmap',
        brdBoardNotFound: 'Board not found.',
        brdBoardSettings: 'Board settings',
        brdClickToRename: 'Click to rename',
        brdClosePanelEsc: 'Close panel (Esc)',
        brdCollapseSplitView: 'Collapse to split view',
        brdColorLabel: 'color',
        brdColumnLabel: 'Column',
        brdColumnMenu: 'Column menu',
        brdColumnNamePlaceholder: 'Column name...',
        brdColumnsCount: '{n} columns',
        brdCommentsHeader: 'Comments',
        brdCompletedClickUndo: 'Completed — click to mark not done',
        brdConfirmDeleteColumn: 'Delete this column and all its tickets?',
        brdConfirmDeleteLabel: 'Delete this label? It will be removed from every ticket that uses it.',
        brdConfirmDeleteTicket: 'Delete this ticket?',
        brdCreateAndAdd: 'Create & Add',
        brdCreateBoard: 'Create board',
        brdCreateNewLabel: 'Create new label',
        brdCreatingBoard: 'Creating...',
        brdCreatorCantLeave: 'As the board\'s creator you can\'t leave — delete the board instead.',
        brdCurrentSuffix: ' (current)',
        brdDeleteBoardMenuItem: 'Delete board',
        brdDeleteBoardQuestion: 'Delete board?',
        brdDeleteBoardWarning: '{name} and all its columns, tickets, and comments will be permanently deleted. This cannot be undone.',
        brdDeleteColumnMenuItem: 'Delete column',
        brdDeleteComment: 'Delete comment',
        brdDeleteLabelAction: 'Delete label',
        brdDeleteTicket: 'Delete ticket',
        brdDescPlaceholder: 'Add a description... (use the toolbar — no need to type markdown)',
        brdDestinationBoard: 'Destination board',
        brdEditComment: 'Edit comment',
        brdEditCommentPlaceholder: 'Edit comment...',
        brdEditLabel: 'Edit label',
        brdExpandCoverChat: 'Expand to cover chat',
        brdFailedAddColumn: 'Failed to add column',
        brdFailedAddComment: 'Failed to add comment',
        brdFailedAddTicket: 'Failed to add ticket',
        brdFailedAttachFile: 'Failed to attach file',
        brdFailedDeleteBoardAlert: 'Failed to delete board',
        brdFailedDeleteColumn: 'Failed to delete column',
        brdFailedDeleteComment: 'Failed to delete comment',
        brdFailedDeleteLabel: 'Failed to delete label',
        brdFailedDeleteTicketAlert: 'Failed to delete ticket',
        brdFailedEditComment: 'Failed to edit comment',
        brdFailedLeaveBoard: 'Failed to leave the board.',
        brdFailedLoadBoard: 'Failed to load board.',
        brdFailedLoadBoards: 'Failed to load boards.',
        brdFailedLoadColumns: 'Failed to load columns.',
        brdFailedMoveColumn: 'Failed to move column.',
        brdFailedMoveTicket: 'Failed to move ticket',
        brdFailedMoveTicketMsg: 'Failed to move ticket.',
        brdFailedRename: 'Failed to rename',
        brdFailedRenameBoard: 'Failed to rename board',
        brdFailedReorder: 'Failed to reorder',
        brdFailedReorderColumns: 'Failed to reorder columns',
        brdFailedSaveLabel: 'Failed to save label',
        brdFailedUpdateLabels: 'Failed to update labels',
        brdLabelNamePlaceholder: 'Label name (e.g. Phase 1)',
        brdLabelWord: 'label',
        brdLabelsHeader: 'Labels',
        brdLeaveBoardMenuItem: 'Leave Board',
        brdLeaveBoardQuestion: 'Leave board?',
        brdLeaveBoardWarning: 'Are you sure you want to exit {name}? You will lose access to this board and this action cannot be undone — only a board owner can invite you back.',
        brdLeavingEllipsis: 'Leaving...',
        brdLoading: 'Loading...',
        brdLoadingBoard: 'Loading board...',
        brdLoadingEllipsis: 'Loading…',
        brdManageMembers: 'Manage Members',
        brdMarkAsCompleted: 'Mark as completed',
        brdMarkCompleted: 'Mark ticket completed',
        brdMarkNotCompleted: 'Mark ticket not completed',
        brdMove: 'Move',
        brdMoveColumnDesc: 'Move "{name}" and all its tickets to another board.',
        brdMoveTicket: 'Move ticket',
        brdMovingTicketDesc: 'Moving "{title}"',
        brdNewBoard: 'New board',
        brdNoBoardMembers: 'No board members.',
        brdNoColumns: 'No columns',
        brdNoLabelsYet: 'No labels yet — create one to start tagging tickets.',
        brdPosition: 'Position',
        brdRemoveAttachment: 'Remove attachment',
        brdRenameBoard: 'Rename board',
        brdSearchInBoard: 'Search in Board',
        brdSelectABoard: 'Select a board…',
        brdSelectFromFileManager: 'Select from File Manager',
        brdSend: 'Send',
        brdSendComment: 'Send comment',
        brdSendingEllipsis: 'sending...',
        brdSomeoneFallback: 'Someone',
        brdTicketDescriptionAria: 'Ticket description',
        brdTicketOptions: 'Ticket options',
        brdTicketTitlePlaceholder: 'Ticket title...',
        brdTicketViewLabel: 'Ticket View',
        // #415 — a ticket whose author deleted their PAI account stays on the
        // board (the team keeps the work) and is attributed to "Deleted user".
        brdTicketCreatedBy: 'Created by',
        brdTicketAuthorDeleted: 'Deleted user',
        brdTicketsCount: '{n} tickets',
        brdUnassignWord: 'Unassign',
        brdUploadFromDevice: 'Upload from Device',
        brdWriteCommentAria: 'Write a comment',
        brdWriteCommentPlaceholder: 'Write a comment... (Enter to send)',
        caleTimeTbc: 'Time to be confirmed',
        // #532 Repeating calendar events — the booking card's series summary line.
        caleSeriesAnd: 'and',
        caleSeriesDaily: 'every day',
        caleSeriesWeekly: 'every {days}',
        caleSeriesEveryN: 'every {n} days',
        caleSeriesMonthly: 'every month on day {n}',
        caleSeriesAt: ' at {time}',
        caleSeriesSummary: '{pattern}, from {first} to {last} ({count})',
        caleSeriesCount: '{n} events',
        caleSeriesCountOne: '{n} event',
        // #533 — a package of more than 5 different events (one Confirm / Cancel).
        calePkgTitle: 'Calendar events',
        calePkgCount: '{n} events',
        calePkgExplain: 'Press Confirm to create them all, or tell me which numbers to change or remove and I\'ll show you the updated list.',
        calePkgUpdated: 'Updated list',
        calePkgUpdatedNote: 'Nothing has been created yet.',
        calePkgNeedsInfo: 'Needs info: {what}',
        calePkgClash: 'Clashes with {title}',
        calePkgError: 'Can\'t be created: {reason}',
        calePkgSkipped: 'Skipped',
        calePkgCreated: 'Created',
        calePkgChanged: 'Changed',
        calePkgWith: 'with {names}',
        calePkgNotInvited: 'Not invited: {names} (not in your network)',
        calePkgInvited: 'Invitations sent: {people}.',
        calePkgOneEvent: '1 event',
        calePkgPending: 'Pending',
        calePkgConfirmed: 'Confirmed',
        calePkgCancelled: 'Cancelled',
        calePkgResolvedConfirmed: 'Created {created} of {total} events.',
        calePkgResolvedCancelled: 'Cancelled — none of these events were created.',
        calePkgError409: 'This package was already handled.',
        caleSeriesBadge: 'Repeats',
        calDeleteSeriesRemoves: 'This permanently removes {n} events of this repeating event.',
        caleUntitledEvent: 'Untitled event',
        caleBookAnyway: 'Book anyway',
        caleEventTitlePlaceholder: 'Event title',
        caleLocationOptionalPlaceholder: 'Location (optional)',
        caleRemoveGuest: 'Remove {name}',
        caleLoadingYourNetwork: 'Loading your network…',
        caleEditEventTooltip: 'Edit event',
        caleStatusCancelled: 'Cancelled',
        caleErrUpdateEvent: 'Failed to update event',
        caleEventSavedToast: 'Event saved',
        caleSchedulingConflict: 'Scheduling conflict',
        caleConflictOverlapPeriod: 'This overlaps an event you have already confirmed.',
        caleAttendancePendingFrom: 'Pending confirmation from {name}',
        caleAttendanceConfirmedName: '{name} confirmed',
        caleAttendanceDeclinedName: '{name} declined',
        caleGuestListAria: 'Guest list — {confirmed} of {total} confirmed',
        caleShowGuestList: 'Show every guest and their status',
        caleConfirmedOfTotal: '{confirmed} of {total} confirmed',
        caleViaTeam: 'via {team}',
        caleGuestsPendingConfirmation: 'Pending confirmation',
        caleGuestsHeading: 'Guests',
        caleNotesHeading: 'Notes',
        caleNoNotesForEvent: 'No notes for this event.',
        caleAttendeesHeading: 'Attendees',
        caleNoOneInvitedYet: 'No one invited yet.',
        caleInviteBtn: 'Invite',
        caleInviteContactOrTeam: 'Invite a contact or team',
        caleSearchNameEmailTeam: 'Search name, email or team…',
        caleCouldntInviteName: 'Couldn\'t invite {name}.',
        caleInvitedName: 'Invited {name}.',
        caleAlreadyOnGuestList: '{name} is already on the guest list.',
        caleRemovedFromGuestList: 'Removed {name} from the guest list.',
        caleCouldntRemoveGuest: 'Couldn\'t remove {name}.',
        caleFilePreview: 'Preview',
        caleFileDownload: 'Download',
        caleFileRename: 'Rename',
        caleDayPrev: 'Previous day',
        caleDayNext: 'Next day',
        caleCloseBackToMonth: 'Close (back to month)',
        caleNoTransactionsLogged: 'No transactions logged',
        caleDayTxnSummary: '{n} transaction{s} · net {sign}{amount}',
        caleNoPaiActivity: 'No PAI activity',
        caleDayDocsSummary: '{n} doc{s} · {duration} of usage',
        caleWithTime: 'With time',
        caleNoEventsToday: 'No events on this day yet.',
        caleAddEventBtn: 'Add event',
        caleTimeFieldPlaceholder: 'e.g. 9:30 or 22',
        caleAmPmAria: '{label} AM or PM',
        caleEventNameRequired: 'Event name is required.',
        caleStartTimeInvalid: 'Starting time not recognised — try 9:30, 930 or 22.',
        caleEndTimeInvalid: 'End time not recognised — try 10:30, 1030 or 23.',
        caleEventNameLabel: 'Event name *',
        caleStartTimeLabel: 'Starting Time (optional)',
        caleEndTimeLabel: 'End Time (optional)',
        caleLocationPlaceholder: 'e.g. Café Luna, Zoom, the office',
        calePeopleLabel: 'People',
        caleInvitePlaceholder: 'Invite a contact or team…',
        caleNotesPlaceholder: 'What it\'s about',
        caleNoTimeSetWarning: 'No time set — this event will land in “Time to be confirmed” for the day.',
        caleSaveAndInvite: 'Save and Invite',
        caleInviteToEvent: 'Invite to “{title}”',
        caleEventWord: 'event',
        caleSearchYourNetwork: 'Search your network…',
        caleNetworkEmptyLong: 'Your Network is empty. Connect with someone first — open Agenda → add them by email → once they accept the handshake they’ll appear here to invite.',
        caleNoNetworkMatch: 'No one in your Network matches “{query}”.',
        caleInvitedBadge: 'Invited',
        caleSendNInvites: 'Send {n} invites',
        caleSendInvite: 'Send invite',
        caleSending: 'Sending…',
        caleCouldntInviteList: 'Couldn\'t invite: {names}',
        caleInvitationsSentTo: 'Invitation{s} sent to {n} {person}.',
        calePeoplePlural: 'people',
        calePersonSingular: 'person',
        caleSelectedCount: '{n} selected',
        caleTimeTbcEditHint: '{label} — set a time via Edit.',
        caleInviteContact: 'Invite contact',
        caleDeleteEvent: 'Delete event',
        caleUnknownMerchant: 'Unknown',
        caleNothingInColumn: 'Nothing in this column.',
        caleNoTransactionsLoggedDay: 'No transactions logged on this day.',
        caleMultipleBooksPickOne: 'Multiple books have activity today — pick one',
        caleNetAmount: 'net {sign}{amount}',
        caleAllBooks: 'All books',
        caleDailyNetResult: 'Daily Net Result',
        caleExpensesCount: 'Expenses ({n})',
        caleIncomeCount: 'Income ({n})',
        caleOpenBookInFinance: 'Open {name} in the Finance module →',
        caleUnassignedBook: 'Unassigned',
        caleUsageTime: 'Usage time',
        caleDocumentsTouched: 'Documents touched · {n}',
        caleNoDocsTouched: 'No documents created or edited on this day.',
        caleNetLabel: 'Net {sign}{amount}',
        caleAddEventTooltip: 'Add event',
        caleAddEventBtnCaps: 'Add Event',
        caleEventsForDay: 'Events — {date}',
        caleNewBtn: 'New',
        caleEventFallbackCaps: 'Event',
        caleEditingTitle: 'Editing — {title}',
        caleNewEventTitle: 'New event',
        caleFinanceForDay: 'Finance — {date}',
        calePaiForDay: 'PAI — {date}',
        caleErrLoadActivity: 'Failed to load activity',
        caleErrSaveEvent: 'Failed to save event',
        caleErrCreateEvent: 'Failed to create event',
        caleErrDeleteEvent: 'Failed to delete event',
        caleErrAnswerInvitation: 'Failed to answer invitation',
        caleErrDownloadFailed: 'Download failed',
        caleErrRenameFailed: 'Rename failed',
        caleErrLoadContacts: 'Failed to load contacts',
        caleCouldntInviteNofM: 'Couldn\'t invite {failed} of {total} {person}.',
        caleConfirmDeleteEvent: 'Are you sure you want to delete this event?',
        caleLoadingEllipsis: 'Loading...',
        caleTransactionsMarker: 'Transactions (income / expense)',
        caleEventsWord: 'Events',
        caleDocumentActivity: 'Document activity',
        caleTransactionsLegend: 'Transactions',
        caleUsageLegend: 'Usage',
        caleEventInvitations: 'Event Invitations',
        caleInvitedByName: 'Invited by {name}',
        caleUpcomingEvents: 'Upcoming Events',
        caleNothingScheduled: 'Nothing scheduled for today or tomorrow.',
        caleRemoveEventTooltip: 'Remove event',
        caleCalendarEvent: 'Calendar Event',
        caleStartLabel: 'Start',
        caleEndLabel: 'End',
        caleConflictOverlapColon: 'This overlaps an event you have already confirmed:',
        caleSaveConfirm: 'Save & Confirm',
        caleNotesOptionalPlaceholder: 'Notes (optional)',
        caleTeamMemberCount: 'Team · {n} member{s}',
        caleTeamWord: 'Team',
        caleContactWord: 'Contact',
        calePeoplePickerPlaceholder: 'Search contacts or teams…',
        calePickerEmptyNoContacts: 'No network contacts or teams yet — connect with someone in Contacts first.',
        calePickerNoMatch: 'No contact or team matches “{query}”.',
        finBookWord: 'Book',
        finBookLowercase: 'book',
        finBookNamePlaceholder: 'Book name',
        finUnassigned: 'Unassigned',
        finUnassignedOnly: 'Unassigned only',
        finExpenseWord: 'Expense',
        finExpensesPlural: 'Expenses',
        finIncomeWord: 'Income',
        finIncomePlural: 'Income',
        finTotalWord: 'Total',
        finTotalLowercase: 'total',
        finThisTransaction: 'this transaction',
        finThisReceipt: 'this receipt',
        finLoadingEllipsis: 'Loading…',
        finSavingEllipsis: 'Saving…',
        finMerchantPlaceholder: 'Merchant',
        finTicketImage: 'Ticket Image',
        finTicketImages: 'Ticket Images',
        finTicketImageTitle: 'Ticket image',
        finNoTicketImage: 'No ticket image or document.',
        finReceiptLoadFailed: 'Could not load this document.',
        finPdfPageFailed: 'Could not render this PDF page.',
        finOpenFullDocument: 'Open full document',
        finCloseEscTitle: 'Close (Esc)',
        finUnknownMerchant: 'Unknown merchant',
        finFixedIncome: 'Fixed Income',
        finFixedExpense: 'Fixed Expense',
        finFixedKind: 'Fixed {kind}',
        finManagingRuleScope: 'You’re managing the recurring rule behind this entry — changes apply to the whole series.',
        finMakeRepeat: 'Make “{merchant}” repeat automatically — each occurrence is logged for you.',
        finStartingDateLabel: 'Starting date for recurring ticket',
        finFrequencyLabel: 'Frequency',
        finSelectFrequency: 'Select a frequency…',
        finRecurrenceNote: '* If these options do not match your recurring time, ask PAI in the main chat or via Telegram to set your specific schedule (e.g., “Make this repeat every 27 days”).',
        finStopRecurring: 'Stop recurring',
        finPickFrequency: 'Pick a frequency.',
        finFailedToSave: 'Failed to save.',
        finSubstitutionQuestion: 'You’re changing an existing recurring rule. What should happen to the entries already generated by the old schedule?',
        finConfirmReplace: 'Confirm & Replace',
        finConfirmReplaceDesc: 'Remove the old generated entries and use the new schedule.',
        finKeepBoth: 'Keep Both',
        finKeepBothDesc: 'Keep the previously generated entries and also apply the new schedule.',
        finRetroConfirm: 'I’ve detected a start date in the past. I am going to generate the {n} missing {entryWord} from {month} until today. Do you confirm this?',
        finEntrySingular: 'entry',
        finEntryPlural: 'entries',
        finRuleStoppedQuestion: 'Rule stopped. Do you want to eliminate all generated duplicates from the ledger, or keep them as individual entries?',
        finCheckingEntries: 'Checking generated entries…',
        finEliminateWillPart1: 'This will eliminate',
        finEliminateWillPart2: 'generated {entryWord} across the whole rule’s history.',
        finKeepingOriginalTicket: 'Keeping your original ticket:',
        finEliminateGeneratedEntries: 'Eliminate generated entries',
        finKeepIndividualEntries: 'Keep them as individual entries',
        finDeleteRecurringTransaction: 'Delete recurring transaction',
        finFailedToSaveChanges: 'Failed to save changes',
        finAmountMustBeNumber: 'Amount must be a number.',
        finDateRequired: 'Date is required — every transaction needs a date.',
        finEditingExpense: 'Editing expense',
        finSaveEnterTitle: 'Save (Enter)',
        finRecurringPrefix: 'Recurring',
        finAutoGeneratedTitle: 'Auto-generated from a recurring transaction',
        finNeedsClarification: 'Needs clarification',
        finViewOnly: 'view only',
        finViewOnlyTitle: 'Created by another member — only owners or the original creator can change this',
        finMoreTitle: 'More',
        finEditDetails: 'Edit details',
        finRecurrenceRule: 'Recurrence rule',
        finMoveKind: 'Move {kind}',
        finNoOtherBooksYet: 'No other books yet.',
        finAmountMustBeGreater: 'Amount must be greater than zero.',
        finSourcePayerRequired: 'Source / payer is required.',
        finMerchantRequired: 'Merchant is required.',
        finSaveFailed: 'Save failed',
        finNewTransaction: 'New transaction',
        finSourcePayerLabel: 'Source / payer',
        finCategoryPlaceholderIncome: 'Salary, Refund…',
        finCategoryPlaceholderExpense: 'Meals, Travel…',
        finDescriptionOptional: 'Description (optional)',
        finRecurring: 'Recurring',
        finOneTimeDefault: 'One-time (default)',
        finPressEnterPrefix: 'Press',
        finEnterKey: 'Enter',
        finPressEnterSuffix: 'to save.',
        finUploadedLabel: 'Uploaded:',
        finCurrencyConversion: 'Currency conversion',
        finOriginalAmount: 'Original amount',
        finConvertedCurrency: 'Converted ({currency})',
        finExchangeRateOn: 'Exchange rate on {date}: 1 {orig} = {rate} {target}',
        finRateSourceEcb: 'Source: European Central Bank',
        finRateSourceBlend: 'Source: Frankfurter, a blend of central-bank rates (approximate)',
        finDigitalReceipt: 'Digital receipt',
        finNoItemization: 'No itemization captured from this receipt.',
        finUnclassifiedItems: 'Unclassified items',
        finReconcilesTotal: '(reconciles total)',
        finSwapDirectionTitle: 'Swap direction',
        finMarkAsKind: 'Mark as {kind}',
        finReceiptAlt: 'Receipt',
        finNewBookTitle: 'New book',
        finNewBookDesc: 'A book is a ledger for grouping transactions — e.g. "PAI Operations", "Personal", "Travel".',
        finFailedToCreateBook: 'Failed to create book',
        finCreate: 'Create',
        finCreatingEllipsis: 'Creating…',
        finRenameBookTitle: 'Rename book',
        finFailedToRenameBook: 'Failed to rename book',
        finFailedToEliminateBook: 'Failed to eliminate book',
        finEliminateBookTitle: 'Eliminate “{bookName}”?',
        finEliminateBookConfirm: 'Are you sure you want to eliminate this book and all its transactions? This cannot be undone.',
        finEliminate: 'Eliminate',
        finEliminatingEllipsis: 'Eliminating…',
        finAdvancedExportTitle: 'Advanced financial export',
        finExportDescPart1: 'Downloads a ZIP with',
        finExportDescPart2: '(Summary & Ledger sheet, optional Itemized Breakdown + auto-aggregated Items Summary) and a',
        finExportDescPart3: 'folder containing each receipt image. Excel Tables enabled, columns auto-fit, positives green and negatives red.',
        finAllBooks: 'All books',
        finStartDate: 'Start date',
        finEndDate: 'End date',
        finFullBookLabel: 'Full Book — export everything (ignore the date range)',
        finDisplayCurrency: 'Display currency',
        finExportCurrencyNote: 'All totals + the Master Ledger amount column are normalized to {currency}. Original amounts are preserved on each row.',
        finIncludeItemizedBreakdown: 'Include Itemized Breakdown',
        finItemizedBreakdownNote: '(adds Sheet 2 with every product + Sheet 3 pivot grouped by item name)',
        finTicketImagesNote: '(bundles the receipts/ folder — untick for a much smaller, data-only ZIP)',
        finLeaveDatesEmpty: 'Leave dates empty to include everything.',
        finExportFailedHttp: 'Export failed (HTTP {status})',
        finExportFailed: 'Export failed',
        finBuildingEllipsis: 'Building…',
        finDownloadZip: 'Download ZIP',
        finMovedToBook: 'Moved {count} to {book}',
        finCountSelected: '{count} selected',
        finMoveToEllipsis: 'Move to…',
        finFailedToLoadBooks: 'Failed to load books',
        finFailedToLoadExpenses: 'Failed to load expenses',
        finConfirmDeleteTransactions: 'Delete {n} transaction{s}? This cannot be undone.',
        finDeletedSkipped: 'Deleted {applied}; skipped {skipped} (permissions or missing).',
        finBulkDeleteFailed: 'Bulk delete failed',
        finMovedSkipped: 'Moved {applied}; skipped {skipped} (permissions or missing).',
        finBulkMoveFailed: 'Bulk move failed',
        finFailedToMoveExpense: 'Failed to move expense',
        finDeleteExpenseFor: 'Delete expense for {merchant}?',
        // #498 follow-up — PAI never deletes a File Manager document on its own.
        finAttachedFileQuestion: 'Do you want to delete the attached file as well, or would you prefer to keep it in the File Manager?',
        finAttachedFileDeleteToo: 'Delete the attached file as well',
        finAttachedFileKeptNote: 'Leave unticked to keep it in the File Manager.',
        // #498 follow-up 2 — whole-book deletion may take its linked files (opt-in).
        finBookFilesQuestion: 'This book\'s transactions have {n} linked file{s} (images or PDFs) in the File Manager.',
        finBookFilesDeleteToo: 'Also delete the {n} linked file{s}',
        finBookFilesKeptNote: 'Leave unticked to keep them in the File Manager.',
        finDeleteTransactionTitle: 'Delete transaction',
        finFailedToDeleteExpense: 'Failed to delete expense',
        finFailedToUpdateBookCurrency: 'Failed to update book currency',
        finMenuTitle: 'Menu',
        finFinanceMenuAriaLabel: 'Finance menu',
        finNewBook: 'New Book',
        finExport: 'Export',
        finUnassignedBook: 'Unassigned Book',
        finNetBalance: 'Net balance',
        finConvertedNote: '(converted)',
        finUnconvertedTitle: 'No exchange rate was available for these transactions, so they are listed but left out of the balance.',
        finUnconvertedCount: '({n} unconverted)',
        finVizToggleLabel: 'View',
        finVizList: 'List',
        finVizCharts: 'Charts',
        finVizSpendingOverTime: 'Spending over time',
        finVizIncomeOverTime: 'Income over time',
        finVizByCategory: 'Spending by category',
        finVizIncomeByCategory: 'Income by category',
        finVizRange10d: 'Last 10 days',
        finVizRange15d: 'Last 15 days',
        finVizRange30d: 'Last 30 days',
        finVizRangeMonth: 'This month',
        finVizRange12m: 'Last 12 months',
        finVizRangeCustom: 'Custom',
        finVizFrom: 'From',
        finVizTo: 'To',
        finVizInvalidRange: 'Choose a start date on or before the end date.',
        finVizError: 'Could not load the charts.',
        finVizEmpty: 'No transactions in this period.',
        finVizEmptyHint: 'Pick a longer range, or log a transaction to see it here.',
        finVizUnconverted: '{n} transaction(s) left out: no exchange rate was available.',
        finVizUndated: '{n} transaction(s) have no date, so they are not in these charts ({amount} in expenses). Add a date to include them.',
        finVizDaily: 'Daily',
        finVizMonthly: 'Monthly',
        finVizDayTooLong: 'Daily view is limited to 366 days.',
        finVizRangeTotal: 'Range total',
        finVizBarLabel: '{period}: {amount}',
        finVizSliceLabel: '{category}: {amount} ({pct})',
        finVizOther: 'Other',
        finVizUncategorized: 'Uncategorized',
        finVizNoSpending: 'No spending in this period.',
        finVizNoIncome: 'No income in this period.',
        finVizTotalLabel: 'Total',
        finCatDetailOpen: 'Show the transactions in {category}',
        finCatDetailCount: '{n} transaction(s)',
        finCatDetailUntitled: 'Untitled transaction',
        finCatDetailEmpty: 'No transactions in this category for this period.',
        finBookOptionsTitle: 'Book options',
        finOptions: 'Options',
        finNewEntry: 'New entry',
        finExitSelection: 'Exit selection',
        finManageMembers: 'Manage Members',
        finMembersWord: 'Members',
        finFollowMyPreference: 'Follow my preference ({currency})',
        finRenameBookMenu: 'Rename book',
        finEliminateBookMenu: 'Eliminate book',
        finNothingRecorded: 'Nothing recorded here yet.',
        finSendReceiptOrPayslip: 'Send a receipt or payslip photo in chat to log one.',
        finUpdateFailed: 'Update failed',
        finDeleteReceiptAt: 'Delete the receipt at {merchant}?',
        finDeleteFailed: 'Delete failed',
        finPulseLabel: 'Financial pulse',
        finOpenFinance: 'Open Finance',
        finNoTransactionsLoggedToday: 'No transactions logged on this day.',
        fmActiveCount: '{n} active',
        fmActualSize: 'Actual size',
        fmAudioNotSupported: 'Your browser does not support the audio element.',
        fmAudioTranscriptionHint: 'Audio was transcribed using Gemini Audio API. The original audio and transcription are shown below.',
        fmAudioTranscriptionTitle: 'Audio with AI-Generated Transcription',
        fmBackToParent: 'Back to parent folder',
        fmBrowseFiles: 'browse files',
        fmBrowseFolders: 'browse folders',
        fmChooseDifferentLocation: 'Choose a different location',
        fmClearAll: 'Clear All',
        fmCloseEsc: 'Close (Esc)',
        fmConfirmDelete: 'Confirm Delete',
        fmContentExtractedLabel: 'Content: {size} extracted',
        fmContextMove: 'Move',
        fmContextNewFolder: 'New Folder',
        fmCouldNotLoadFolders: 'Could not load your folders.',
        fmCreate: 'Create',
        fmCreateFolderFailed: 'Failed to create folder',
        fmCreateFolderTitle: 'Create New Folder',
        fmCurrent: 'Current',
        fmDeleteBreakdown: '({files} files, {folders} folders)',
        fmDeleteFileTitle: "Delete '{name}'?",
        fmDeleteFolderTitle: "Delete folder '{name}' and all its contents?",
        fmDeleteCannotUndo: 'This action cannot be undone.',
        fmDeleteDocumentFailed: 'Failed to delete document',
        fmDeleteFoldersWarning: 'Folders and their contents will be permanently deleted.',
        fmDeleteItemsConfirm: 'Delete {n} item{s}?',
        fmDeleteNSelected: 'Delete {n} selected',
        fmDeletePermanentlyHint: 'Permanently delete this document',
        fmDeleteToTrash: 'Files will be moved to Recently Deleted, where you can restore them.',
        fmDeleteVersionConfirm: 'Permanently delete version {n}? This cannot be undone.',
        fmDeleteVersionFailed: 'Failed to delete version',
        fmDeleteVersionPermanently: 'Delete this version permanently',
        fmDeletingFiles: 'Deleting files…',
        fmDeletingFilesHint: 'You can keep working — we\'ll notify you when it\'s done.',
        fmDeselectAll: 'Deselect All',
        fmDiscard: 'Discard',
        fmDiscardConfirm: 'This will permanently delete the document. Are you sure?',
        fmDismiss: 'Dismiss',
        fmDone: 'Done',
        fmDragDropHere: 'Drag and drop files or folders here, or',
        fmDragOutToAttachHint: 'Drag a file or folder out to attach it to your message',
        fmDropHereToAttach: 'Drop here to add to conversation',
        fmDuplicateName: 'Duplicate name',
        fmDuplicateNameMessage: 'A document with this name already exists. Please provide a unique name.',
        fmEnterFolderName: 'Enter folder name',
        fmEnterNewName: 'Enter new name',
        fmEnterUniqueDocName: 'Enter a unique document name',
        fmExitSelection: 'Exit selection',
        fmExtractedFiles: 'Extracted {n} files',
        fmExtractedTextFallback: 'Showing extracted text content from the document',
        fmExtractedTextOcr: 'Extracted Text (OCR)',
        fmFileTooLarge: 'File {name} is too large. Maximum size per file is 1GB.',
        fmFilename: 'Filename',
        fmLocation: 'Location',
        fmFilesToUpload: '{n} file{s} to upload',
        fmFit: 'Fit',
        fmFitToWidth: 'Fit to width',
        fmFitWidth: 'fit width',
        fmFolderEmpty: 'This folder is empty',
        fmFolderEmptyHint: 'Create a folder or upload files to get started',
        fmFullFidelityHtmlPreview: 'Full-fidelity HTML preview',
        fmItemCount: '{n} item{s}',
        fmItemsCountPlain: '{n} items',
        fmLoadFoldersFailed: 'Failed to load folders',
        fmLoadPreviewFailed: 'Failed to load preview',
        fmLoadVersionHistoryFailed: 'Failed to load version history',
        fmLoadingAudioPlayer: 'Loading audio player...',
        fmLoadingEllipsis: 'Loading...',
        fmLoadingEllipsisAlt: 'Loading…',
        fmLoadingImage: 'Loading image…',
        fmLoadingPreviewEllipsis: 'Loading preview...',
        fmLoadingVersions: 'Loading versions...',
        fmManageDocument: 'Manage Document',
        fmMenuAriaLabel: 'File manager menu',
        fmMenuTitle: 'Menu',
        fmMove: 'Move',
        fmMoveDocumentFailed: 'Failed to move document',
        fmMoveFailed: 'The move failed. Please try again.',
        fmMoveInsideSelection: 'Inside selection',
        fmMoveNDocuments: '{n} document{s}',
        fmMoveNFolders: '{n} folder{s}',
        fmMoveNItems: '{n} items ({parts})',
        fmMoveNSelected: 'Move {n} selected',
        fmMoveNothingMoved: 'Nothing was moved.',
        fmMoveProtectedSkipped: '{n} system financial folder(s) can\'t be moved and were left out.',
        fmMoveSelectedFolder: 'Selected',
        fmMoveHere: 'Move Here',
        fmMoveToFolder: 'Move to folder',
        fmMovedToFolder: 'Moved to {folder}',
        fmMoving: 'Moving...',
        fmMyDocuments: 'My Documents',
        fmNameInvalidChars: 'Name contains invalid characters',
        fmNameRequired: 'Name is required',
        fmNameTooLong: 'Name is too long (max 255 characters)',
        fmNeedAttentionCount: '{n} need attention',
        fmNewFolder: 'New folder',
        fmNewFolderCap: 'New Folder',
        fmNewName: 'New name',
        fmNoDocumentsFound: 'No documents found',
        fmNoDocumentsFoundPeriod: 'No documents found.',
        fmNoDocumentsMatching: 'No documents matching "{query}"',
        fmNoFoldersMatch: 'No folders match that search.',
        fmNoSubfolders: 'No subfolders here',
        fmNoTextExtractedImage: 'No text could be extracted from this image.',
        fmNoTranscriptionAvailable: 'No transcription available for this audio file.',
        fmNoVersionHistory: 'No version history available',
        fmOcrExtractedHint: 'Text below was extracted by Gemini Vision OCR — the image bytes are unmodified.',
        fmOpenOriginal: 'Open original ↗',
        fmOpenOriginalHint: 'Open the original image in a new tab',
        fmOrganize: 'Organize',
        fmOriginalImageHint: 'Original image · zoom or open in a new tab for full fidelity',
        fmPartialUploadStatus: '{success}/{total} uploaded · {failed} failed',
        fmPdfPreview: 'PDF preview',
        fmPreviewError: 'Preview Error',
        fmProcessingCount: 'Processing {current}/{total}',
        fmProcessingEllipsis: 'Processing…',
        fmProtectedFolderCannotDelete: 'System financial folders can\'t be deleted. You can rename them, and delete the files inside.',
        fmProtectedFolderCannotMove: 'System financial folders can\'t be moved. You can rename them; they stay in the Financials tree.',
        fmProtectedNoDelete: 'System folder — can\'t be deleted',
        fmProtectedNoSelect: 'System folder — can\'t be selected or deleted',
        fmRenameAndRetry: 'Rename and retry',
        fmRenameDocumentFailed: 'Failed to rename document',
        fmRenameDocumentTitle: 'Rename Document',
        fmRenameFolderTitle: 'Rename Folder',
        fmRenamedSuccess: 'Renamed!',
        fmRoot: 'Root',
        fmRowsByColumns: '{rows} row{rs} × {cols} column{cs}',
        fmSaveName: 'Save name',
        fmSaveWithThisName: 'Save with this name',
        fmSavingEllipsis: 'Saving…',
        fmSearchDocumentsEllipsis: 'Search documents…',
        fmSearchDocumentsPlaceholder: 'Search documents...',
        fmSearchFoldersPlaceholder: 'Search folders...',
        fmSelectAll: 'Select All',
        fmSelectedFiles: 'Selected Files ({n})',
        fmSelectedFolders: 'Selected Folders ({n})',
        fmSizeLabel: 'Size: {size}',
        fmSizeLimits: 'Individual files up to 1GB. Archives up to 100GB (max 1000 files).',
        fmSortAscending: 'Sort ascending',
        fmSortDescending: 'Sort descending',
        fmSpreadsheetPreview: 'Spreadsheet Preview',
        fmSpreadsheetPreviewHint: 'Showing data as a table. Download the file to see formulas, formatting, and charts.',
        fmStartFileUploads: '{n} file upload{s}',
        fmStartFolderUploads: '{n} folder upload{s}',
        fmStartFoldersAndFiles: '{folders} folder(s) and {files} file(s)',
        fmStartUpload: 'Start {rest}',
        fmStatusFailed: 'Failed to process',
        fmStatusLine: 'Status: {label}',
        fmStatusPending: 'Pending',
        fmStatusProcessing: 'Processing...',
        fmSupportedExtensions: 'PDF, DOCX, PPTX, XLSX, XLS, TXT, MD, PY, JS, TS, HTML, CSS, JSON, CSV, SQL, ZIP, 7Z, and many more...',
        fmSupportsFileTypes: 'Supports documents, source code, config files, data files, and archives',
        fmToggleSortDirection: 'Toggle sort direction',
        fmTranscription: 'Transcription',
        fmTypeArchive: 'Archive',
        fmTypeAudio: 'Audio',
        fmTypeCode: 'Code',
        fmTypeExcel: 'Excel',
        fmTypeFile: 'File',
        fmTypeFolder: 'Folder',
        fmTypeImage: 'Image',
        fmTypePdf: 'PDF',
        fmTypePpt: 'PPT',
        fmTypeText: 'Text',
        fmTypeWord: 'Word',
        fmUnknown: 'Unknown',
        fmUploadDocuments: 'Upload Documents',
        fmUploadFailed: 'Upload failed',
        fmUploadFiles: 'Upload Files',
        fmUploadStagingCustom: 'Custom',
        fmUploadStagingDestinationFor: 'Destination for {name}',
        fmUploadStagingGlobal: 'Set Global Destination',
        fmUploadStagingReset: 'Use the global destination',
        fmUploaded: 'Uploaded',
        fmUploadedCount: 'Uploaded {success}/{total} files',
        fmUploadingItemsPct: 'Uploading {n} item{s} ({pct}%)',
        fmUploads: 'Uploads',
        fmUploadsBackgroundHint: 'Uploads run in the background — you can close this and keep working.',
        fmUploadsNeedAttention: '{n} upload{s} need attention',
        fmVersionHistory: 'Version History',
        fmVersionN: 'Version {n}',
        fmVersionsColumn: 'Versions',
        fmView: 'View',
        fmViewGrid: 'Grid',
        fmViewLinear: 'Linear',
        fmZoomIn: 'Zoom in',
        fmZoomOut: 'Zoom out',
        msgConvDataOpenDoc: 'Open {name}',
        msgConvDataDocumentsTitle: 'Documents in this Conversation',
        msgConvDataDocsLoadError: 'Couldn\'t load documents.',
        msgConvDataDocsLoading: 'Loading…',
        msgConvDataDocsEmpty: 'No documents created or edited here.',
        msgConvDataDocsCreated: 'Created',
        msgConvDataDocsEdited: 'Edited',
        msgConvDataDialogAria: 'Conversation data',
        msgConvDataHeader: 'Conversation Data',
        msgConvDataDefaultTitle: 'Conversation',
        msgConvDataCloseAria: 'Close conversation data',
        msgConvDataLoadError: 'Couldn\'t load conversation data.',
        msgConvDataLoading: 'Loading…',
        msgCtxEmpty: '(empty)',
        msgCtxShowLess: 'Show less',
        msgCtxShowMore: 'Show more',
        msgCtxSectionSystemPrompt: 'System Prompt',
        msgCtxSectionWorkspaceContext: 'Workspace Context',
        msgCtxSectionConversationHistory: 'Conversation History',
        msgCtxSectionRepliedMessages: 'Replied Messages',
        msgCtxSectionDocumentContext: 'Document Context',
        msgCtxSectionCurrentQuery: 'Current Query',
        msgCtxTokensCount: '{n} tokens',
        msgCtxNotAvailable: '(not available)',
        msgCtxNotConfigured: '(not configured)',
        msgCtxNoHistory: '(no history)',
        msgCtxNoDocuments: '(no documents)',
        msgCtxRoleUser: 'User',
        msgCtxRoleAssistant: 'Assistant',
        msgCtxUnnamedDocument: 'Unnamed document',
        msgCtxLegendSystem: 'System',
        msgCtxLegendWorkspace: 'Workspace',
        msgCtxLegendHistory: 'History',
        msgCtxLegendReply: 'Reply',
        msgCtxLegendDocuments: 'Documents',
        msgCtxLegendQuery: 'Query',
        msgCtxAgenticPipeline: 'Agentic Pipeline',
        msgCtxIterationsLabel: '{n} iteration{s}',
        msgCtxToolCallsLabel: '{n} tool call{s}',
        msgCtxStepLabel: 'step {n}',
        msgCtxHeaderTitle: 'LLM Context',
        msgCtxTotalTokens: '{n} total tokens',
        msgCtxGroundingUsed: '🌐 Grounding Used',
        msgCtxGroundingOffered: '🌐 Grounding Offered',
        msgCtxShowStructuredView: 'Show Structured View',
        msgCtxShowRawPrompt: 'Show Raw Prompt',
        msgCtxLoadingContext: 'Loading context...',
        msgCtxFailedToLoad: 'Failed to load context',
        msgCtxNoContextAvailable: 'No context available for this message',
        // #460: the prompt snapshot is removed by the retention sweep after 90 days.
        msgCtxRetentionExpired: 'Technical details are kept for 90 days.',
        msgCtxFooterNote: 'This is the exact prompt and context sent to the LLM for this response.',
        msgThinkingLabel: 'Thinking',
        msgAnalyzingRequest: 'Analyzing your request...',
        msgToolsUsedLabel: '{n} tool{s} used',
        msgWithReasoning: 'with reasoning',
        msgProcessed: 'Processed',
        // Personalized Welcome (#367): named form is used once the user's
        // profile has loaded; the generic form covers the brief pre-identity
        // window (and any caller that never passes a name) so the heading
        // never flashes "Welcome undefined".
        msgWelcomeTitle: 'Welcome {name}',
        msgWelcomeTitleGeneric: 'Welcome',
        msgWelcomeSubtitle: 'Start a conversation with your personal artificial intelligence (PAI)',
        msgVoiceAudioUnavailable: 'Audio unavailable (link expired)',
        msgVoicePause: 'Pause',
        msgVoicePlay: 'Play',
        msgRteHeading: 'Heading',
        msgRteBold: 'Bold',
        msgRteItalic: 'Italic',
        msgRteBulletList: 'Bullet list',
        msgRteNumberedList: 'Numbered list',
        msgRteChecklist: 'Checklist',
        msgRteLink: 'Link',
        msgRteAddLink: 'Add',
        msgDragToResize: 'Drag to resize',
        msgUploadNoDocumentId: 'Upload did not return a document id',
        attachArchiveFiles: '{n} files saved',
        attachArchiveUnpacking: 'unpacking…',
        msgUploadDuplicateFilename: 'A document with this name already exists. Please provide a unique name.',
        msgUploadFailed: 'Upload failed',
        msgAttachmentDefaultFilename: 'Document',
        msgVoiceInaudible: '[inaudible]',
        msgTranscriptionFailed: 'Transcription failed',
        msgTranscriptionInterrupted: 'Transcription was interrupted — please record again',
        msgStreamErrorContent: 'I encountered an error while generating the answer: {message}',
        msgScrollToLatest: 'Scroll to latest message',
        msgDeleteConversationTitle: 'Delete Conversation',
        msgDeleteConversationMessage: 'Are you sure you want to delete "{name}"?\n\nThis action cannot be undone.',
        msgDeleteConversationFallbackName: 'this conversation',
        msgDeleteConversationFailed: 'Could not delete "{name}". It has been restored — please try again.',
        msgDeleteConversationsFailed: 'Could not delete those conversations. They have been restored — please try again.',
        msgDuplicateBatchSuffix: '{message} ({n} files in this batch need a unique name)',
        msgSelectFromFileManager: 'Select from File Manager',
        msgDropFilesToUpload: 'Drop files to upload',
        msgDropFilesHint: 'Files will be indexed and added to your library',
        msgUploadingAndIndexing: 'Uploading and indexing...',
        msgReceiptImageAlt: 'Receipt image',
        msgReceiptFallback: 'Receipt',
        msgKeptCopyLabel: 'Kept copy',
        msgKeptLabel: 'Kept',
        msgDuplicateLabel: 'Duplicate',
        msgReceiptImageWithName: '{name} image',
        msgPreviewReceipt: 'Preview receipt',
        msgDownloadReceipt: 'Download receipt',
        msgThatBookFallback: 'that book',
        msgUnassignedFallback: 'Unassigned',
        msgMovedIntoAnyway: 'Moved into {target} anyway — a second copy now lives there.',
        msgCouldNotMoveTransaction: 'Could not move the transaction. Please try again.',
        msgDeletedDuplicateFrom: 'Deleted the duplicate from {source}. {target} keeps the original.',
        msgCouldNotDeleteDuplicate: 'Could not delete the duplicate. Please try again.',
        msgKeptDuplicateIn: 'Kept the duplicate in {source}. Nothing was moved.',
        msgSkippedDiscardedUpload: 'Skipped — discarded the duplicate upload.',
        msgCouldNotDiscardUpload: 'Could not discard the upload. Please try again.',
        msgLoggedAnywayIn: 'Logged it anyway in {book}.',
        msgCouldNotLogExpense: 'Could not log the expense. Please try again.',
        msgTicketMayExistIn: 'This ticket may already exist in {target}',
        msgPossibleDuplicateDetected: 'Possible duplicate detected',
        msgAlreadyInTarget: 'Already in {target}',
        msgAlreadyInLedger: 'Already in your ledger',
        msgBeingMovedFrom: 'Being moved from {source}',
        msgNewUpload: 'New upload',
        msgMoreMatches: '+{n} more match{es} {place}.',
        msgInTargetBook: 'in {target}',
        msgInYourLedger: 'in your ledger',
        msgDeleteDuplicateFromTitle: 'Delete the duplicate from {source} — it already exists in {target}',
        msgDeleteDuplicateFromBtn: 'Delete duplicate from {source}',
        msgKeepDuplicateInTitle: 'Keep the duplicate in {source} — cancels the move',
        msgKeepDuplicateInBtn: 'Keep duplicate in {source}',
        msgMoveAnywayTitle: 'Move it into {target} anyway — creates a second copy',
        msgMoveAnywayBtn: 'Move anyway',
        msgCustomInstructionPrefix: 'Or type a custom instruction below — e.g.',
        msgCustomInstructionExample: '"move all duplicates to a new book called Check Later"',
        msgDiscardDuplicateUploadTitle: 'Discard this duplicate upload',
        msgSkipDiscardUploadBtn: 'Skip (discard upload)',
        msgLogAnywaySeparateTitle: 'Log it anyway as a separate transaction',
        msgProceedLogAnywayBtn: 'Proceed (log anyway)',
        msgCompareReceiptsPrefix: 'Compare the receipts above, then choose — or reply',
        msgProceedWord: '"proceed"',
        msgSkipWord: '"skip"',
        msgOcrUnableToExtract: 'We were unable to extract data from {target}',
        msgOcrThisImage: 'this image',
        msgOcrNImages: '{n} images',
        msgOcrNoTransactionLogged: 'No transaction was logged.',
        msgOcrUnreadableLabel: '{name} (unreadable)',
        msgOcrCouldntReadTotal: '{name} — couldn\'t read total',
        msgOcrCouldntReadReceipt: 'Couldn\'t read this receipt',
        msgEliminatedRedundantFiles: 'Eliminated {n} redundant file{s} — your ledger keeps one copy of each.',
        msgCouldNotDeleteRedundantFiles: 'Could not delete the redundant files. Please try again.',
        msgAddedDuplicatesAsTransactions: 'Added {n} duplicate{s} as separate transaction{s2} in {book}.',
        msgCouldNotAddDuplicates: 'Could not add the duplicates. Please try again.',
        msgBatchProcessedSummary: 'Batch processed — {unique} unique ticket{us} saved, {dup} duplicate{ds} found',
        msgBatchOneVersionRecorded: 'One version of each duplicate is already recorded in',
        msgBatchChooseRedundant: 'Choose what to do with the redundant {copies}:',
        msgCopySingular: 'copy',
        msgCopyPlural: 'copies',
        msgEliminateDuplicatesTitle: 'Permanently delete the redundant duplicate files to save storage',
        msgEliminateDuplicatesBtn: 'Eliminate Duplicates',
        msgAddRegardlessTitle: 'Save the redundant copies as additional separate transactions',
        msgAddRegardlessBtn: 'Add Regardless',
        msgNoTransactionsMatched: 'No transactions matched.',
        msgTransactionsCount: '{n} transactions',
        msgUnknownFallback: 'Unknown',
        msgNoDateFallback: 'no date',
        msgNoTranscription: '[no transcription]',
        msgVoiceMessagesPlural: 'Voice messages',
        msgVoiceMessageSingular: 'Voice message',
        msgHideTranscription: 'Hide transcription',
        msgShowTranscription: 'Show transcription',
        msgVoiceMessageN: 'Message {n}',
        msgDownloadReplyTitle: 'Download this reply as a file (does not save to your file manager)',
        msgDownloadAsDocument: 'Download as document',
        msgDownloadAsHeader: 'Download as',
        msgDownloadFormatPdf: 'PDF (.pdf)',
        msgDownloadFormatWord: 'Word (.docx)',
        msgDownloadFormatText: 'Plain text (.txt)',
        msgDownloadPreparing: 'Preparing…',
        msgDownloadedName: 'Downloaded “{name}”',
        msgDownloadFailed: 'Download failed',
        msgVoiceMessageEditedLabel: 'Voice Message {n} Edited:',
        msgRemoveFromReply: 'Remove from reply',
        msgReplyToThisMessage: 'Reply to this message',
        msgEditMessage: 'Edit message',
        msgEditYourMessagePlaceholder: 'Edit your message...',
        msgNoAudioTextOnly: 'No audio — this message will be saved as text-only.',
        msgVoiceMessageEditedSingular: 'Voice Message Edited',
        msgVoiceMessageNEdited: 'Voice Message {n} Edited',
        msgRemoveAudio: 'Remove audio',
        msgRemoveSegmentAria: 'Remove {label}',
        msgSaveAndRegenerate: 'Save & Regenerate',
        msgStopAndAddRecording: 'Stop and add recording',
        msgStopAndAddElapsed: 'Stop & add ({n}s)',
        msgAddVoice: 'Add voice',
        msgEditKeyboardHints: 'Enter to save, Shift+Enter for new line, Esc to cancel',
        msgEditedIndicator: '(edited)',
        msgDocumentUpdated: 'Document updated',
        msgDocumentUpdatedVersion: 'Document updated (v{version})',
        msgChunksIndexed: '{n} chunks indexed',
        msgClickToViewDocument: 'Click to view document',
        msgViewVersionHistory: 'View version history',
        msgHistory: 'History',
        msgDocumentDiscarded: 'Document discarded',
        msgDocumentCreated: 'Document created',
        msgMovedBadge: 'Moved',
        msgRenamedBadge: 'Renamed',
        msgRenameMoveDiscardTitle: 'Rename, move, or discard this document',
        msgManage: 'Manage',
        msgEnhancedWithDocContext: 'Enhanced with document context',
        msgUsingNDocuments: 'Using {n} document{s}',
        msgDocumentsUsedLabel: 'Documents used:',
        msgClickToViewOptions: 'Click to view options',
        msgDocumentNotAvailable: 'Document not available',
        msgUsingDocumentContext: 'Using document context',
        msgStandardAiResponse: 'Standard AI response',
        msgNoDocumentContextUsed: 'No document context used',
        msgGoogleSearchUsed: 'Google Search was used to answer this query',
        msgSearchCount: '({n} search{es})',
        // #349: the footer entry is "View Energy" now — the receipt is the
        // primary view and the raw trace lives one click inside it. The old key
        // stays: ContextViewerModal is still reachable and still titled.
        msgViewEnergy: 'View Energy',
        msgViewLlmContext: 'View LLM Context',
        msgViewDocumentContent: 'View document content',
        msgSaveToYourDevice: 'Save to your device',
        msgFailedToDownloadDocument: 'Failed to download document. Please try again.',
        msgClickToJumpOriginal: 'Click to jump to original message',
        navExportFailed: 'Could not export this conversation. Please try again.',
        navNewConversationTitle: 'New Conversation',
        navRenameFailed: 'Failed to rename conversation',
        navDeleteConversationsConfirm: 'Delete {n} conversation{s}?',
        navBulkDeleteFailed: 'Failed to delete some conversations. Please try again.',
        navUnfinishedDraft: 'Unfinished draft',
        navNewResponseReady: 'New response ready',
        navFromUser: 'from {name}',
        navConversationOptions: 'Conversation options',
        navConversationData: 'Conversation Data',
        navExportTraceTitle: 'Download a full debug trace (messages, thinking steps, prompts, LLM calls)',
        navExport: 'Export',
        navSearchAllChats: 'Search all chats...',
        navSearchAndManage: 'Search & manage',
        navAllCount: 'All ({n})',
        navEliminateCount: 'Eliminate ({n})',
        navSearching: 'Searching...',
        navNoMessagesFound: 'No messages found',
        navSearchTotalOccurrences: '{n} results',
        navSearchOccurrences: '{n} occurrences',
        navSearchNameMatch: 'Name match',
        navSearchExpandMessages: 'Show matching messages',
        navSearchCollapseMessages: 'Hide matching messages',
        navSearchLoadingMessages: 'Loading matches...',
        navSearchMessagesError: 'Could not load matches',
        navYou: 'You',
        navAi: 'AI',
        navLoadingConversations: 'Loading conversations...',
        navStartNewChat: 'Start a new chat to begin',
        navSharedWithMe: 'Shared with me',
        navHideConversations: 'Hide conversations',
        navShowConversations: 'Show conversations',
        navCloseMainChat: 'Close Main Chat',
        navHideSidebar: 'Hide sidebar',
        navShowSidebar: 'Show sidebar',
        navSharedReadOnly: 'Shared (read-only)',
        navSearchInConversation: 'Search in conversation',
        navShareConversation: 'Share conversation',
        navFindInConversation: 'Find in conversation...',
        navNoResults: 'No results',
        navPreviousMatch: 'Previous match (Shift+Enter)',
        navNextMatch: 'Next match (Enter)',
        navCloseSearchEsc: 'Close search (Esc)',
        navCollapseToSplitView: 'Collapse to split view',
        navExpandToCoverChat: 'Expand to cover chat',
        navClosePanelEsc: 'Close panel (Esc)',
        navAccountMenu: 'Account menu',
        navTelegramNotConfigured: 'PAI Tools is not configured',
        navTelegramLinkFailed: 'Failed to start PAI Tools linking',
        navTelegramUnlinkFailed: 'Failed to unlink Telegram',
        navConvoNotConfigured: 'PAI Conversations is not configured',
        navConvoLinkFailed: 'Failed to start linking',
        navConvoDisconnectFailed: 'Failed to disconnect',
        navCloseEsc: 'Close (Esc)',
        navNotifications: 'Notifications',
        navNotificationCenter: 'Notification Center',
        navNotificationCenterDesc: 'Choose which alerts you receive and where they land.',
        navSearchTimezone: 'Search timezone…',
        navAdmin: 'Admin',
        navUsageDashboard: 'Usage Dashboard',
        navUsageDashboardDesc: 'Monitor token usage, costs, and pipeline breakdown',
        navChannelMainOffice: 'Main Office',
        navChannelShortOffice: 'Office',
        navChannelShortTg: 'TG',
        navLinkTelegramHint: 'Link Telegram in Settings to enable.',
        navNotifLoadError: 'Could not load notification settings.',
        navNotifSaveError: 'Could not save. Please try again.',
        navNotificationCenterTitle: 'Notification Center',
        navAlerts: 'Alerts',
        navDeliveryChannels: 'Delivery Channels',
        navEnableFeatureNotifications: 'Enable {label} Notifications',
        navSilenceFeatureAlerts: 'Turn off to silence every {label} alert.',
        navEnableFeatureNotificationsAria: 'Enable {label} notifications',
        navRequiredAlwaysOn: 'Required — always on',
        navMinAgo: '{n} min ago',
        navHourAgo: '{n}h ago',
        navNow: 'Now',
        navInMin: 'In {n} min',
        navTodayAt: 'Today {time}',
        navTomorrowAt: 'Tomorrow {time}',
        navBadgeCalendar: 'Calendar',
        navBadgeInvitation: 'Invitation',
        navBadgeContactRequest: 'Contact request',
        navBadgeSystem: 'System notification',
        // Per-feature Bell badges (#381 §2) — a dispatched notification reads
        // as its own feature instead of borrowing the Reminder label.
        navBadgeFinance: 'Finance',
        navBadgeBoards: 'Boards',
        navBadgeVault: 'File Manager',
        navBadgeNotes: 'Notes',
        navBadgeNetwork: 'Network',
        navNotifComingSoon: 'Coming soon',
        navDismissNotification: 'Dismiss notification',
        navLoadRemindersFailed: 'Failed to load reminders',
        navClearReminderFailed: 'Failed to clear reminder',
        navActiveCount: '{n} active',
        navLoadingEllipsis: 'Loading…',
        navNoReminders: 'No reminders',
        navReminderExample: 'Try "remind me to call Charlie at 3pm"',
        navSharedWithEmail: 'Shared with {email}',
        navShareConversationTitle: 'Share Conversation',
        navShareReferencesDocs: 'This conversation references {n} document{s}',
        navSelectDocsPrefix: 'Select which documents',
        navSelectDocsSuffix: 'will be able to preview and download:',
        navOfSelected: '{selected} of {total} selected',
        navSelectAll: 'Select all',
        navDeselectAll: 'Deselect all',
        navSharing: 'Sharing...',
        navShareWithDocs: 'Share with {n} doc{s}',
        navShareWithoutDocs: 'Share without docs',
        navShareByEmail: 'Share with user (by email)',
        navRecentContacts: 'Recent Contacts',
        navSharedWithCount: 'Shared with ({n})',
        navNotSharedWithAnyone: 'Not shared with anyone',
        navRemoveAccess: 'Remove access',
        navShareReadOnlyFooter: 'Shared users get read-only access. They can view messages but cannot send, edit, or delete.',
        navShareBoardTitle: 'Share Board',
        navShareBookTitle: 'Share Book',
        navBoardPermOwnerDesc: 'Full board management: add & remove Members, rename the board. An Owner cannot change or remove another Owner.',
        navBoardPermMemberDesc: 'Full ticket autonomy: create, edit, move, assign and delete tickets. No member management — the Members list is read-only.',
        navBoardPermNote: 'Only the Original Creator can delete the board or manage Owner roles.',
        navBookPermOwnerDesc: 'Full ledger management: add & remove Members, edit or delete any transaction. An Owner cannot change or remove another Owner.',
        navBookPermMemberDesc: 'Full autonomy over their OWN entries only.',
        navBookPermNote: 'Only the Original Creator can delete the book or manage Owner roles.',
        navMembers: 'Members',
        navFailedToLoadMembers: 'Failed to load members',
        navAddOwner: 'Add Owner',
        navAddMember: 'Add Member',
        navMembersSkippedNotice: '{n} member{s} already had individual access and kept it — they are not managed by this team share.',
        navFailedToAdd: 'Failed to add.',
        navFailedToRemoveTeam: 'Failed to remove the team.',
        navFailedToChangeRole: 'Failed to change role',
        navFailedToRemove: 'Failed to remove',
        navYouParens: '(you)',
        navAccessViaTeamHint: 'Access comes from a team share — manage it in the Teams section',
        navViaTeam: 'via {team}',
        navTeamFallback: 'team',
        navRoleLabelOwner: 'owner',
        navRoleLabelMember: 'member',
        navLeave: 'Leave',
        navRemoveMember: 'Remove member',
        navMembersViaThisTeam: '{n} member{s} via this team',
        navRemoveTeamHint: 'Remove this team — everyone it added loses access',
        navRemovingTeamRemovesEveryone: 'Removing a team removes everyone it added. To keep someone, invite them individually afterwards.',
        navInviteContact: 'Invite Contact',
        navTeamMemberCount: 'Team · {n} members',
        navAlreadyHereViaTeam: 'Already here via {team}',
        navPermissionInfo: 'Permission info',
        navTeamsAddedAsMembers: 'Teams are added as Members, and their access follows the team roster.',
        // ── Legal & cookie consent (iubenda) ───────────────────────────────
        // The three document TITLES are what the links read as; the consent
        // sentence is assembled from parts so each language can put its own
        // article ("the" / "los", "la") beside the right link.
        legalPrivacyPolicy: 'Privacy Policy',
        legalCookiePolicy: 'Cookie Policy',
        legalTerms: 'Terms and Conditions',
        // The one document PAI hosts itself, at /legal/pai-data-ai-addendum.
        // Only its CHROME is translated — the addendum's body is a legal
        // instrument incorporated into the Terms and stays in its signed
        // English.
        legalAddendum: 'Data & AI Addendum',
        // The DMCA takedown-notice page we host at /dmca. Same rule as the
        // Addendum above: only the chrome is translated, the notice body is a
        // legal document tied to a U.S. Copyright Office filing.
        legalDmca: 'DMCA',
        legalEyebrow: 'Legal',
        legalBackToPai: 'Back to PAI',
        // Copyright line + legal-entity popover (#461). Only this chrome is
        // translated: the entity name, address and support email are literal.
        legalAllRightsReserved: 'All rights reserved.',
        legalEntityProductOf: 'PAI is a product of',
        legalEntityEmailLabel: 'Email:',
        settingsLegalTitle: 'Legal',
        settingsLegalDocuments: 'Policies and terms',
        settingsLegalDesc: 'Read the documents that govern your use of PAI.',
        settingsCookiePrefs: 'Cookie preferences',
        settingsCookiePrefsDesc: 'Review or change the tracking choices you made.',
        settingsCookiePrefsAction: 'Manage',
        settingsCookiePrefsUnavailable: 'The consent widget could not be loaded.',
        // The signup consent CHECKBOX (first person — the user is the one
        // agreeing) …
        authLegalConsentPrefix: 'I agree to the',
        authLegalConsentMiddle: 'and acknowledge the',
        authLegalConsentSuffix: '.',
        // Extra terms and 18 (#410) — its own trailing sentence, a SEPARATE
        // assertion from the Terms/Privacy acknowledgement above. PROOF TEXT:
        // kept in step with BE's CONSENT_PROOF_PASSWORD_CHECKBOX.
        authLegalConsentAge: 'I confirm that I am 18 years of age or older.',
        authLegalConsentRequired: 'Please accept the Terms and Conditions to create an account.',
        // … and the click-wrap under the Google buttons (second person — it
        // describes what the tap does). The English here is PROOF TEXT: the
        // backend records what the user was shown, so it changes only together
        // with that record. Separate keys from the checkbox above because the
        // two share an English middle but not a Spanish one ("reconozco" vs
        // "reconoces").
        authGoogleConsentPrefix: 'By continuing with Google you agree to the',
        authGoogleConsentMiddle: 'and acknowledge the',
        authGoogleConsentSuffix: '.',
        // ── Two-step verification (2FA #333) ─────────────────────────────────
        mfaFactorLegacyPhone: 'Phone (no longer supported)',
        mfaFactorTotp: 'Authenticator app',
        mfaErrInvalidCode: 'That code is not correct. Check it and try again.',
        mfaErrExpired: 'That code has expired. Wait for a new one and try again.',
        mfaErrTooMany: 'Too many attempts. Please wait a few minutes and try again.',
        mfaErrWrongPassword: 'Incorrect password.',
        mfaCodesFileTitle: 'PAI recovery codes',
        mfaCodesFileNote: 'Each code works once. Keep them somewhere safe.',
        mfaRecoveryInvalid: 'That recovery code is not valid.',
        mfaRecoveryTitle: 'Use a recovery code',
        mfaRecoveryIntro: 'Enter one of the recovery codes you saved when you set up two-step verification. Using one turns two-step verification off for your account.',
        mfaRecoveryLabel: 'Recovery code',
        mfaRecoverySubmit: 'Use recovery code',
        mfaUseCodeInstead: 'Use a verification code instead',
        mfaBackToSignIn: 'Back to sign in',
        mfaChallengeTitle: 'Two-step verification',
        mfaChallengeTotpIntro: 'Enter the 6-digit code from your authenticator app.',
        mfaNoTotpRecovery: 'This account has no authenticator app set up. Use one of your recovery codes to sign in.',
        mfaNoTotp: 'This account has no authenticator app set up, so it cannot be confirmed here.',
        mfaChooseMethod: 'Verification method',
        mfaCodeLabel: 'Verification code',
        mfaVerifying: 'Verifying…',
        mfaVerify: 'Verify',
        mfaUseRecovery: 'Use a recovery code',
        mfaCodesTitle: 'Save your recovery codes',
        mfaCodesIntro: 'If you lose your phone, a recovery code is the only way back into your account. Keep them somewhere safe, away from your phone.',
        mfaCodesCopied: 'Copied',
        mfaCodesCopy: 'Copy',
        mfaCodesDownload: 'Download (.txt)',
        mfaCodesAck: 'I have saved these 10 recovery codes. Each one works once.',
        mfaContinue: 'Continue',
        mfaStatusOn: 'On. Every sign-in asks for a code from your second step.',
        mfaStatusOff: 'Off. Add a second step to sign-in so a stolen password is not enough.',
        mfaTotpScanTitle: 'Scan with your authenticator app',
        mfaTotpScanIntro: 'Open Google Authenticator, 1Password, Authy or a similar app and scan this code. Or type the key below into the app.',
        mfaTotpQrAlt: 'QR code for your authenticator app',
        mfaTotpKeyLabel: 'Setup key',
        mfaEnrollCodeLabel: 'Enter the 6-digit code',
        mfaEnrollCodeTotp: 'Your authenticator app now shows a code for PAI.',
        mfaActivate: 'Activate',
        mfaReauthTitle: 'Confirm it is you',
        mfaReauthPasswordIntro: 'For your security, enter your password to continue.',
        mfaReauthGoogleIntro: 'For your security, sign in with Google again to continue.',
        mfaSecuritySection: 'Security',
        mfaTitle: 'Two-step verification',
        mfaFactorAdded: 'Added {date}',
        mfaRecoveryCodesTitle: 'Recovery codes',
        mfaCodesRemaining: '{n} of 10 left',
        mfaRegenerateCodes: 'Generate new recovery codes',
        mfaAddTotp: 'Add an authenticator app',
        mfaSetUp: 'Set up two-step verification',
        mfaMethodsHint: 'Use an authenticator app such as Google Authenticator, 1Password or Authy. Your recovery codes are the backup.',
        mfaEnableTotp: 'Set up authenticator app',
        mfaEnabledNotice: 'Two-step verification is on. Your other devices have to sign in again.',
        mfaRemovedNotice: 'Method removed.',
        mfaCodesSavedNotice: 'New recovery codes saved. The old ones no longer work.',
        mfaVerifyEmailNotice: 'Verify your email first — we sent you a link.',
        mfaMessagingNote: 'Messaging links (Telegram) can only be changed from Settings.',
        mfaSignedOutNotice: 'Your account now uses two-step verification. Sign in again to continue.',
        mfaRecoveredNotice: 'Two-step verification has been turned off. Set it up again in Settings → Security.',
        mfaRecoveredGoogle: 'Press Continue with Google to sign in again.',
    },
    es: {
        newChat: 'Nueva Conversación',
        fileManager: 'Mis Documentos',
        select: 'Seleccionar',
        // ── Vocabulario COMPARTIDO (pase de localización completa "Spanish") ──
        commonCancel: 'Cancelar',
        commonSave: 'Guardar',
        commonSaving: 'Guardando…',
        commonDelete: 'Eliminar',
        commonConfirm: 'Confirmar',
        commonClose: 'Cerrar',
        commonEdit: 'Editar',
        commonRename: 'Renombrar',
        commonRemove: 'Quitar',
        commonAdd: 'Añadir',
        commonBack: 'Atrás',
        commonNext: 'Siguiente',
        commonOpen: 'Abrir',
        commonShare: 'Compartir',
        commonDownload: 'Descargar',
        commonPreview: 'Vista previa',
        commonUpload: 'Subir',
        commonRetry: 'Reintentar',
        statusConnected: 'Conectado',
        statusLoading: 'Cargando modelo...',
        statusError: 'Error de conexión',
        statusChecking: 'Conectando...',
        errorRetry: 'Se produjo un error, por favor, inténtalo de nuevo.',
        errorRefLabel: 'Ref.: {ref}',
        commonProfileUnavailable: 'No hemos podido cargar tu cuenta. Comprueba tu conexión e inténtalo de nuevo.',
        commonSearch: 'Buscar',
        commonName: 'Nombre',
        commonDate: 'Fecha',
        commonTime: 'Hora',
        commonSize: 'Tamaño',
        commonType: 'Tipo',
        commonStatus: 'Estado',
        commonDescription: 'Descripción',
        commonTitle: 'Título',
        commonAmount: 'Importe',
        commonCurrency: 'Moneda',
        commonCategory: 'Categoría',
        commonNotes: 'Notas',
        commonLocation: 'Ubicación',
        commonEmail: 'Correo',
        commonPassword: 'Contraseña',
        commonCopy: 'Copiar',
        // ── Chrome del compositor / ChatInput ──────────────────────────────
        composerExpenseFallback: 'Gasto',
        composerRemoveReference: 'Quitar referencia',
        composerRemoveSegment: 'Quitar este segmento',
        composerTranscribingAudio: 'Transcribiendo audio…',
        composerHideTranscription: 'Ocultar transcripción',
        composerShowTranscription: 'Ver transcripción',
        composerEditedTitle: 'Esta transcripción se editó — el texto corregido es el que PAI usará',
        composerEdited: 'Editada',
        composerTranscriptionFailed: 'La transcripción falló — {reason}',
        composerTranscriptionFailedFallback: 'prueba a quitarla y grabar de nuevo',
        composerSilent: '[en silencio]',
        composerAddAttachment: 'Añadir adjunto',
        composerAttachmentsCount: '{n} adjuntos',
        composerRemoveAllAttachments: 'Quitar todos los adjuntos',
        composerClearAllAttachments: 'Vaciar todos los adjuntos',
        composerClearAll: 'Vaciar todo',
        composerReplyingTo: 'Respondiendo a {n} mensaje{s}',
        composerCancelAllReplies: 'Cancelar todas las respuestas',
        composerRemoveReply: 'Quitar esta respuesta',
        composerYou: 'Tú',
        composerVoiceMessagesCount: '{n} mensajes de voz',
        // La barra de grabación (#276) — el ejemplo principal del PO en la tarjeta.
        composerRecordingLabel: 'Grabando...',
        composerDiscardTitle: 'Descartar esta grabación',
        composerAcceptTitle: 'Detener, añadir este segmento a la cola y seguir escribiendo',
        composerPlaceholder: 'Escribe tu mensaje... (Shift+Intro para nueva línea)',
        composerPlaceholderShort: 'Escribe tu mensaje',
        composerPlaceholderMinimal: 'Mensaje...',
        composerStopGenerating: 'Detener la generación de la respuesta',
        composerRecordVoice: 'Grabar mensaje de voz',
        // Dictado (#541) — el micrófono del chat principal transcribe al cuadro de mensaje.
        dictationStartTitle: 'Dictar',
        dictationStopTitle: 'Detener e insertar el texto en tu mensaje',
        dictationTranscribing: 'Transcribiendo…',
        dictationDone: 'Listo',
        dictationEmpty: 'No te he entendido — inténtalo de nuevo',
        // El sufijo {s} inglés no sirve en español (transcripción → transcripciones),
        // así que esta forma lleva el número entre paréntesis y omite {s}.
        composerFinalizing: 'Finalizando transcripciones ({n})…',
        composerSend: 'Enviar',
        // ── Errores de autenticación de Firebase (useAuth) ─────────────────
        authLoginFailed: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
        authInvalidEmail: 'La dirección de correo no es válida.',
        authAccountDisabled: 'Esta cuenta ha sido deshabilitada.',
        authNoAccount: 'No hay ninguna cuenta con este correo.',
        authWrongPassword: 'Contraseña incorrecta.',
        authInvalidCredential: 'Correo o contraseña incorrectos.',
        authTooManyAttempts: 'Demasiados intentos fallidos. Inténtalo de nuevo más tarde.',
        authGoogleFailed: 'No se pudo iniciar sesión con Google. Inténtalo de nuevo.',
        authLoginCancelled: 'Inicio de sesión cancelado.',
        authPopupBlocked: 'Se bloqueó la ventana emergente. Permite las ventanas emergentes de este sitio.',
        authSignupFailed: 'No se pudo crear la cuenta. Inténtalo de nuevo.',
        authEmailInUse: 'Ya existe una cuenta con este correo.',
        authWeakPassword: 'La contraseña debe tener al menos 6 caracteres.',
        // ── Tarjeta de invitación del Chat Principal (event_invite_request) ─
        eventInviteCardTitle: '¿Invitar a “{title}”?',
        eventInviteRecipients: 'Invitando a',
        eventInviteDropRecipient: 'Quitar de la invitación',
        eventInviteNeedRecipient: 'Mantén al menos un destinatario para invitar.',
        eventInviteConfirmBtn: 'Confirmar',
        eventInviteConfirmBusy: 'Enviando…',
        eventInviteDeclineBtn: 'Rechazar',
        eventInviteEditBtn: 'Editar',
        eventInviteEditDone: 'Listo',
        eventInviteDone: 'Evento creado y {count} invitación(es) enviada(s).',
        eventInviteDeclined: 'Invitación cancelada.',
        // #535 — invitaciones a una serie que se repite (una por persona).
        eventInviteSeriesDone: 'Serie creada — {events} eventos, {count} invitación(es) enviada(s) (una por persona).',
        eventInviteSeriesExistingDone: '{count} invitación(es) enviada(s) para {events} eventos (una por persona).',
        eventInviteExistingDone: '{count} invitación(es) enviada(s).',
        caleInvitationSeries: 'Se repite · {summary}',
        caleInvitationPackage: '{n} eventos: {titles}',
        invitePkgAcceptAll: 'Aceptar todos',
        invitePkgCount: '{n} de {total} eventos pendientes',
        invitePkgDeclineAll: 'Rechazar todos',
        invitePkgHint: "Pulsa Aceptar todos para aceptarlos todos o Rechazar todos para rechazarlos todos. Si solo quieres algunos, escríbelo, por ejemplo: 'Acepta el 3 y rechaza el resto' o 'Rechaza el 4 y el 9, acepta los demás'.",
        invitePkgTitle: 'Invitación de {name}',
        invitePkgSomeone: 'alguien',
        invitePkgPendingCount: '{n} de {total} pendientes',
        invitePkgAccepted: 'Aceptados — ya están en tu calendario.',
        invitePkgDeclined: 'Rechazados.',
        invitePkgAnswered: 'Esta invitación ya está respondida.',
        eventConflictTitle: 'Esto se solapa con un evento que ya has confirmado:',
        eventConflictForce: 'Reservar de todos modos',
        eventConflictCancel: 'Cancelar',
        cardDocumentFallback: 'documento',
        cardDocumentFallbackCap: 'Documento',
        cardTransactionFallback: 'Transacción',
        cardPreviewReceipt: 'Ver el recibo',
        cardDownloadReceipt: 'Descargar el recibo',
        // "ticket" pluraliza igual en español (ticket→tickets), así que el sufijo
        // {s} del inglés sí funciona aquí y conserva el singular.
        columnMoveDetail: 'Moviendo la columna {column} con {n} ticket{s} a {board} en {position}.',
        columnMoveEnd: 'el final',
        // ── Chrome de Contactos / Agenda / canal del día ──────────────────
        auxDeleteNote: 'Eliminar nota',
        auxLoadingEllipsis: 'Cargando…',
        auxContactNameRequired: 'Nombre *',
        auxClearSearch: 'Borrar la búsqueda',
        auxContactsMenu: 'Menú de contactos',
        auxClosePanelEsc: 'Cerrar el panel (Esc)',
        auxDayOptions: 'Opciones del día',
        auxSearchInThisDay: 'Buscar en este día',
        auxShareConversation: 'Compartir conversación',
        auxCloseDay: 'Cerrar el día',
        auxSocialHandles: 'Perfiles sociales',
        auxAddToFavorites: 'Añadir a favoritos',
        auxRemoveMethod: 'Quitar {label} {n}',
        auxAddMethod: 'Añadir {label}',
        auxQrUnavailable: 'QR no disponible',
        msgSharedReadOnly: 'Esta es una conversación compartida. Tienes acceso de solo lectura.',
        // Tooltips de «quitar de este lote» en las tarjetas de confirmación.
        cardKeepItem: 'Conservar {name} — quitar de la eliminación',
        cardKeepItemBatch: 'Conservar {name} — quitar del lote',
        cardKeepRename: 'Conservar {name} — quitar del lote de renombrado',
        cardKeepComment: 'Conservar este comentario — quitar de la eliminación',
        cardRemoveFromSelection: 'Quitar {name} de la selección',
        cardDontShareWith: 'No compartir con {name}',
        cardNewNameFor: 'Nuevo nombre para {name}',
        cardThisFile: 'este archivo',
        cardThisTransaction: 'esta transacción',
        cardThisEvent: 'este evento',
        cardThisNote: 'esta nota',
        cardThisReminder: 'este recordatorio',
        cardThisBoard: 'este tablero',
        cardThisDocument: 'este documento',
        cardFileNoun: 'archivo',
        // Cabeceras de canal en "Buscar en todos los chats".
        searchGroupMainChat: 'Chat Principal',
        // Voice composer (audio parity #173)
        voiceRecord: 'Grabar voz',
        voiceRecording: 'Grabando',
        voiceDiscard: 'Descartar',
        voiceAccept: 'Aceptar',
        voiceTranscribing: 'Transcribiendo…',
        voiceRemove: 'Quitar',
        voiceTranscriptPlaceholder: 'Transcripción — edítala antes de enviar',
        // Botón de expandir/contraer la transcripción (#296 re-abierto)
        voiceTranscriptExpand: 'Ver la transcripción completa',
        voiceTranscriptCollapse: 'Contraer la transcripción',
        mainChat: 'Chat Principal',
        launcherNotes: 'Notas y Tareas',
        launcherReminders: 'Recordatorios y Notificaciones',
        // #534 — tile teaser de PAI Map (nunca "Próximamente", nunca una fecha)
        launcherPaiMapStatus: 'En desarrollo',
        navLauncher: 'PAI — todas las funciones',
        navAccount: 'Cuenta',
        documents: 'Gestor de Archivos',
        calendar: 'Calendario',
        expenses: 'Finanzas',
        agenda: 'Contactos',
        boards: 'Tableros',
        workspace: 'Espacio de trabajo',
        notes: 'Notas y Tareas',
        reminders: 'Recordatorios y Notificaciones',
        // PAI Rulebook (#146)
        rulebook: 'Reglamento',
        rulebookFolio: 'El Folio',
        rulebookFolioPlaceholder: 'Escribe las reglas que PAI debe seguir…',
        rulebookDesk: 'La Mesa del Asistente',
        rulebookDeskPlaceholder: 'Describe una regla que PAI deba seguir…',
        rulebookSaving: 'Guardando…',
        rulebookSaved: 'Guardado',
        rulebookSaveFailed: 'No se pudo guardar',
        rulebookPreview: 'Vista previa',
        rulebookEdit: 'Editar',
        rulebookDeskError: 'Algo salió mal. ¿Lo intentamos de nuevo?',
        // La tarjeta de confirmación «Añadir regla» (#146).
        ruleAddTitle: '¿Añadir esta regla a tu Folio?',
        ruleAddConfirmBtn: 'Añadir regla',
        ruleAddBusy: 'Añadiendo…',
        ruleAddEditBtn: 'Editar',
        ruleAddEditingHint: 'Sigue puliéndola abajo: todavía no se ha añadido nada.',
        ruleAddDone: 'Añadida a tu Folio: {rule}',
        ruleAddCancelled: 'Borrador descartado: no se añadió nada.',
        ruleAddFailed: 'No se pudo añadir la regla. Inténtalo de nuevo.',
        // ── Centro de Energía (Energy HUB parte 1) ──────────────────────────
        energy: 'Tanque de Energía',
        energyHub: 'Centro de Energía',
        energyHubDesc: 'Consulta exactamente en qué se va tu energía.',
        energyOpenHub: 'Ver el Centro de Energía',
        energyPillar_listening: 'Escucha',
        energyPillar_viewing: 'Visión',
        energyPillar_writing: 'Escritura',
        energyPillar_reasoning: 'Razonamiento',
        energyPillar_executing: 'Ejecución',
        energyTankTitle: 'Depósito de energía',
        energyBalance: 'Saldo',
        energyGray: 'Puntos grises',
        energyAction: 'Puntos de acción',
        energyCycle: 'Ciclo',
        energyEmpty: 'La energía de PAI se ha agotado.',
        energyLockdownBody: 'Las nuevas solicitudes están en pausa hasta tu próximo ciclo o una recarga. Tus datos no se han tocado.',
        energyStorage: 'Almacenamiento',
        energyStorageReserved: '{n} días reservados',
        energyCleanSlate: 'Borrón y cuenta nueva',
        // Desglose de la pila gris (#302).
        energyStorageBreakdown: 'Desglose del almacenamiento',
        energyStorageBreakdownHint: 'Contra qué está reservada tu pila gris',
        energyStorageTotal: 'Total al mes',
        energyStorageCatDocuments: 'Documentos y PDFs',
        energyStorageCatDocumentsHint: 'Archivos de tu búnker',
        energyStorageCatImages: 'Imágenes y capturas',
        energyStorageCatImagesHint: 'Fotos, recibos y capturas de pantalla',
        energyStorageCatVoice: 'Notas de voz',
        energyStorageCatVoiceHint: 'Mensajes de voz del chat y Telegram',
        energyStorageCatData: 'Estructuras de datos',
        energyStorageCatDataHint: 'Conversaciones, notas, tableros, finanzas, contactos',
        energyStorageCatOverhead: 'Sobrecarga del sistema',
        energyStorageCatOverheadHint: 'Versiones antiguas, archivos eliminados e índices',
        energyGraystack: 'Graystack',
        energyGraystackHint: 'Puntos reservados para tu almacenamiento. Suben y bajan en cuanto añades o eliminas un archivo.',
        energyGraystackConsumed: 'Consumidos',
        energyGraystackConsumedHint: 'La parte de este ciclo que ya ha pasado',
        energyGraystackReserved: 'Reservados',
        energyGraystackReservedHint: '{days} días restantes del ciclo + {buffer} días de gracia',
        energyGraystackProtected: 'Tus puntos reservados cubren tu almacenamiento hasta el {date}.',
        energyUploadRefused: 'Este archivo necesita {needed} puntos de almacenamiento; tienes {available} disponibles.',
        energyRangeCycle: 'Este ciclo',
        energyCyclePrev: 'Ciclo anterior',
        energyCycleNext: 'Ciclo siguiente',
        energyRangeCustom: 'Personalizado',
        energyRangeFrom: 'Desde',
        energyRangeTo: 'Hasta',
        energyPizzaLabel: 'Energía por pilar',
        energyTotalPoints: 'Puntos totales',
        energyOverview: 'Resumen',
        energyBreadcrumb: 'Navegación de energía',
        energyBack: 'Volver al resumen',
        energyCategories: 'Categorías',
        energyItems: 'Actividad',
        energyNoCategories: 'Sin actividad en este pilar para el periodo seleccionado.',
        energyNoItems: 'No hay nada registrado aquí para el periodo seleccionado.',
        // Gráfico diario + vista del día (#385).
        energyDailyTitle: 'Energía diaria',
        energyDailyBarLabel: '{date}: {pts} puntos',
        energyDailyFutureLabel: '{date}: todavía no',
        energyDailyToday: 'Hoy',
        energyDailyNotYet: 'todavía no',
        energyDailyRangeTotal: 'Total del periodo',
        energyDailyError: 'No se pudo cargar tu energía diaria.',
        energyDailyEmpty: 'No se usó energía en este periodo.',
        energyDayBack: 'Volver al periodo',
        energyDayEyebrow: 'Energía usada el',
        energyDayNone: 'No se usó energía este día.',
        energyDayDesc_listening: 'Convertir lo que dijiste o subiste en algo que PAI pueda usar: transcripción de voz e indexación.',
        energyDayDesc_viewing: 'Leer imágenes y escaneos: OCR, recibos y tarjetas de visita.',
        energyDayDesc_writing: 'Producir texto para ti: documentos, ediciones, resúmenes e informes.',
        energyDayDesc_reasoning: 'Pensar: entender peticiones, investigar, buscar en la web y redactar respuestas.',
        energyDayDesc_executing: 'Actuar sobre tus datos: crear, actualizar, mover y eliminar registros.',
        energyLoadMore: 'Cargar más',
        energyLoading: 'Cargando…',
        energyError: 'No se pudieron cargar tus datos de energía.',
        energyPtsSuffix: 'pts',
        energyInputTokens: 'Tokens de entrada',
        energyThinkingTokens: 'Tokens de razonamiento',
        energyOutputTokens: 'Tokens de salida',
        // Atribución de origen (Labelling energy #315).
        energyChannel_web: 'Chat principal',
        energyChannel_telegram: 'Telegram',
        energyChannel_telegram_convo: 'Telegram (Conversaciones)',
        energyChannelOtherMessaging: 'Mensajería',
        energyChannel_rulebook: 'Reglamento',
        energyFromConversation: 'De la conversación: {title}',
        // Recibo de Energía + Termostato de Contexto (#349).
        receiptTitle: 'Recibo de Energía',
        receiptSubtitle: 'Lo que te costó este mensaje',
        receiptLineTranscription: 'Transcripción',
        receiptLineTranscriptionHint: 'Audio e imágenes convertidos en texto',
        receiptLineInputContext: 'Contexto de entrada (Historial de la conversación)',
        receiptLineInputContextHint: 'El historial de la conversación que PAI estaba sosteniendo',
        receiptLineMemoryCompression: 'Compresión de la memoria',
        receiptLineMemoryCompressionHint: 'Resumir el hilo cuando superó su presupuesto de memoria',
        receiptLineSystemOverhead: 'Instrucciones del sistema',
        receiptLineSystemOverheadHint: 'Las instrucciones de PAI y las definiciones de sus herramientas, reenviadas en cada paso',
        receiptLineReasoning: 'Razonamiento y Salida de PAI',
        receiptLineReasoningHint: 'Lo que pensó, y la generación del texto del mensaje',
        receiptPartThinking: 'Razonamiento de PAI',
        receiptPartOutput: 'Salida de PAI',
        receiptLineTools: 'Herramientas y Creación',
        receiptLineToolsHint: 'Búsquedas web, documentos, extracción',
        receiptTokens: '{n} Tokens',
        receiptTokensPerStep: '{n} Tokens ({calls} pasos × ~{per} reenviados en cada paso)',
        receiptPerStepDetail: '{calls} pasos × ~{per} reenviados en cada paso',
        receiptHistoryTokens: 'Historial {n}',
        receiptMessageTokens: 'Este mensaje {n}',
        receiptCompositionHint: 'El control de memoria solo limita el historial: tu mensaje nuevo siempre se envía completo.',
        receiptPartAudio: 'Transcripción de audio',
        receiptPartOcr: 'OCR / Procesamiento de imagen',
        receiptPartToolTokens: 'Tokens de herramientas y creación',
        receiptPartSearchFees: 'Tarifas de búsqueda web',
        receiptSearchFeeDetail: '{n} búsquedas web × {rate}',
        receiptStepGrounded: 'Búsqueda de Google ({n} consultas)',
        receiptPartPlacesFees: 'Búsquedas de lugares en Google Maps',
        receiptPlacesFeeDetail: '{n} búsquedas de lugares × {rate}',
        receiptStepPlaces: 'Búsqueda de lugar en Google Maps',
        receiptStepsSummary: '{n} llamadas al modelo en este turno',
        receiptLinePrefixCache: 'Preparación de la caché del prompt',
        receiptLinePrefixCacheHint: 'Guarda una vez las instrucciones de PAI para que los siguientes mensajes las reenvíen por mucho menos',
        receiptPartCacheCreate: 'Preparación (abarata los siguientes turnos)',
        receiptPartCacheExtend: 'Mantenida activa para los siguientes turnos',
        receiptStepCacheCreate: 'Preparación de la caché del prompt',
        receiptStepCacheExtend: 'Caché del prompt mantenida activa',
        receiptStoppedMessageCost: 'Coste del mensaje detenido',
        receiptStoppedMessageSteps: '{n} pasos antes de que lo detuvieras',
        msgStoppedBy: 'PAI fue detenido por {name}',
        msgStoppedByYou: 'PAI fue detenido',
        msgTurnPending: 'PAI está trabajando en esto…',
        msgTurnPendingStale: 'No llegó respuesta para este mensaje.',
        msgTurnSending: 'Enviando…',
        msgTurnResend: 'Reenviar',
        receiptStepTool: 'Ronda de herramienta',
        receiptStepFinal: 'Redactando la respuesta',
        receiptStepVerify: 'Recomprobación de verificación',
        receiptStepSynthesis: 'Respondiendo con lo recopilado',
        receiptStepAnomaly: 'Reintento tras un fallo de streaming',
        receiptStepsNote: 'Cada ronda de herramienta es una llamada al modelo, y redactar la respuesta final es una más, así que un turno siempre factura una llamada más que las herramientas que ves.',
        receiptCached: '{n} en caché',
        receiptActive: '{n} activos',
        receiptIfActive: '{n} si fuera 100% activo',
        receiptSaved: '· Ahorro del {n}%',
        receiptCacheRate: 'Tasa de aprovechamiento de la caché: {n}%',
        receiptCacheRateDetail: '{cached} de {total} tokens de entrada servidos desde la caché',
        receiptCacheInfoLabel: '¿Qué es la entrada en caché?',
        receiptCacheInfo: 'La entrada en caché es información de tus mensajes anteriores, documentos o instrucciones del sistema que PAI ya ha procesado. El sistema mantiene esa información en estado activo durante un tiempo para no tener que volver a leerla desde cero. Por eso la información en caché cuesta muchos menos puntos de energía en el cálculo final.',
        receiptPoints: '{n} Puntos de Energía',
        receiptTotal: 'Coste total del turno',
        receiptTankPct: '{n}% de tu Depósito de Energía',
        receiptTechnicalDetails: 'Detalles técnicos',
        thermostatTitle: 'Presupuesto de memoria',
        thermostatPages: '{n} Páginas',
        thermostatTotalRecall: 'Memoria total',
        thermostatWords: '~{n} palabras de memoria',
        thermostatWholeThread: 'Toda la conversación',
        thermostatInForce: 'En vigor: {v}',
        thermostatApplyChat: 'Aplicar solo a este chat',
        thermostatApplyGlobal: 'Guardar como predeterminado',
        thermostatGlobalHint: 'Se aplica a las conversaciones nuevas. Cada chat puede cambiarlo desde su Recibo de Energía.',
        thermostatSaveFailed: 'No se pudo guardar ese presupuesto.',
        thermostatSavedChat: 'Guardado solo para este chat.',
        thermostatSavedGlobal: 'Guardado como predeterminado para los chats nuevos y este.',
        thermostatSavedDefault: 'Guardado como predeterminado para los chats nuevos.',
        thermostatInfoAria: 'Sobre el presupuesto de memoria de PAI',
        thermostatInfoBody: 'Este es el presupuesto de memoria de PAI. Si quieres que PAI recuerde un hilo más largo de tu conversación, tiene que sostener más información en su cabeza y "pensar" más detalles. Eso lo hace más inteligente, pero gasta más Energía por mensaje.',
        thermostatInfoPage: '1 Página = ~560 palabras de memoria.',
        thermostatInfoLowLabel: 'Presupuesto bajo:',
        thermostatInfoLow: 'PAI se centra en el "ahora". Ahorra Energía.',
        thermostatInfoHighLabel: 'Presupuesto alto:',
        thermostatInfoHigh: 'PAI sostiene todo el hilo. Más inteligente, pero cuesta más.',
        thermostatWarnTitle: 'Aviso de consumo alto de Energía',
        thermostatWarnBody: 'Esto aumentará significativamente el coste por mensaje. ¿Continuar?',
        // Tarjeta de Datos de la Conversación (#301).
        convDataUserMessages: 'Mensajes del usuario',
        convDataAiAnswers: 'Respuestas de la IA',
        convDataInteractionEnergy: 'Energía de interacción total',
        convDataInteractionEnergyHint: 'Gastado una vez, para producir este hilo',
        convDataPtsUnit: 'pts',
        convDataTokensUnit: 'tokens',
        convDataStorageCost: 'Coste de almacenamiento en memoria',
        convDataStorageCostHint: 'Reservado al mes (pila gris)',
        convDataStartingDate: 'Fecha de inicio',
        convDataLastUsed: 'Último uso',
        settings: 'Configuración',
        // Menú superpuesto móvil (Mobile Visual #171)
        menu: 'Menú',
        close: 'Cerrar',
        logout: 'Cerrar sesión',
        noConversations: 'Aún no hay conversaciones',
        untitledChat: 'Nueva conversación',
        telegram: 'Telegram',
        telegramConnected: 'Conectado',
        telegramSearchPlaceholder: 'Buscar en todos los días…',
        telegramNoDays: 'Aún no hay días de Telegram.',
        telegramNoResults: 'Ningún día coincide con tu búsqueda.',
        telegramItems: '{n} Elementos',
        telegramUpdated: 'Actualizado {ago}',
        telegramAgoNow: 'ahora mismo',
        telegramAgoMin: 'hace {n}m',
        telegramAgoHour: 'hace {n}h',
        telegramAgoDay: 'hace {n}d',
        telegramRenameAction: 'Renombrar',
        telegramShareAction: 'Compartir conversación',
        telegramDeleteAction: 'Eliminar conversación del día',
        telegramDeletePrompt: '¿Seguro que quieres eliminar la conversación del día, la conversación de Telegram del día {date}?',
        telegramDeleteYes: 'Sí',
        telegramDeleteNo: 'No',
        telegramSelectAction: 'Seleccionar',
        telegramSelectAllAction: 'Seleccionar todo',
        telegramDeselectAllAction: 'Deseleccionar todo',
        telegramSelectionCount: '{n} seleccionados',
        telegramSelectionHint: 'Toca los días para seleccionarlos',
        telegramExitSelection: 'Listo',
        telegramBulkDeleteAction: 'Eliminar los días seleccionados',
        telegramBulkDeletePrompt: '¿Eliminar {n} conversaciones de día? Sus mensajes y notas de voz se borran definitivamente; los documentos se quedan en tu Gestor de Archivos.',
        telegramBulkDeleteYes: 'Eliminar',
        telegramBulkDeleteNo: 'Cancelar',
        telegramBulkDeleteError: 'No se pudieron eliminar algunos días. Inténtalo de nuevo.',
        telegramCardCheckboxLabel: 'Seleccionar {date}',
        telegramBack: 'Volver a la biblioteca',
        telegramSearchInDay: 'Buscar en este día…',
        telegramContinuePlaceholder: 'Continúa este día en tu Oficina Central…',
        telegramSend: 'Enviar',
        telegramToolsAccount: 'PAI Tools',
        settingsInfoAbout: 'Acerca de {name}',
        telegramAccountDesc: 'El PAI completo en Telegram — documentos, calendario, finanzas, sin límites',
        telegramConnect: 'Conectar PAI Tools',
        telegramLinkedLabel: 'Vinculado',
        telegramUnlink: 'Desvincular',
        telegramOpenHint: 'Pulsa "Start" en Telegram para terminar de vincular — esto se actualiza solo.',
        telegramConnectTitle: 'Conectar PAI Tools',
        telegramQrIntro: 'Escanea este código con tu móvil para conectar PAI Tools. Telegram se abre en tu móvil; pulsa Start y esta página se actualiza sola — no necesitas la app de escritorio.',
        telegramQrAlt: 'Escanea para conectar PAI Tools',
        telegramQrOpen: 'Abrir PAI Tools en Telegram',
        telegramConvoConnectTitle: 'Conectar PAI Conversaciones',
        telegramConvoQrIntro: 'Escanea este código con tu móvil para conectar PAI Conversaciones — la vía rápida. Telegram se abre en tu móvil; pulsa Start y esta página se actualiza sola.',
        telegramConvoQrAlt: 'Escanea para conectar PAI Conversaciones',
        telegramConvoQrOpen: 'Abrir PAI Conversaciones en Telegram',
        telegramQrLoading: 'Generando tu código…',
        telegramQrExpiresIn: 'El código caduca en {time}',
        telegramQrExpired: 'Este código ha caducado. Actualízalo para obtener uno nuevo.',
        telegramQrExpiredBadge: 'Caducado',
        telegramQrAwaiting: 'Esperando a que pulses "Start" en Telegram — esta página se actualiza sola.',
        telegramQrRefresh: 'Actualizar código',
        telegramQrRefreshing: 'Actualizando…',
        // PAI Conversations vs. PAI Tools — el sistema de doble bucket.
        telegramWorldToggleLabel: 'Elige qué mundo de Telegram quieres ver',
        telegramWorldConversations: 'PAI Conversaciones',
        telegramWorldTools: 'PAI Tools',
        telegramConvoAccount: 'PAI Conversaciones',
        telegramConvoAccountDesc: 'La vía rápida: preguntas cortas y búsqueda web, respondidas en segundos',
        telegramConvoConnect: 'Conectar PAI Conversaciones',
        telegramConvoLinkedLabel: 'Conectado',
        telegramConvoUnlink: 'Desconectar',
        telegramConvoOpenHint: 'Pulsa "Start" en Telegram para terminar de conectar — esto se actualiza solo.',
        accountMenu: 'Cuenta',
        settingsTitle: 'Configuración',
        // Settings Update (#236) — dos vistas estructurales
        userSettings: 'Ajustes de Usuario',
        systemSettings: 'Ajustes del Sistema',
        userSettingsTitle: 'Ajustes de Usuario',
        systemSettingsTitle: 'Ajustes del Sistema',
        accountSection: 'Cuenta',
        logoutDesc: 'Cierra la sesión de tu cuenta en este dispositivo.',
        appearance: 'Apariencia',
        darkMode: 'Modo Oscuro',
        darkModeDesc: 'Cambia entre tema claro y oscuro',
        language: 'Idioma',
        languageDesc: 'Elige el idioma de la interfaz',
        planSection: 'Plan y energía',
        plan_free: 'Gratis',
        plan_base: 'Sueldo de entrada',
        plan_executive: 'Sueldo de asociado',
        plan_enterprise: 'Sueldo de socio',
        plan_normal: 'Sueldo de entrada',
        plan_premium: 'Sueldo de asociado',
        plan_max: 'Sueldo de socio',
        planOf: 'de',
        planUsedThisCycle: 'usado este ciclo',
        planNoEnergy: 'Sin energía incluida — mejora tu plan para usar PAI',
        currency: 'Moneda',
        baseCurrency: 'Moneda predeterminada',
        baseCurrencyDesc: 'Moneda base para totales y nuevas entradas',
        timezone: 'Zona horaria',
        timezoneDesc: 'Zona horaria de referencia para calendario, recordatorios y fechas',
        preferenceSaved: 'Guardado',
        recentlyDeleted: 'Eliminados recientemente',
        retentionPeriod: 'Período de retención',
        retentionPeriodDesc: 'Cuánto tiempo permanecen los archivos eliminados antes de purgarse',
        restore: 'Restaurar',
        deletePermanently: 'Eliminar permanentemente',
        recentlyDeletedEmpty: 'No hay documentos eliminados recientemente',
        // Privacy — Ghost Mode (Agenda TO Contacts — Phase A, T7)
        privacy: 'Privacidad',
        ghostMode: 'Modo Fantasma',
        ghostModeDesc: 'Oculta tu cuenta de la búsqueda de contactos por correo.',
        logOut: 'Cerrar sesión',
        // Confirmation cards (ResolutionCard family — H-3.2)
        cardYes: 'Sí',
        cardNo: 'No',
        cardCancel: 'Cancelar',
        cardPreview: 'Vista previa',
        cardDownload: 'Descargar',
        cardHide: 'Ocultar',
        cardCantSaveDecision: 'No se pudo guardar tu decisión — podría no persistir tras recargar.',
        cardSuperseded: 'Sustituida por la tarjeta actualizada',
        docDeleteBusy: 'Eliminando…',
        docDeleteConfirmTitle: 'Confirmar eliminación permanente de {n} archivo{s}',
        docDeleteCannotUndo: 'Esta acción no se puede deshacer.',
        docDeleteDone: 'Se eliminaron {n} archivo{s}.',
        docDeleteCancelled: 'Eliminación cancelada — archivo{s} conservado{s}.',
        docDeleteCancelledPlural: 'Eliminación cancelada — archivos conservados.',
        expDeleteTitle: '¿Eliminar {n} transacción/es ({n})?',
        expDeleteBtn: 'Eliminar {n} elemento{s}',
        expDeleteBusy: 'Eliminando…',
        expDeleteDone: 'Se eliminaron {n} transacción/es ({n}).',
        expDeleteCancelled: 'Eliminación cancelada — transacción/es conservada/s.',
        cardCannotUndo: 'Esto no se puede deshacer.',
        calDeleteTitle: '¿Eliminar este evento?',
        calDeleteBusy: 'Eliminando…',
        calDeleteConfirmBtn: 'Confirmar eliminación',
        calDeleteRemoves: 'Esto elimina el evento de forma permanente.',
        calDeleteDone: 'Se eliminó el evento "{title}".',
        calDeleteCancelled: 'Eliminación cancelada — el evento "{title}" se conservó.',
        calBatchTitle: '¿Eliminar {n} evento{s}?',
        calBatchBtn: 'Eliminar {n} evento{s}',
        calBatchDone: 'Se eliminaron {n} evento{s}.',
        calBatchCancelled: 'Eliminación cancelada — evento{s} conservado{s}.',
        // Notas y Recordatorios (Fase 1) — panel
        notesTitle: 'Notas',
        notesColumnTitle: 'Notas',
        remindersColumnTitle: 'Recordatorios',
        notificationsColumnTitle: 'Notificaciones',
        notificationsEmpty: 'No hay notificaciones.',
        taskListTitle: 'Lista de tareas',
        taskMenuLabel: 'Menú de la lista de tareas',
        // Disposición de Notas y Lista de tareas (#488)
        splitDragHandle: 'Arrastra para reordenar',
        splitMoveUp: 'Subir {name}',
        splitMoveDown: 'Bajar {name}',
        splitMoveLeft: 'Mover {name} a la izquierda',
        splitMoveRight: 'Mover {name} a la derecha',
        splitExpand: 'Expandir {name}',
        splitCollapse: 'Contraer {name}',
        taskClearCompleted: 'Eliminar tareas completadas',
        taskDeleteAll: 'Eliminar todas las tareas',
        taskAddPlaceholder: 'Añade una tarea…',
        taskAdd: 'Añadir tarea',
        taskNewTitle: 'Nueva tarea',
        taskSearchPlaceholder: 'Buscar tareas…',
        taskNoResults: 'Ninguna tarea coincide con tu búsqueda.',
        taskEmpty: 'Aún no hay tareas. Añade una arriba o pídeselo a PAI.',
        taskConfirmDeleteAll: '¿Eliminar las {n} tareas? No se puede deshacer.',
        taskConfirmDeleteCompleted: '¿Eliminar {n} tareas completadas? No se puede deshacer.',
        taskMarkDone: 'Marcar "{label}" como hecha',
        taskMarkNotDone: 'Marcar "{label}" como no hecha',
        taskEditHint: 'Haz clic para editar',
        taskDelete: 'Eliminar tarea',
        taskSaveFailed: 'No se pudo guardar la tarea.',
        taskDeleteTitle: '¿Eliminar {n} tarea{s}?',
        taskDeleteTitleAll: '¿Eliminar toda tu lista de tareas ({n} tarea{s})?',
        taskDeleteBtn: 'Eliminar {n} tarea{s}',
        taskDeleteBusy: 'Eliminando…',
        taskDeleteDone: 'Se eliminaron {n} tarea{s}.',
        taskDeleteCancelled: 'Eliminación cancelada — tarea{s} conservada{s}.',
        cardThisTask: 'esta tarea',
        noteNew: 'Nueva nota',
        reminderNew: 'Nuevo recordatorio',
        notesSearchPlaceholder: 'Buscar notas…',
        notesEmpty: 'Aún no hay notas. Captura una idea para empezar.',
        notesNoResults: 'Ninguna nota coincide con tu búsqueda.',
        remindersEmpty: 'Aún no hay recordatorios.',
        noteUntitled: 'Nota sin título',
        notesSelect: 'Seleccionar',
        notesSelectDone: 'Cancelar',
        notesBulkDelete: 'Eliminar ({n})',
        reminderOverdue: 'Vencido',
        // #279 — menús de tres puntos de cada columna.
        notesMenuLabel: 'Menú de notas',
        remindersMenuLabel: 'Menú de recordatorios',
        remindersBack: 'Volver',
        // #284 — Recordatorios pasados (ya notificados, se guardan 7 días).
        remindersPastView: 'Recordatorios pasados',
        remindersActiveView: 'Recordatorios activos',
        remindersPastTitle: 'Recordatorios pasados',
        remindersPastEmpty: 'No hay recordatorios pasados.',
        remindersPastNote: 'Estos recordatorios se eliminarán automáticamente en 7 días.',
        reminderFired: 'Notificado',
        // Recordatorios recurrentes — campo "Repetir" del modal + la tira de la tarjeta.
        reminderRepeatLabel: 'Repetir',
        reminderRepeatNone: 'Ninguna',
        reminderRepeatDaily: 'Diariamente',
        reminderRepeatWeekly: 'Semanalmente en días concretos',
        reminderRepeatEveryNDays: 'Cada X días',
        reminderRepeatMonthly: 'Mensualmente',
        reminderRepeatDaysRequired: 'Elige al menos un día de la semana.',
        reminderRepeatIntervalRequired: 'Indica cada cuántos días se repite.',
        reminderRepeatIntervalUnit: 'días',
        reminderRecurrenceDaily: 'Diariamente',
        reminderRecurrenceEveryNDays: 'Cada {n} días',
        reminderRecurrenceMonthly: 'Mensualmente el {day}',
        reminderRecurrenceLabel: 'Se repite',
        // Recordatorios con fin (#463) — campo "Termina" del modal + lo que queda.
        reminderEndsLabel: 'Termina',
        reminderEndsNever: 'Nunca',
        reminderEndsAfterCount: 'Tras un número de veces',
        reminderEndsOnDate: 'En una fecha',
        reminderEndsCountUnit: 'veces en total',
        reminderEndsFiredSoFar: '{n} hechas',
        reminderEndsCountRequired: 'Indica cuántas veces debe sonar.',
        reminderEndsCountTooLow: 'Ya ha sonado {n} veces — indica un número mayor.',
        reminderEndsUntilRequired: 'Elige una fecha de fin igual o posterior al primer recordatorio.',
        reminderEndsUntil: 'hasta el {date}',
        reminderRemainingCount: 'quedan {n} de {total}',
        reminderRemainingLeft: 'quedan {n}',
        reminderDay0: 'Lun',
        reminderDay1: 'Mar',
        reminderDay2: 'Mié',
        reminderDay3: 'Jue',
        reminderDay4: 'Vie',
        reminderDay5: 'Sáb',
        reminderDay6: 'Dom',
        // Notas y Recordatorios — modal de edición de nota
        noteModalNewTitle: 'Nueva nota',
        noteModalEditTitle: 'Editar nota',
        noteModalViewTitle: 'Nota',
        noteEditLabel: 'Editar nota',
        noteTitlePlaceholder: 'Título (opcional)',
        noteContentPlaceholder: 'Escribe tu nota…',
        noteContentRequired: 'La nota necesita contenido.',
        noteSaveError: 'No se pudo guardar la nota.',
        // Note attached document update (#283) — adjuntos en la barra de título.
        noteAttachLabel: 'Adjuntar un archivo',
        noteAttachError: 'No se pudo adjuntar ese archivo.',
        noteAttachmentRemove: 'Quitar adjunto',
        noteAttachmentUnnamed: 'archivo',
        // Attached documents to reminders (#386).
        reminderAttachmentCount: '{n} adjuntos',
        reminderAttachmentRemove: 'Quitar el adjunto de este recordatorio',
        reminderAttachmentUnavailable: 'No disponible (en Eliminados recientemente)',
        reminderAttachmentRemoveHint: 'Quitar un adjunto nunca borra el archivo.',
        attachUploadDevice: 'Subir desde el dispositivo',
        attachFromFileManager: 'Elegir del gestor de archivos',
        // PAI Shared notes (#259) — colaboración.
        noteShareTitle: 'Compartir nota',
        noteShareCollaborators: 'Compartida con',
        noteShareEmpty: 'Aún no compartida. Solo tú puedes ver esta nota.',
        noteShareCanEdit: 'Puede ver y editar',
        noteShareRemove: 'Quitar acceso',
        noteSharePlaceholder: 'Buscar contacto o equipo...',
        noteShareNoResults: 'Ningún contacto o equipo coincide. Conecta primero en Contactos.',
        noteShareTeamLabel: 'Equipo',
        noteShareBtn: 'Compartir',
        noteShareContactNoun: 'contacto',
        noteShareTeamNoun: 'equipo',
        noteShareWarning: 'Estás a punto de compartir esta nota. Esto le dará permisos completos de edición para modificar tu nota.',
        noteShareError: 'No se pudo compartir esta nota.',
        noteUnshareError: 'No se pudo actualizar el acceso.',
        noteSharedBy: 'Compartida por {name}',
        noteSharedWith: 'Compartida con {n}',
        noteShareCardTitle: '¿Compartir "{note}" con {n} persona{s}?',
        noteShareCardBtn: 'Compartir con {n} persona{s}',
        noteShareCardBusy: 'Compartiendo...',
        noteShareCardWarning: 'Esto le dará permisos completos de edición para modificar tu nota.',
        noteShareCardDone: 'Compartida con {n} persona{s} — ya pueden editarla.',
        noteShareCardCancelled: 'Compartir cancelado — la nota sigue siendo privada.',
        // Notas y Recordatorios — modal de edición de recordatorio
        reminderModalNewTitle: 'Nuevo recordatorio',
        reminderModalEditTitle: 'Editar recordatorio',
        reminderContentPlaceholder: '¿De qué quieres que te recuerde?',
        reminderDateLabel: 'Fecha',
        reminderTimeLabel: 'Hora',
        reminderTitleRequired: 'El recordatorio necesita un título.',
        reminderWhenRequired: 'Elige una fecha y hora.',
        reminderSaveError: 'No se pudo guardar el recordatorio.',
        modalDelete: 'Eliminar',
        modalCancel: 'Cancelar',
        // Notas y Recordatorios — tarjetas de eliminación en el chat
        noteDeleteTitle: '¿Eliminar {n} nota{s}?',
        noteDeleteBtn: 'Eliminar {n} nota{s}',
        noteDeleteBusy: 'Eliminando…',
        noteDeleteDone: 'Se eliminaron {n} nota{s}.',
        noteDeleteCancelled: 'Eliminación cancelada — nota{s} conservada{s}.',
        reminderDeleteTitle: '¿Eliminar {n} recordatorio{s}?',
        reminderDeleteBtn: 'Eliminar {n} recordatorio{s}',
        reminderDeleteBusy: 'Eliminando…',
        reminderDeleteDone: 'Se eliminaron {n} recordatorio{s}.',
        reminderDeleteCancelled: 'Eliminación cancelada — recordatorio{s} conservado{s}.',

        // action revocation / undo (Double tap W/T #210)
        actionRevokeTitle: '¿Deshacer — eliminar {label}?',
        actionRevokeBody: 'Esto elimina permanentemente {label} que PAI creó antes.',
        actionRevokeDocNote: 'El documento pasa a Eliminados recientemente, así que aún puedes restaurarlo.',
        actionRevokeConfirmBtn: 'Confirmar deshacer',
        actionRevokeBusy: 'Deshaciendo…',
        actionRevokeNote: 'Solo se elimina lo que creó esta acción — nada más se toca.',
        actionRevokeDone: 'Deshecho — {label} se eliminó.',
        actionRevokeCancelled: 'Deshacer cancelado — {label} se conservó.',
        // Comment section PAI #242 — tarjeta de eliminación de comentarios en el chat
        commentDeleteTitle: '¿Eliminar {n} comentario{s}?',
        commentDeleteBtn: 'Eliminar {n} comentario{s}',
        commentDeleteBusy: 'Eliminando…',
        commentDeleteDone: 'Se eliminaron {n} comentario{s}.',
        commentDeleteCancelled: 'Eliminación cancelada — comentario{s} conservado{s}.',
        bookDeleteTitle: '¿Eliminar el libro "{name}"?',
        bookDeleteBusy: 'Eliminando…',
        bookDeleteConfirmBtn: 'Confirmar eliminación',
        bookDeleteNote: 'Esto elimina permanentemente el libro y sus datos.',
        bookDeleteTotalTickets: 'Total de tickets',
        bookDeleteItemizedLines: 'Líneas detalladas',
        bookDeleteNetBalance: 'Balance neto',
        bookDeleteLinkedFiles: 'Archivos vinculados',
        bookDeleteDone: 'Se eliminó el libro "{name}"{tx}.',
        bookDeleteDoneTx: ' y sus {n} transacción/es ({n})',
        bookDeleteCancelled: 'Eliminación cancelada — el libro "{name}" se conservó.',
        bookRenameTitle: '¿Cambiar el nombre de este libro?',
        bookRenameCurrent: 'Nombre actual',
        bookRenameNew: 'Nombre nuevo',
        bookRenameBody: 'Todas las transacciones de este libro siguen en él con el nombre nuevo.',
        bookRenameConfirmBtn: 'Confirmar',
        bookRenameBusy: 'Renombrando…',
        bookRenameDone: 'Se cambió el nombre de "{old}" a "{name}".',
        bookRenameCancelled: 'Cambio de nombre cancelado — el libro sigue llamándose "{old}".',
        ruleChangeTitleAdd: '¿Añadir la Regla {n} a tu Reglamento?',
        ruleChangeTitleUpdate: '¿Cambiar la Regla {n} de tu Reglamento?',
        ruleChangeTitleDelete: '¿Eliminar la Regla {n} de tu Reglamento?',
        ruleChangeCurrent: 'Texto actual',
        ruleChangeNew: 'Texto nuevo',
        ruleChangeConfirmBtn: 'Confirmar',
        ruleChangeBusy: 'Guardando…',
        ruleChangeDoneAdd: 'Regla {n} añadida a tu Reglamento.',
        ruleChangeDoneUpdate: 'Regla {n} actualizada.',
        ruleChangeDoneDelete: 'Regla {n} eliminada — las reglas siguientes suben un número.',
        ruleChangeCancelled: 'Cancelado — tu Reglamento no se ha cambiado.',
        ticketUpdateTitle: '{action} — ¿{n} ticket{s}?',
        ticketUpdateBtn: 'Actualizar {n} ticket{s}',
        ticketUpdateBusy: 'Actualizando…',
        ticketUpdateDone: '{action} — aplicado a {n} ticket{s}.',
        ticketUpdateCancelled: 'Actualización cancelada — ticket{s} sin cambios.',
        ticketDeleteTitle: '¿Eliminar {n} ticket{s}?',
        ticketDeleteBtn: 'Eliminar {n} ticket{s}',
        ticketDeleteBusy: 'Eliminando…',
        ticketDeleteDone: 'Se eliminaron {n} ticket{s}.',
        ticketDeleteCancelled: 'Eliminación cancelada — ticket{s} conservado{s}.',
        ticketNoDescription: 'Sin descripción.',
        boardDeleteTitle: '¿Eliminar {n} tablero{s}?',
        boardDeleteBtn: 'Eliminar {n} tablero{s}',
        boardDeleteBusy: 'Eliminando…',
        boardDeleteDone: 'Se eliminaron {n} tablero{s}.',
        boardDeleteCancelled: 'Eliminación cancelada — tablero{s} conservado{s}.',
        // Leave board (#275)
        boardLeaveTitle: '¿Salir de {name}?',
        boardLeaveQuestion: '¿Seguro que quieres salir? Esta acción no se puede deshacer.',
        boardLeaveBody: 'Perderás el acceso a este tablero — solo un propietario puede volver a invitarte.',
        boardLeaveConfirmBtn: 'Confirmar salida',
        boardLeaveBusy: 'Saliendo…',
        boardLeaveDone: 'Saliste de {name}.',
        boardLeaveCancelled: 'Salida cancelada — sigues siendo miembro de {name}.',
        boardOpen: 'Abrir tablero',
        columnMoveTitle: 'Mover columna',
        columnMoveBusy: 'Moviendo…',
        columnMoveConfirmBtn: 'Confirmar movimiento',
        columnMoveDone: 'Se movió la columna "{name}" a {target}.',
        columnMoveCancelled: 'Movimiento cancelado.',
        // eliminación de etiqueta
        labelDeleteTitle: '¿Eliminar la etiqueta "{name}"?',
        labelDeleteBoard: 'en {board}',
        labelDeleteAffected: 'Esto la quita de {n} ticket{s}.',
        labelDeleteNoTickets: 'Aún no está en ningún ticket.',
        labelDeleteMore: '…y {n} más',
        labelDeleteConfirmBtn: 'Confirmar eliminación',
        labelDeleteBusy: 'Eliminando…',
        labelDeleteNote: 'Esto quita la etiqueta de la paleta y de cada ticket etiquetado de forma permanente.',
        labelDeleteDone: 'Se eliminó la etiqueta "{name}".',
        labelDeleteCancelled: 'Eliminación cancelada — se conservó la etiqueta "{name}".',

        columnDeleteTitle: '¿Eliminar la columna "{name}"?',
        columnDeleteBoard: 'en {board}',
        columnDeleteAffected: 'Esta columna tiene {n} ticket{s} — al eliminarla se borra la columna y todos sus tickets.',
        columnDeleteEmpty: 'Esta columna está vacía — no se eliminará ningún ticket.',
        columnDeleteMore: '…y {n} más',
        columnDeleteConfirmBtn: 'Confirmar eliminación',
        columnDeleteBusy: 'Eliminando…',
        columnDeleteNote: 'Esto elimina de forma permanente la columna y todos los tickets que contiene.',
        columnDeleteDone: 'Se eliminó la columna "{name}".',
        columnDeleteCancelled: 'Eliminación cancelada — se conservó la columna "{name}".',
        ticketAttachTitle: '¿Adjuntar {n} documento{s} al ticket?',
        ticketAttachBtn: 'Subir al ticket',
        ticketAttachBusy: 'Subiendo…',
        ticketAttachDone: 'Se adjuntaron {n} documento{s} al ticket.',
        ticketAttachCancelled: 'Adjunto cancelado.',
        docRenameTitle: '¿Renombrar {n} archivo{s}?',
        docRenameBtn: 'Confirmar cambios',
        docRenameBusy: 'Renombrando…',
        docRenameDone: 'Se renombraron {n} archivo{s}.',
        docRenameCancelled: 'Renombrado cancelado.',
        docRenameInputTitle: 'Editar el nuevo nombre del archivo',
        docRenameEmptyName: 'El nombre del archivo no puede estar vacío.',
        contactConfirmTitle: '¿Crear contacto "{name}"?',
        contactConfirmBtn: 'Confirmar',
        contactConfirmBusy: 'Creando…',
        contactDeclineBtn: 'Rechazar',
        contactEditBtn: 'Editar',
        contactEditDoneBtn: 'Listo',
        contactCreatedDone: 'Contacto {name} creado.',
        contactDeclined: 'Creación de contacto rechazada.',
        contactNameRequired: 'Se requiere un nombre.',
        // Duplicate Guard (#260) — las tres opciones que ofrece el 409 del backend.
        contactDuplicateSaveAnyway: 'Guardar duplicado igualmente',
        contactDuplicateUpdate: 'Actualizar la entrada existente',
        contactDuplicateCancel: 'Cancelar',
        // ── Contacts Hub (Agenda TO Contacts — Phase B) ──────────────────
        contactsTitle: 'Contactos',
        contactsSearchPlaceholder: 'Busca nombre, equipo o correo...',
        contactsSortRecent: 'Recientes',
        contactsSortAlpha: 'A–Z',
        contactsSortTooltip: 'Cambiar orden',
        contactsPending: 'Solicitudes pendientes',
        contactsMenuBlockList: 'Lista de bloqueados',
        contactsMenuNewContact: 'Nuevo contacto',
        contactsLoadFailed: 'No se pudieron cargar los contactos',
        contactsEmptyAll: 'Aún no hay contactos — añade uno desde el menú.',
        contactsNoMatch: 'Ningún contacto coincide con tu búsqueda.',
        tierNetwork: 'Red',
        tierTeams: 'Equipos',
        tierRolodex: 'Directorio',
        tierNetworkEmpty: 'Aún no hay contactos en tu red.',
        tierTeamsEmpty: 'Aún no hay equipos.',
        tierRolodexEmpty: 'Aún no hay contactos guardados.',
        contactInviteToNetwork: 'Invitar a la Red',
        contactRequestSent: 'Solicitud enviada',
        contactFieldEmail: 'Correo',
        contactFieldPhone: 'Teléfono',
        contactFieldCompany: 'Empresa',
        contactFieldIndustry: 'Sector',
        contactFieldPosition: 'Cargo',
        contactFieldBirthday: 'Cumpleaños',
        contactEditAction: 'Editar',
        pillarLabelDocs: 'Archivos',
        pillarLabelChats: 'Conversaciones',
        pillarLabelEvents: 'Eventos',
        pillarLabelFinancials: 'Finanzas',
        pillarTotal: 'Total',
        contactEventViewTitle: 'Evento',
        contactEventUntitled: 'Evento sin título',
        contactEventEdit: 'Editar',
        contactEventYou: 'Tú',
        contactEventGuest: 'Invitado',
        contactEventStatus_confirmed: 'Confirmado',
        contactEventStatus_pending: 'Pendiente',
        contactEventStatus_declined: 'Rechazado',
        pillarDocsEmpty: 'No hay documentos compartidos.',
        pillarChatsEmpty: 'No hay conversaciones compartidas.',
        pillarEventsEmpty: 'No hay eventos compartidos.',
        pillarFinancialsEmpty: 'No hay libros compartidos.',
        ghostPaiUser: 'Usuario PAI',
        ghostSendRequest: 'Enviar solicitud de contacto',
        ghostRequestSent: 'Solicitud enviada',
        ghostCancelRequest: 'Cancelar solicitud',
        ghostPendingReceived: 'Este usuario ya TE envió una solicitud.',
        ghostAccept: 'Aceptar',
        ghostDecline: 'Rechazar',
        ghostLinked: 'Ya está en tu Red',
        ghostNotFound: 'No se encontró ningún usuario PAI.',
        ghostSelf: '¡Espera, ese eres tú! No puedes enviarte una solicitud a ti mismo.',
        discoverErrAlreadyRequested: 'Ya le enviaste una solicitud a este usuario.',
        discoverErrAlreadyConnected: 'Ya estás conectado con este usuario.',
        discoverErrTheyRequestedYou: 'Este usuario ya te envió una solicitud — revisa Solicitudes pendientes.',
        discoverErrGeneric: 'Algo salió mal. Inténtalo de nuevo.',
        pendingTitle: 'Solicitudes pendientes',
        pendingReceivedHeading: 'Recibidas',
        pendingSentHeading: 'Enviadas',
        pendingAcceptBtn: 'Aceptar',
        pendingDeclineBtn: 'Rechazar',
        pendingCancelBtn: 'Cancelar solicitud',
        pendingStatusLabel: 'Pendiente',
        pendingNoReceived: 'No hay solicitudes entrantes.',
        pendingNoSent: 'No hay solicitudes salientes.',
        blockListTitle: 'Lista de bloqueados',
        blockUnblockBtn: 'Desbloquear',
        blockAddPlaceholder: 'Bloquear un correo…',
        blockAddBtn: 'Bloquear',
        blockListEmpty: 'No hay usuarios bloqueados.',
        editContactTitle: 'Editar contacto',
        editSaveBtn: 'Guardar',
        editCancelBtn: 'Cancelar',
        editSaving: 'Guardando…',
        contactTimeline: 'Cronología',
        contactAddNotePlaceholder: 'Añade una nota rápida…',
        contactAddNoteBtn: 'Añadir',
        contactNoNotes: 'Aún no hay interacciones registradas.',
        dangerZone: 'Zona de peligro',
        uncontactBtn: 'Desconectar',
        uncontactConfirmTitle: '¿Desconectar esta conexión?',
        uncontactDisclaimer: '¿Estás seguro? Esta acción no se puede revertir. Perderás de inmediato todo acceso a los datos compartidos que no te pertenecen, y el otro usuario será eliminado de todos los datos que te pertenecen. Esta desconexión es permanente e inmediata.',
        uncontactConfirmBtn: 'Sí, desconectar',
        uncontactDone: 'Conexión desconectada.',
        blockContactBtn: 'Bloquear contacto',
        blockContactConfirmTitle: '¿Bloquear este contacto?',
        blockContactDisclaimer: 'Bloquear corta cualquier conexión activa y os oculta mutuamente en todas partes. Puedes desbloquear más tarde desde la Lista de bloqueados.',
        blockContactConfirmBtn: 'Sí, bloquear',
        deleteContactBtn: 'Eliminar contacto',
        deleteContactConfirmTitle: '¿Eliminar a {name} permanentemente? Esto también elimina la cronología de notas.',
        deleteContactConfirmBtn: 'Sí, eliminar',
        // ── LOS EQUIPOS (Agenda TO Contacts — Fase C / T5) ───────────────
        contactsMenuNewTeam: 'Crear nuevo equipo',
        teamRoleCreator: 'Creador',
        teamRoleOwner: 'Propietario',
        teamRoleMember: 'Miembro',
        teamRoleFor: 'Rol de {name}',
        teamMemberSearchPlaceholder: 'Busca en tu red por nombre o correo...',
        teamMemberSearchNoMatch: 'Ningún contacto coincide con esa búsqueda.',
        teamMemberStranger: 'No está en tu red',
        teamInviteMember: 'Invitar',
        teamKickMember: 'Quitar del equipo',
        teamAddMember: 'Añadir miembro',
        teamAddMemberEmpty: 'Todos tus contactos de la red ya son miembros.',
        teamAddMemberFailed: 'No se pudo añadir ese miembro.',
        teamAddMemberMenu: 'Añadir un contacto de la red',
        teamLoadFailed: 'No se pudo cargar el equipo.',
        teamKickFailed: 'No se pudo quitar ese miembro.',
        teamLeaveFailed: 'No se pudo salir del equipo.',
        teamDeleteFailed: 'No se pudo eliminar el equipo.',
        teamRenameFailed: 'No se pudieron guardar los cambios.',
        teamNameRequired: 'Se requiere un nombre de equipo.',
        teamNoMembers: 'Aún no hay miembros.',
        teamEditBtn: 'Editar',
        teamLeaveBtn: 'Salir del equipo',
        teamDeleteBtn: 'Eliminar equipo',
        teamDeleteConfirmTitle: '¿Eliminar "{name}"?',
        teamDeleteDisclaimer: 'Esto disuelve el equipo para todos y revoca de inmediato cada documento, conversación, libro y evento compartido. Los miembros deben descargar antes lo que necesiten. Esto no se puede deshacer.',
        teamDeleteConfirmBtn: 'Eliminar equipo',
        // Diálogo de creación de equipo
        teamCreateTitle: 'Crear un equipo',
        teamCreateConfirmTitle: 'Confirmar los miembros',
        teamFieldName: 'Nombre del equipo',
        teamFieldNamePlaceholder: 'p. ej. Escuadrón de Producto',
        teamFieldDescription: 'Descripción (opcional)',
        teamFieldMembers: 'Miembros (de tu red)',
        teamCreateNoNetwork: 'Aún no tienes contactos en tu red — conecta con alguien primero.',
        teamCreateConfirmPrompt: 'Estás creando "{name}" con {n} miembro(s). ¿Confirmas?',
        teamCreateRosterEmpty: 'Sin miembros — solo tú.',
        teamCreateBack: 'Atrás',
        teamCreateNext: 'Siguiente',
        teamCreateConfirmBtn: 'Crear equipo',
        teamCreateNotLinked: 'Estos miembros no están en tu red: {names}',
        teamCreateFailed: 'No se pudo crear el equipo.',
        // Tarjetas de chat de equipos
        teamCardCreateTitle: '¿Crear el equipo "{name}"?',
        teamCardCreateNoMembers: 'Sin miembros — solo tú.',
        teamCardCreateBtn: 'Crear equipo',
        teamCardCreateBusy: 'Creando…',
        teamCardCreateDone: 'Se creó el equipo "{name}".',
        teamCardCreateCancelled: 'Creación de equipo cancelada.',
        teamCardShareTitle: '¿Compartir con {team}?',
        teamCardShareDocBody: 'Añadiré esto a los documentos compartidos del equipo {team}. ¿Confirmas?',
        teamCardShareBookBody: 'Añadiré esto a los libros compartidos del equipo {team}. ¿Confirmas?',
        teamCardShareEventBody: 'Añadiré este evento al calendario del equipo {team}. ¿Confirmas?',
        teamCardShareAssetFallback: 'este elemento',
        teamCardShareEventFallback: 'este evento',
        teamCardShareBtn: 'Confirmar',
        teamCardShareBusy: 'Compartiendo…',
        teamCardShareDone: 'Compartido con {team}.',
        teamCardShareCancelled: 'Compartir cancelado.',
        teamCardDeleteTitle: '¿Eliminar el equipo "{name}"?',
        teamCardDeleteMembers: 'Miembros',
        teamCardDeleteShares: 'Elementos compartidos',
        teamCardDeleteBtn: 'Eliminar equipo',
        teamCardDeleteBusy: 'Eliminando…',
        teamCardDeleteDone: 'Se eliminó el equipo "{name}".',
        teamCardDeleteCancelled: 'Eliminación cancelada — el equipo se conservó.',
        // Settings — Zona de peligro (Agenda TO Contacts Phase D / T1 "Smithereens").
        // NOTE: `dangerZone` ya está definido arriba (overlay de descontactar) —
        // se reutiliza aquí, no se redefine, para evitar una clave duplicada.
        deleteAccount: 'Eliminar cuenta',
        deleteAccountDesc: 'Elimina tu cuenta y todo lo que contiene de forma permanente',
        deleteAccountModalTitle: '¿Eliminar tu cuenta?',
        deleteAccountWarnErase: 'Cada documento, conversación, contacto, tablero y libro que posees se elimina de inmediato.',
        deleteAccountWarnShared: 'Un ticket que creaste dentro del espacio compartido de otra persona permanece con ella, mostrado como creado por “Usuario eliminado”; los comentarios que escribiste allí se eliminan con tu cuenta.',
        deleteAccountWarnBooks: 'Los tableros, libros financieros, notas y equipos que creaste se destruyen para todos los que están en ellos.',
        deleteAccountWarnBilling: 'Cualquier suscripción activa se cancela de inmediato y se elimina tu método de pago guardado.',
        deleteAccountWarnFinal: 'Esto no se puede deshacer.',
        deleteAccountResidueTitle: 'Lo que permanece brevemente:',
        deleteAccountResidueBackups: 'Las copias de seguridad de recuperación se purgan en la rotación habitual; los registros de diagnóstico y seguridad se conservan brevemente y luego se eliminan automáticamente. Ninguno se usa para restaurar una cuenta.',
        deleteAccountResidueLegal: 'Se conservan los registros que la ley nos obliga a guardar: las facturas de pago que mantiene nuestro procesador de pagos por motivos fiscales y el registro del consentimiento que diste al registrarte. También pueden conservarse totales estadísticos anónimos.',
        deleteAccountConfirmLabel: 'Escribe DELETE para confirmar',
        deleteAccountConfirmBtn: 'Eliminar mi cuenta',
        deleteAccountBusy: 'Eliminando…',
        deleteAccountCancel: 'Cancelar',
        deleteAccountError: 'No se pudo eliminar tu cuenta. Inténtalo de nuevo.',
        // User Settings (#184) — perfil + salario/facturación + exportación de datos
        profileSection: 'Perfil',
        userName: 'Nombre de usuario',
        userNameDesc: 'El nombre que PAI usa para dirigirse a ti.',
        registeredEmail: 'Correo registrado',
        // Salario y pago (#312).
        salarySection: 'Salario y pago',
        salaryType: 'Tipo de salario',
        changeSalary: 'Cambiar salario',
        salaryCurrent: 'Actual',
        changeSalaryNote: 'Serás redirigido a nuestro pago seguro de Stripe. Los precios no incluyen IVA — el impuesto se añade al pagar.',
        salaryManage: 'Gestionar facturación',
        salaryRedirecting: 'Redirigiendo al pago seguro…',
        salaryError: 'No se pudo iniciar el pago — inténtalo de nuevo.',
        salaryNoBilling: 'Aún no hay cuenta de facturación — tu nivel actual fue concedido, no comprado. Elige un nivel para contratar a PAI.',
        // Selección de salario — paso 3 del alta (#374).
        salarySelectTitle: 'Elige el salario de PAI',
        salarySelectSubtitle: 'Estás contratando a PAI. Elige cuánto le pagas por trabajar contigo — puedes cambiarlo cuando quieras en Ajustes.',
        salarySelectSaving: 'Guardando…',
        salarySelectNote: 'Todos los salarios continúan en nuestro pago seguro de Stripe, donde se muestra el importe exacto. Los precios no incluyen IVA — el impuesto se añade al pagar.',
        salarySelectError: 'No se pudo guardar esa elección — inténtalo de nuevo.',
        salarySelectCancelled: 'Pago cancelado — no se ha cobrado nada. Elige un salario para terminar de configurar tu cuenta.',
        salaryAwaitingTitle: 'Confirmando tu pago…',
        salaryAwaitingBody: 'Tu cuenta se abrirá en cuanto se confirme el pago. Normalmente tarda unos segundos — puedes dejar esta página abierta.',
        salaryAwaitingSlow: 'Está tardando más de lo esperado. Tu pago está a salvo — actualiza esta página o contacta con soporte si no se confirma pronto.',
        salaryAwaitingChange: 'Elegir otro plan',
        salaryUnpaidTitle: 'Tu pago no se ha completado',
        salaryUnpaidBody: 'El pago de este salario no se terminó. Continúa al pago para abrir tu cuenta.',
        salaryUnpaidPlan: 'Salario elegido: {plan}',
        salaryUnpaidPay: 'Continuar al pago',
        salaryPageEyebrow: 'Sueldos',
        salaryPageTitle: 'Cuánto cobra tu PAI.',
        salaryPageIntro: 'Todos los sueldos incluyen todas las capacidades: nada está bloqueado. Como con cualquier sueldo, lo que cambia es cuánto trabajo puedes pedirle cada mes.',
        salaryTierTaglineBase: 'Tu día a día, fuera de tu cabeza.',
        salaryTierTaglineExecutive: 'Tu vida y tu trabajo, el mismo cerebro.',
        salaryTierTaglineEnterprise: 'Para quien se pasa el día con otras personas.',
        salaryMostPopular: 'EL MÁS ELEGIDO',
        salaryPerMonth: '/ mes',
        salaryVatForCountry: '+ IVA según tu país',
        salaryEnergy: 'Energía',
        salaryPointsPerMonth: 'puntos / mes',
        salaryPerkEveryCapability: 'Todas las capacidades, nada bloqueado',
        salaryPerkRollOver: 'La energía que no uses se acumula',
        salaryPerkTelegramWeb: 'Telegram y la web',
        salaryPerkStandardSupport: 'Soporte estándar',
        salaryPerkFasterSupport: 'Respuesta de soporte más rápida',
        salaryPerkEarlyAccess: 'Acceso anticipado a las nuevas funciones',
        salaryPerkPrioritySupport: 'Soporte prioritario',
        salaryHireCta: 'Contrata tu propio PAI',
        salaryRollOverNote: 'La energía que tu PAI no consuma durante el mes no se pierde: se acumula para el mes siguiente. Un punto es una unidad de pensamiento. Archivar un ticket cuesta unos pocos, una investigación profunda cuesta unos miles. Despide a tu PAI cuando quieras, sin preaviso, y te llevas todos tus datos.',
        billingSuccessPending: 'Pago recibido — activando tu salario…',
        billingSuccessDone: 'Tu salario está activo.',
        billingCancelled: 'Pago cancelado — sin cambios.',
        energyConsumedPct: 'Porcentaje de puntos de energía consumidos',
        paymentMethod: 'Método de pago',
        paymentMethodNone: 'No hay método de pago registrado',
        paymentMethodStripe: 'Tarjeta y facturas gestionadas de forma segura por Stripe',
        paymentHistory: 'Historial de pagos',
        verifyTitle: 'Confirma tu correo',
        verifySubtitle: 'Introduce el código de 6 dígitos que enviamos a',
        verifyConfirm: 'Confirmar correo',
        verifyResend: 'Reenviar código',
        verifyCodeSent: 'Hemos enviado un código de 6 dígitos a tu correo.',
        verifyCodeAlreadySent: 'Ya hay un código en camino — revisa tu bandeja de entrada.',
        verifyCodeIncomplete: 'Introduce el código de 6 dígitos de tu correo.',
        verifyInvalid: 'Ese código no es correcto.',
        verifyInvalidWithAttempts: 'Ese código no es correcto.',
        verifyExpired: 'Ese código ha caducado — solicita uno nuevo.',
        verifyTooManyAttempts: 'Demasiados intentos. Solicita un código nuevo.',
        verifyNoCode: 'Aún no se ha enviado ningún código — solicita uno primero.',
        verifySendFailed: 'No hemos podido enviar el correo ahora mismo. Inténtalo de nuevo en un momento.',
        verifyDone: 'Correo confirmado — abriendo PAI…',
        planFree: 'Gratis',
        save: 'Guardar',
        saved: 'Guardado',
        nameEmptyError: 'El nombre no puede estar vacío',
        nameSaveError: 'No se pudo guardar el nombre',
        exportModalTitle: 'Descarga todos tus datos',
        exportModalBody: 'Antes de eliminar tu cuenta, descarga una copia completa de tus datos. El ZIP contiene cada conversación y nota (en markdown), tus documentos e imágenes subidos, tus tableros y tus registros contables, de calendario, recordatorios y contactos.',
        exportDownloadBtn: 'Descargar todos los datos (ZIP)',
        exportBusy: 'Generando el archivo…',
        exportBusyHint: 'Recopilando tus conversaciones, documentos y registros. Puede tardar un minuto si tu cuenta es grande — mantén esta ventana abierta.',
        exportDone: 'Tus datos se han descargado.',
        exportError: 'No hemos podido crear tu archivo de datos.',
        exportRetry: 'Reintentar',
        exportContinueBtn: 'Continuar para eliminar',
        back: 'Atrás',
        edit: 'Editar',
        done: 'Hecho',
        // ShareDialog — permiso de escritura (Agenda TO Contacts Phase D / T6)
        shareCanWrite: 'Puede escribir',
        shareCanWriteHint: 'Permitir que envíe mensajes en esta conversación',
        shareReadOnly: 'Solo lectura',
        // Tarjeta de chat — envío directo PAI-a-PAI (Agenda TO Contacts Phase D / T6 + T4)
        directSendTitle: '¿Enviar a {contact}?',
        directSendDocLabel: 'Documento',
        directSendCardLabel: 'Tarjeta de contacto',
        directSendConfirmBtn: 'Confirmar envío',
        directSendBusy: 'Enviando…',
        directSendDone: 'Se envió {asset} a {contact}.',
        directSendCancelled: 'Envío cancelado — no se envió nada.',
        directSendSent: 'Entregado a su PAI',
        directSendBellOnly: 'En espera en las notificaciones de su PAI.',
        // Panel de uso (admin) — listado de usuarios paginado
        // ── Full-localization pass ("Spanish" bug card) ─────────────────────
        // 1041 keys covering the panels, menus, modals and status text that
        // #123 left hardcoded. English values here are byte-identical to the
        // t() fallback at each call site — see src/i18n/i18n.test.js.
        auxAllUsers: 'Todos los usuarios',
        auxApply: 'Aplicar',
        auxBillingCyclePT: 'Ciclo de facturación (PT)',
        auxBillingPT: 'Facturación (PT)',
        auxBillingPTTitle: 'Alinear los límites de fecha con el ciclo de facturación de Google Cloud (medianoche, hora del Pacífico)',
        auxCacheHitRate: 'Tasa de aciertos de caché',
        auxCacheHitRateTitle: 'Tasa de aciertos de la caché de prompts (en caché ÷ tokens de entrada)',
        auxIncludesDeletedAccounts: 'Incluye totales anonimizados heredados de cuentas eliminadas. Esas cifras no pueden atribuirse a ningún usuario y se excluyen de las vistas por usuario.',
        auxCacheHitSub: 'la entrada en caché se factura a 1/10',
        auxCacheSuffix: '{pct} en caché',
        auxCachedSuffix: '{n} en caché',
        auxColCachePct: '% caché',
        auxColCached: 'En caché',
        auxColCalls: 'Llamadas',
        auxColCost: 'Coste',
        auxColDate: 'Fecha',
        auxColInput: 'Entrada',
        auxColOutput: 'Salida',
        auxColTotal: 'Total',
        auxConfirmPassword: 'Confirmar contraseña',
        auxCustomDateRangeTitle: 'Rango de fechas personalizado',
        auxCustomRange: 'Personalizado',
        auxDailyUsage: 'Uso diario',
        auxModuleFinance: 'Finanzas',
        auxModuleReminders: 'Recordatorios',
        auxModuleNotes: 'Notas',
        auxModuleBoards: 'Tableros',
        auxModuleCalendar: 'Calendario',
        auxModuleFiles: 'Gestor de archivos',
        auxModuleChat: 'Mensajes',
        auxTelemetryExpenses: 'Gastos',
        auxTelemetryIncome: 'Ingresos',
        auxTelemetryTickets: 'Tarjetas',
        auxTelemetryMainChat: 'Chat principal',
        auxTelemetryTelegram: 'Telegram',
        auxTelemetryUnavailable: 'La telemetría de módulos aún no está disponible.',
        auxTelemetryPrivacyNote: 'Solo cifras agregadas: ningún número lleva identidad ni contenido.',
        auxTelemetryEnergy: 'Puntos de Energía consumidos',
        auxTelemetryEnergyTotal: 'Total',
        auxTelemetryEnergyAxis: 'Puntos',
        auxTelemetryEnergyHint: 'Pasa el cursor o toca un día para ver los pilares.',
        auxTelemetryEnergyDayAria: '{date}: {total} Puntos de Energía',
        auxTelemetryLeaderboard: 'Ranking de módulos',
        auxTelemetryNoData: 'Aún no hay datos para este periodo.',
        auxTelemetryDailyTokens: 'Tokens diarios (entrada / salida)',
        auxTelemetryModules: 'Módulos',
        auxTelemetryModule: 'Módulo',
        auxTelemetryCreated: 'Creados en el periodo',
        auxTelemetryAvgPerUser: 'Media por usuario',
        auxTelemetryBreakdown: 'Desglose',
        auxTelemetryTicketDensity: 'Tarjetas por tablero',
        auxTelemetryChannels: 'Canales',
        auxTelemetryChannel: 'Canal',
        auxTelemetryMessages: 'Mensajes',
        auxTelemetryLinked: 'Cuentas vinculadas',
        auxTelemetryStorage: 'Almacenamiento',
        auxTelemetryStorageTotal: 'Total',
        auxTelemetryGrowth: 'Crecimiento en el periodo',
        auxTelemetryTokenInput: 'Entrada',
        auxTelemetryTokenOutput: 'Salida',
        auxTelemetryTokenCached: 'En caché',
        auxTelemetryTokenTotalOf: 'Total {series}',
        auxTelemetryTokenHint: 'Pasa el cursor o toca un día para ver las cifras exactas.',
        auxTelemetryTokenAxis: 'Tokens',
        auxTelemetryTokenDayAria: '{date}: {input} de entrada, {output} de salida, {cached} tokens en caché',
        auxRefresh: 'Actualizar',
        auxDashboardTitle: 'Panel de uso',
        auxDateRangeTo: '{start} a {end}',
        auxDaysSub: '{n} días',
        auxEmailAddress: 'Dirección de correo',
        auxEstCost: 'Coste est.',
        auxFailedAdjustEnergy: 'No se pudo ajustar la energía',
        auxForgotPasswordBackToLogin: 'Volver a iniciar sesión',
        auxForgotPasswordConfirmation: 'Si existe una cuenta con este correo, se ha enviado un enlace para restablecer la contraseña.',
        auxForgotPasswordSpamHint: 'Si no ves el correo en tu bandeja de entrada en unos minutos, revisa tu carpeta de spam.',
        auxForgotPasswordInvalidEmail: 'Introduce una dirección de correo válida.',
        auxForgotPasswordLink: '¿Olvidaste tu contraseña?',
        auxForgotPasswordNetworkError: 'Algo salió mal. Comprueba tu conexión e inténtalo de nuevo.',
        auxForgotPasswordSending: 'Enviando...',
        auxForgotPasswordSubmit: 'Enviar enlace de restablecimiento',
        auxForgotPasswordSubtitle: 'Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.',
        auxForgotPasswordTitle: 'Restablecer tu contraseña',
        auxFrom: 'Desde',
        auxGoogleSearchGrounding: 'Contraste con Google Search',
        auxGroundingActuallyUsed: 'Usado realmente',
        auxGroundingEnabled: 'Activado',
        auxGroundingSearchQueries: 'Consultas de búsqueda',
        auxInOutCalls: '{in} entrada / {out} salida ({n} llamadas)',
        auxInputTokens: 'Tokens de entrada',
        auxLast24Hours: 'Últimas 24 horas',
        auxLastNDays: 'Últimos {n} días',
        auxLogIn: 'Iniciar sesión',
        auxLoginContinueGoogle: 'Continuar con Google',
        auxLoginGoogleFailed: 'No se pudo iniciar sesión con Google.',
        auxLoginInvalidCreds: 'Correo o contraseña incorrectos.',
        auxLoginNoAccount: '¿No tienes una cuenta?',
        auxLoginOrEmail: 'O continúa con tu correo',
        auxLoginSigningIn: 'Iniciando sesión...',
        auxLoginSubmit: 'Iniciar sesión con correo',
        auxLoginTitle: 'Iniciar sesión',
        auxOutputTokens: 'Tokens de salida',
        auxShowingDataFor: 'Mostrando datos de:',
        auxSignUp: 'Crear cuenta',
        auxSignupCreatingAccount: 'Creando cuenta...',
        auxSignupFailed: 'No se pudo crear la cuenta. Inténtalo de nuevo.',
        auxSignupGoogleFailed: 'No se pudo crear la cuenta con Google.',
        auxSignupHaveAccount: '¿Ya tienes una cuenta?',
        auxSignupOrEmail: 'O crea tu cuenta con correo',
        auxSignupPasswordMismatch: 'Las contraseñas no coinciden',
        auxSignupPasswordTooShort: 'La contraseña debe tener al menos 6 caracteres',
        auxSignupSigningUp: 'Creando cuenta...',
        auxSignupSubmit: 'Crear cuenta con correo',
        auxTabOverview: 'Resumen',
        auxTo: 'a',
        auxTokenUsageByStep: 'Uso de tokens por paso del pipeline',
        auxTotalCalls: 'Llamadas totales',
        auxUnexpectedError: 'Se produjo un error inesperado. Inténtalo de nuevo.',
        auxUsername: 'Nombre de usuario',
        brdAddColumn: 'Añadir columna',
        brdAddDescriptionPlaceholder: 'Añade una descripción...',
        brdAddTicket: 'Añadir ticket',
        brdAssignWord: 'Asignar',
        brdAssigneesHeader: 'Asignados',
        brdAttachFile: 'Adjuntar archivo',
        brdAttachingEllipsis: 'Adjuntando…',
        brdAttachmentsHeader: 'Archivos adjuntos',
        brdBackToBoards: 'Volver a los tableros',
        brdBoardFallback: 'Tablero',
        brdBoardLabel: 'Tablero',
        brdBoardMembers: 'Miembros del tablero',
        brdBoardNamePlaceholder: 'p. ej. Mudanza al apartamento, hoja de ruta T3',
        brdBoardNotFound: 'Tablero no encontrado.',
        brdBoardSettings: 'Configuración del tablero',
        brdClickToRename: 'Haz clic para renombrar',
        brdClosePanelEsc: 'Cerrar panel (Esc)',
        brdCollapseSplitView: 'Contraer a vista dividida',
        brdColorLabel: 'color',
        brdColumnLabel: 'Columna',
        brdColumnMenu: 'Menú de columna',
        brdColumnNamePlaceholder: 'Nombre de la columna...',
        brdColumnsCount: '{n} columnas',
        brdCommentsHeader: 'Comentarios',
        brdCompletedClickUndo: 'Completado — haz clic para marcar como no hecho',
        brdConfirmDeleteColumn: '¿Eliminar esta columna y todos sus tickets?',
        brdConfirmDeleteLabel: '¿Eliminar esta etiqueta? Se quitará de todos los tickets que la usan.',
        brdConfirmDeleteTicket: '¿Eliminar este ticket?',
        brdCreateAndAdd: 'Crear y añadir',
        brdCreateBoard: 'Crear tablero',
        brdCreateNewLabel: 'Crear nueva etiqueta',
        brdCreatingBoard: 'Creando...',
        brdCreatorCantLeave: 'Como creador del tablero no puedes salir — elimina el tablero en su lugar.',
        brdCurrentSuffix: ' (actual)',
        brdDeleteBoardMenuItem: 'Eliminar tablero',
        brdDeleteBoardQuestion: '¿Eliminar tablero?',
        brdDeleteBoardWarning: '{name} y todos sus elementos —columnas, tickets y comentarios— se eliminarán de forma permanente. Esta acción no se puede deshacer.',
        brdDeleteColumnMenuItem: 'Eliminar columna',
        brdDeleteComment: 'Eliminar comentario',
        brdDeleteLabelAction: 'Eliminar etiqueta',
        brdDeleteTicket: 'Eliminar ticket',
        brdDescPlaceholder: 'Añade una descripción... (usa la barra de herramientas — no es necesario escribir markdown)',
        brdDestinationBoard: 'Tablero de destino',
        brdEditComment: 'Editar comentario',
        brdEditCommentPlaceholder: 'Editar comentario...',
        brdEditLabel: 'Editar etiqueta',
        brdExpandCoverChat: 'Expandir para cubrir el chat',
        brdFailedAddColumn: 'No se pudo añadir la columna',
        brdFailedAddComment: 'No se pudo añadir el comentario',
        brdFailedAddTicket: 'No se pudo añadir el ticket',
        brdFailedAttachFile: 'No se pudo adjuntar el archivo',
        brdFailedDeleteBoardAlert: 'No se pudo eliminar el tablero',
        brdFailedDeleteColumn: 'No se pudo eliminar la columna',
        brdFailedDeleteComment: 'No se pudo eliminar el comentario',
        brdFailedDeleteLabel: 'No se pudo eliminar la etiqueta',
        brdFailedDeleteTicketAlert: 'No se pudo eliminar el ticket',
        brdFailedEditComment: 'No se pudo editar el comentario',
        brdFailedLeaveBoard: 'No se pudo salir del tablero.',
        brdFailedLoadBoard: 'No se pudo cargar el tablero.',
        brdFailedLoadBoards: 'No se pudieron cargar los tableros.',
        brdFailedLoadColumns: 'No se pudieron cargar las columnas.',
        brdFailedMoveColumn: 'No se pudo mover la columna.',
        brdFailedMoveTicket: 'No se pudo mover el ticket',
        brdFailedMoveTicketMsg: 'No se pudo mover el ticket.',
        brdFailedRename: 'No se pudo renombrar',
        brdFailedRenameBoard: 'No se pudo renombrar el tablero',
        brdFailedReorder: 'No se pudo reordenar',
        brdFailedReorderColumns: 'No se pudieron reordenar las columnas',
        brdFailedSaveLabel: 'No se pudo guardar la etiqueta',
        brdFailedUpdateLabels: 'No se pudieron actualizar las etiquetas',
        brdLabelNamePlaceholder: 'Nombre de la etiqueta (p. ej. Fase 1)',
        brdLabelWord: 'etiqueta',
        brdLabelsHeader: 'Etiquetas',
        brdLeaveBoardMenuItem: 'Salir del tablero',
        brdLeaveBoardQuestion: '¿Salir del tablero?',
        brdLeaveBoardWarning: '¿Seguro que quieres salir de {name}? Perderás el acceso a este tablero y esta acción no se puede deshacer — solo un propietario del tablero puede volver a invitarte.',
        brdLeavingEllipsis: 'Saliendo...',
        brdLoading: 'Cargando...',
        brdLoadingBoard: 'Cargando tablero...',
        brdLoadingEllipsis: 'Cargando…',
        brdManageMembers: 'Gestionar miembros',
        brdMarkAsCompleted: 'Marcar como completado',
        brdMarkCompleted: 'Marcar ticket como completado',
        brdMarkNotCompleted: 'Marcar ticket como no completado',
        brdMove: 'Mover',
        brdMoveColumnDesc: 'Mover "{name}" y todos sus tickets a otro tablero.',
        brdMoveTicket: 'Mover ticket',
        brdMovingTicketDesc: 'Moviendo "{title}"',
        brdNewBoard: 'Nuevo tablero',
        brdNoBoardMembers: 'No hay miembros en el tablero.',
        brdNoColumns: 'Sin columnas',
        brdNoLabelsYet: 'Aún no hay etiquetas — crea una para empezar a etiquetar tickets.',
        brdPosition: 'Posición',
        brdRemoveAttachment: 'Quitar archivo adjunto',
        brdRenameBoard: 'Renombrar tablero',
        brdSearchInBoard: 'Buscar en el tablero',
        brdSelectABoard: 'Selecciona un tablero…',
        brdSelectFromFileManager: 'Seleccionar desde el Gestor de archivos',
        brdSend: 'Enviar',
        brdSendComment: 'Enviar comentario',
        brdSendingEllipsis: 'enviando...',
        brdSomeoneFallback: 'Alguien',
        brdTicketDescriptionAria: 'Descripción del ticket',
        brdTicketOptions: 'Opciones del ticket',
        brdTicketTitlePlaceholder: 'Título del ticket...',
        brdTicketViewLabel: 'Vista del ticket',
        brdTicketCreatedBy: 'Creado por',
        brdTicketAuthorDeleted: 'Usuario eliminado',
        brdTicketsCount: '{n} tickets',
        brdUnassignWord: 'Quitar',
        brdUploadFromDevice: 'Subir desde el dispositivo',
        brdWriteCommentAria: 'Escribe un comentario',
        brdWriteCommentPlaceholder: 'Escribe un comentario... (Enter para enviar)',
        caleTimeTbc: 'Hora por confirmar',
        // #532 Eventos repetidos.
        caleSeriesAnd: 'y',
        caleSeriesDaily: 'cada día',
        caleSeriesWeekly: 'cada {days}',
        caleSeriesEveryN: 'cada {n} días',
        caleSeriesMonthly: 'cada mes el día {n}',
        caleSeriesAt: ' a las {time}',
        caleSeriesSummary: '{pattern}, del {first} al {last} ({count})',
        caleSeriesCount: '{n} eventos',
        caleSeriesCountOne: '{n} evento',
        // #533 — paquete de más de 5 eventos distintos.
        calePkgTitle: 'Eventos de calendario',
        calePkgCount: '{n} eventos',
        calePkgExplain: 'Pulsa Confirmar para crearlos todos, o dime qué números quieres cambiar o quitar y te enseño la lista actualizada.',
        calePkgUpdated: 'Lista actualizada',
        calePkgUpdatedNote: 'Todavía no se ha creado nada.',
        calePkgNeedsInfo: 'Falta información: {what}',
        calePkgClash: 'Coincide con {title}',
        calePkgError: 'No se puede crear: {reason}',
        calePkgSkipped: 'Omitido',
        calePkgCreated: 'Creado',
        calePkgChanged: 'Cambiado',
        calePkgWith: 'con {names}',
        calePkgNotInvited: 'Sin invitar: {names} (no está en tu red)',
        calePkgInvited: 'Invitaciones enviadas: {people}.',
        calePkgOneEvent: '1 evento',
        calePkgPending: 'Pendiente',
        calePkgConfirmed: 'Confirmado',
        calePkgCancelled: 'Cancelado',
        calePkgResolvedConfirmed: 'Creados {created} de {total} eventos.',
        calePkgResolvedCancelled: 'Cancelado — no se ha creado ninguno de estos eventos.',
        calePkgError409: 'Este paquete ya se ha gestionado.',
        caleSeriesBadge: 'Se repite',
        calDeleteSeriesRemoves: 'Esto elimina de forma permanente {n} eventos de este evento repetido.',
        caleUntitledEvent: 'Evento sin título',
        caleBookAnyway: 'Reservar de todos modos',
        caleEventTitlePlaceholder: 'Título del evento',
        caleLocationOptionalPlaceholder: 'Ubicación (opcional)',
        caleRemoveGuest: 'Eliminar a {name}',
        caleLoadingYourNetwork: 'Cargando tu red…',
        caleEditEventTooltip: 'Editar evento',
        caleStatusCancelled: 'Cancelado',
        caleErrUpdateEvent: 'No se pudo actualizar el evento',
        caleEventSavedToast: 'Evento guardado',
        caleSchedulingConflict: 'Conflicto de horario',
        caleConflictOverlapPeriod: 'Esto se superpone con un evento que ya has confirmado.',
        caleAttendancePendingFrom: 'Pendiente de confirmación de {name}',
        caleAttendanceConfirmedName: '{name} confirmó',
        caleAttendanceDeclinedName: '{name} rechazó',
        caleGuestListAria: 'Lista de invitados — {confirmed} de {total} confirmados',
        caleShowGuestList: 'Mostrar todos los invitados y su estado',
        caleConfirmedOfTotal: '{confirmed} de {total} confirmados',
        caleViaTeam: 'vía {team}',
        caleGuestsPendingConfirmation: 'Pendiente de confirmación',
        caleGuestsHeading: 'Invitados',
        caleNotesHeading: 'Notas',
        caleNoNotesForEvent: 'No hay notas para este evento.',
        caleAttendeesHeading: 'Asistentes',
        caleNoOneInvitedYet: 'Nadie invitado todavía.',
        caleInviteBtn: 'Invitar',
        caleInviteContactOrTeam: 'Invitar a un contacto o equipo',
        caleSearchNameEmailTeam: 'Buscar nombre, correo o equipo…',
        caleCouldntInviteName: 'No se pudo invitar a {name}.',
        caleInvitedName: 'Se invitó a {name}.',
        caleAlreadyOnGuestList: '{name} ya está en la lista de invitados.',
        caleRemovedFromGuestList: 'Se eliminó a {name} de la lista de invitados.',
        caleCouldntRemoveGuest: 'No se pudo eliminar a {name}.',
        caleFilePreview: 'Vista previa',
        caleFileDownload: 'Descargar',
        caleFileRename: 'Renombrar',
        caleDayPrev: 'Día anterior',
        caleDayNext: 'Día siguiente',
        caleCloseBackToMonth: 'Cerrar (volver al mes)',
        caleNoTransactionsLogged: 'Sin transacciones registradas',
        caleDayTxnSummary: '{n} transacción{s} · neto {sign}{amount}',
        caleNoPaiActivity: 'Sin actividad de PAI',
        caleDayDocsSummary: '{n} documento{s} · {duration} de uso',
        caleWithTime: 'Con hora',
        caleNoEventsToday: 'Aún no hay eventos este día.',
        caleAddEventBtn: 'Añadir evento',
        caleTimeFieldPlaceholder: 'p. ej. 9:30 o 22',
        caleAmPmAria: '{label} AM o PM',
        caleEventNameRequired: 'El nombre del evento es obligatorio.',
        caleStartTimeInvalid: 'Hora de inicio no reconocida — prueba 9:30, 930 o 22.',
        caleEndTimeInvalid: 'Hora de fin no reconocida — prueba 10:30, 1030 o 23.',
        caleEventNameLabel: 'Nombre del evento *',
        caleStartTimeLabel: 'Hora de inicio (opcional)',
        caleEndTimeLabel: 'Hora de fin (opcional)',
        caleLocationPlaceholder: 'p. ej. Café Luna, Zoom, la oficina',
        calePeopleLabel: 'Personas',
        caleInvitePlaceholder: 'Invitar a un contacto o equipo…',
        caleNotesPlaceholder: 'De qué se trata',
        caleNoTimeSetWarning: 'No se estableció una hora — este evento quedará en “Hora por confirmar” para el día.',
        caleSaveAndInvite: 'Guardar e invitar',
        caleInviteToEvent: 'Invitar a “{title}”',
        caleEventWord: 'evento',
        caleSearchYourNetwork: 'Buscar en tu red…',
        caleNetworkEmptyLong: 'Tu Red está vacía. Conecta con alguien primero — abre Agenda → añádelo por correo → una vez acepte la solicitud aparecerá aquí para invitarlo.',
        caleNoNetworkMatch: 'Nadie en tu Red coincide con “{query}”.',
        caleInvitedBadge: 'Invitado',
        caleSendNInvites: 'Enviar {n} invitaciones',
        caleSendInvite: 'Enviar invitación',
        caleSending: 'Enviando…',
        caleCouldntInviteList: 'No se pudo invitar a: {names}',
        caleInvitationsSentTo: 'Invitación{s} enviada{s} a {n} {person}.',
        calePeoplePlural: 'personas',
        calePersonSingular: 'persona',
        caleSelectedCount: '{n} seleccionados',
        caleTimeTbcEditHint: '{label} — establece una hora mediante Editar.',
        caleInviteContact: 'Invitar contacto',
        caleDeleteEvent: 'Eliminar evento',
        caleUnknownMerchant: 'Desconocido',
        caleNothingInColumn: 'Nada en esta columna.',
        caleNoTransactionsLoggedDay: 'Sin transacciones registradas este día.',
        caleMultipleBooksPickOne: 'Varios libros tienen actividad hoy — elige uno',
        caleNetAmount: 'neto {sign}{amount}',
        caleAllBooks: 'Todos los libros',
        caleDailyNetResult: 'Resultado neto diario',
        caleExpensesCount: 'Gastos ({n})',
        caleIncomeCount: 'Ingresos ({n})',
        caleOpenBookInFinance: 'Abrir {name} en el módulo Finanzas →',
        caleUnassignedBook: 'Sin asignar',
        caleUsageTime: 'Tiempo de uso',
        caleDocumentsTouched: 'Documentos tocados · {n}',
        caleNoDocsTouched: 'No se crearon ni editaron documentos este día.',
        caleNetLabel: 'Neto {sign}{amount}',
        caleAddEventTooltip: 'Añadir evento',
        caleAddEventBtnCaps: 'Añadir evento',
        caleEventsForDay: 'Eventos — {date}',
        caleNewBtn: 'Nuevo',
        caleEventFallbackCaps: 'Evento',
        caleEditingTitle: 'Editando — {title}',
        caleNewEventTitle: 'Nuevo evento',
        caleFinanceForDay: 'Finanzas — {date}',
        calePaiForDay: 'PAI — {date}',
        caleErrLoadActivity: 'No se pudo cargar la actividad',
        caleErrSaveEvent: 'No se pudo guardar el evento',
        caleErrCreateEvent: 'No se pudo crear el evento',
        caleErrDeleteEvent: 'No se pudo eliminar el evento',
        caleErrAnswerInvitation: 'No se pudo responder a la invitación',
        caleErrDownloadFailed: 'Error al descargar',
        caleErrRenameFailed: 'Error al renombrar',
        caleErrLoadContacts: 'No se pudieron cargar los contactos',
        caleCouldntInviteNofM: 'No se pudo invitar a {failed} de {total} {person}.',
        caleConfirmDeleteEvent: '¿Seguro que quieres eliminar este evento?',
        caleLoadingEllipsis: 'Cargando...',
        caleTransactionsMarker: 'Transacciones (ingreso / gasto)',
        caleEventsWord: 'Eventos',
        caleDocumentActivity: 'Actividad de documentos',
        caleTransactionsLegend: 'Transacciones',
        caleUsageLegend: 'Uso',
        caleEventInvitations: 'Invitaciones a eventos',
        caleInvitedByName: 'Invitado por {name}',
        caleUpcomingEvents: 'Próximos eventos',
        caleNothingScheduled: 'No hay nada programado para hoy o mañana.',
        caleRemoveEventTooltip: 'Eliminar evento',
        caleCalendarEvent: 'Evento de calendario',
        caleStartLabel: 'Inicio',
        caleEndLabel: 'Fin',
        caleConflictOverlapColon: 'Esto se superpone con un evento que ya has confirmado:',
        caleSaveConfirm: 'Guardar y confirmar',
        caleNotesOptionalPlaceholder: 'Notas (opcional)',
        caleTeamMemberCount: 'Equipo · {n} miembro{s}',
        caleTeamWord: 'Equipo',
        caleContactWord: 'Contacto',
        calePeoplePickerPlaceholder: 'Buscar contactos o equipos…',
        calePickerEmptyNoContacts: 'Tu Red no tiene contactos ni equipos todavía — conecta con alguien en Contactos primero.',
        calePickerNoMatch: 'Ningún contacto o equipo coincide con “{query}”.',
        finBookWord: 'Libro',
        finBookLowercase: 'libro',
        finBookNamePlaceholder: 'Nombre del libro',
        finUnassigned: 'Sin asignar',
        finUnassignedOnly: 'Solo sin asignar',
        finExpenseWord: 'Gasto',
        finExpensesPlural: 'Gastos',
        finIncomeWord: 'Ingreso',
        finIncomePlural: 'Ingresos',
        finTotalWord: 'Total',
        finTotalLowercase: 'total',
        finThisTransaction: 'esta transacción',
        finThisReceipt: 'este recibo',
        finLoadingEllipsis: 'Cargando…',
        finSavingEllipsis: 'Guardando…',
        finMerchantPlaceholder: 'Comercio',
        finTicketImage: 'Imagen del ticket',
        finTicketImages: 'Imágenes de tickets',
        finTicketImageTitle: 'Imagen del ticket',
        finNoTicketImage: 'Sin imagen ni documento del ticket.',
        finReceiptLoadFailed: 'No se pudo cargar este documento.',
        finPdfPageFailed: 'No se pudo mostrar esta página del PDF.',
        finOpenFullDocument: 'Abrir documento completo',
        finCloseEscTitle: 'Cerrar (Esc)',
        finUnknownMerchant: 'Comercio desconocido',
        finFixedIncome: 'Ingreso fijo',
        finFixedExpense: 'Gasto fijo',
        finFixedKind: '{kind} fijo',
        finManagingRuleScope: 'Estás gestionando la regla recurrente de esta entrada — los cambios se aplican a toda la serie.',
        finMakeRepeat: 'Haz que “{merchant}” se repita automáticamente — cada ocurrencia se registra por ti.',
        finStartingDateLabel: 'Fecha de inicio del ticket recurrente',
        finFrequencyLabel: 'Frecuencia',
        finSelectFrequency: 'Selecciona una frecuencia…',
        finRecurrenceNote: '* Si estas opciones no coinciden con tu periodicidad, pídele a PAI en el chat principal o por Telegram que configure tu calendario específico (p. ej., “Haz que esto se repita cada 27 días”).',
        finStopRecurring: 'Detener recurrencia',
        finPickFrequency: 'Elige una frecuencia.',
        finFailedToSave: 'Error al guardar.',
        finSubstitutionQuestion: 'Estás cambiando una regla recurrente existente. ¿Qué debe pasar con las entradas ya generadas por el calendario anterior?',
        finConfirmReplace: 'Confirmar y reemplazar',
        finConfirmReplaceDesc: 'Elimina las entradas generadas anteriormente y aplica el nuevo calendario.',
        finKeepBoth: 'Mantener ambos',
        finKeepBothDesc: 'Conserva las entradas generadas anteriormente y aplica también el nuevo calendario.',
        finRetroConfirm: 'He detectado una fecha de inicio en el pasado. Voy a generar {entryWord} pendiente(s) ({n}) desde {month} hasta hoy. ¿Confirmas esta acción?',
        finEntrySingular: 'entrada',
        finEntryPlural: 'entradas',
        finRuleStoppedQuestion: 'Regla detenida. ¿Quieres eliminar todos los duplicados generados del libro, o conservarlos como entradas individuales?',
        finCheckingEntries: 'Comprobando entradas generadas…',
        finEliminateWillPart1: 'Esto eliminará',
        finEliminateWillPart2: '{entryWord} generado(s) en todo el historial de la regla.',
        finKeepingOriginalTicket: 'Conservando tu ticket original:',
        finEliminateGeneratedEntries: 'Eliminar entradas generadas',
        finKeepIndividualEntries: 'Mantenerlas como entradas individuales',
        finDeleteRecurringTransaction: 'Eliminar transacción recurrente',
        finFailedToSaveChanges: 'Error al guardar los cambios',
        finAmountMustBeNumber: 'El importe debe ser un número.',
        finDateRequired: 'La fecha es obligatoria: toda transacción necesita una fecha.',
        finEditingExpense: 'Editando gasto',
        finSaveEnterTitle: 'Guardar (Intro)',
        finRecurringPrefix: 'Recurrente',
        finAutoGeneratedTitle: 'Generado automáticamente a partir de una transacción recurrente',
        finNeedsClarification: 'Necesita aclaración',
        finViewOnly: 'solo lectura',
        finViewOnlyTitle: 'Creado por otro miembro — solo los propietarios o el creador original pueden modificar esto',
        finMoreTitle: 'Más',
        finEditDetails: 'Editar detalles',
        finRecurrenceRule: 'Regla de recurrencia',
        finMoveKind: 'Mover {kind}',
        finNoOtherBooksYet: 'Todavía no hay otros libros.',
        finAmountMustBeGreater: 'El importe debe ser mayor que cero.',
        finSourcePayerRequired: 'El origen / pagador es obligatorio.',
        finMerchantRequired: 'El comercio es obligatorio.',
        finSaveFailed: 'Error al guardar',
        finNewTransaction: 'Nueva transacción',
        finSourcePayerLabel: 'Origen / pagador',
        finCategoryPlaceholderIncome: 'Salario, Reembolso…',
        finCategoryPlaceholderExpense: 'Comidas, Viajes…',
        finDescriptionOptional: 'Descripción (opcional)',
        finRecurring: 'Recurrente',
        finOneTimeDefault: 'Única vez (predeterminado)',
        finPressEnterPrefix: 'Pulsa',
        finEnterKey: 'Intro',
        finPressEnterSuffix: 'para guardar.',
        finUploadedLabel: 'Subido:',
        finCurrencyConversion: 'Conversión de moneda',
        finOriginalAmount: 'Importe original',
        finConvertedCurrency: 'Convertido ({currency})',
        finExchangeRateOn: 'Tipo de cambio del {date}: 1 {orig} = {rate} {target}',
        finRateSourceEcb: 'Fuente: Banco Central Europeo',
        finRateSourceBlend: 'Fuente: Frankfurter, una media de tipos de bancos centrales (aproximada)',
        finDigitalReceipt: 'Recibo digital',
        finNoItemization: 'No se capturó ningún desglose de este recibo.',
        finUnclassifiedItems: 'Artículos sin clasificar',
        finReconcilesTotal: '(concilia el total)',
        finSwapDirectionTitle: 'Cambiar dirección',
        finMarkAsKind: 'Marcar como {kind}',
        finReceiptAlt: 'Recibo',
        finNewBookTitle: 'Nuevo libro',
        finNewBookDesc: 'Un libro es un registro para agrupar transacciones — p. ej. "Operaciones PAI", "Personal", "Viajes".',
        finFailedToCreateBook: 'Error al crear el libro',
        finCreate: 'Crear',
        finCreatingEllipsis: 'Creando…',
        finRenameBookTitle: 'Renombrar libro',
        finFailedToRenameBook: 'Error al renombrar el libro',
        finFailedToEliminateBook: 'Error al eliminar el libro',
        finEliminateBookTitle: '¿Eliminar “{bookName}”?',
        finEliminateBookConfirm: '¿Seguro que quieres eliminar este libro y todas sus transacciones? Esta acción no se puede deshacer.',
        finEliminate: 'Eliminar',
        finEliminatingEllipsis: 'Eliminando…',
        finAdvancedExportTitle: 'Exportación financiera avanzada',
        finExportDescPart1: 'Descarga un ZIP con',
        finExportDescPart2: '(hoja de Resumen y Libro Mayor, Desglose por artículos opcional + Resumen de artículos auto-agregado) y una',
        finExportDescPart3: 'carpeta que contiene cada imagen de recibo. Tablas de Excel habilitadas, columnas ajustadas automáticamente, positivos en verde y negativos en rojo.',
        finAllBooks: 'Todos los libros',
        finStartDate: 'Fecha de inicio',
        finEndDate: 'Fecha de fin',
        finFullBookLabel: 'Libro completo — exportar todo (ignorar el rango de fechas)',
        finDisplayCurrency: 'Moneda de visualización',
        finExportCurrencyNote: 'Todos los totales y la columna de importe del Libro Mayor se normalizan a {currency}. Los importes originales se conservan en cada fila.',
        finIncludeItemizedBreakdown: 'Incluir desglose por artículos',
        finItemizedBreakdownNote: '(añade la Hoja 2 con cada producto + la Hoja 3 con una tabla dinámica agrupada por nombre de artículo)',
        finTicketImagesNote: '(incluye la carpeta receipts/ — desmárcalo para un ZIP mucho más pequeño, solo con datos)',
        finLeaveDatesEmpty: 'Deja las fechas vacías para incluirlo todo.',
        finExportFailedHttp: 'Error al exportar (HTTP {status})',
        finExportFailed: 'Error al exportar',
        finBuildingEllipsis: 'Generando…',
        finDownloadZip: 'Descargar ZIP',
        finMovedToBook: 'Se movieron {count} a {book}',
        finCountSelected: '{count} seleccionado(s)',
        finMoveToEllipsis: 'Mover a…',
        finFailedToLoadBooks: 'Error al cargar los libros',
        finFailedToLoadExpenses: 'Error al cargar los gastos',
        finConfirmDeleteTransactions: '¿Eliminar {n} transacción/es? Esta acción no se puede deshacer.',
        finDeletedSkipped: 'Se eliminaron {applied}; se omitieron {skipped} (permisos o no encontrados).',
        finBulkDeleteFailed: 'Error al eliminar en lote',
        finMovedSkipped: 'Se movieron {applied}; se omitieron {skipped} (permisos o no encontrados).',
        finBulkMoveFailed: 'Error al mover en lote',
        finFailedToMoveExpense: 'Error al mover el gasto',
        finDeleteExpenseFor: '¿Eliminar el gasto de {merchant}?',
        finAttachedFileQuestion: '¿Quieres eliminar también el archivo adjunto o prefieres conservarlo en el Gestor de Archivos?',
        finAttachedFileDeleteToo: 'Eliminar también el archivo adjunto',
        finAttachedFileKeptNote: 'Déjalo sin marcar para conservarlo en el Gestor de Archivos.',
        finBookFilesQuestion: 'Las transacciones de este libro tienen {n} archivo{s} vinculado{s} (imágenes o PDF) en el Gestor de Archivos.',
        finBookFilesDeleteToo: 'Eliminar también {n} archivo{s} vinculado{s}',
        finBookFilesKeptNote: 'Déjalo sin marcar para conservarlos en el Gestor de Archivos.',
        finDeleteTransactionTitle: 'Eliminar transacción',
        finFailedToDeleteExpense: 'Error al eliminar el gasto',
        finFailedToUpdateBookCurrency: 'Error al actualizar la moneda del libro',
        finMenuTitle: 'Menú',
        finFinanceMenuAriaLabel: 'Menú de Finanzas',
        finNewBook: 'Nuevo libro',
        finExport: 'Exportar',
        finUnassignedBook: 'Libro sin asignar',
        finNetBalance: 'Saldo neto',
        finConvertedNote: '(convertido)',
        finUnconvertedTitle: 'No había un tipo de cambio disponible para estas transacciones, así que aparecen en la lista pero no se incluyen en el saldo.',
        finUnconvertedCount: '({n} sin convertir)',
        finVizToggleLabel: 'Vista',
        finVizList: 'Lista',
        finVizCharts: 'Gráficos',
        finVizSpendingOverTime: 'Gastos en el tiempo',
        finVizIncomeOverTime: 'Ingresos en el tiempo',
        finVizByCategory: 'Gastos por categoría',
        finVizIncomeByCategory: 'Ingresos por categoría',
        finVizRange10d: 'Últimos 10 días',
        finVizRange15d: 'Últimos 15 días',
        finVizRange30d: 'Últimos 30 días',
        finVizRangeMonth: 'Este mes',
        finVizRange12m: 'Últimos 12 meses',
        finVizRangeCustom: 'Personalizado',
        finVizFrom: 'Desde',
        finVizTo: 'Hasta',
        finVizInvalidRange: 'Elige una fecha de inicio igual o anterior a la fecha de fin.',
        finVizError: 'No se pudieron cargar los gráficos.',
        finVizEmpty: 'No hay transacciones en este periodo.',
        finVizEmptyHint: 'Elige un periodo más largo o registra una transacción para verla aquí.',
        finVizUnconverted: '{n} transacción(es) excluida(s): no había tipo de cambio disponible.',
        finVizUndated: '{n} transacción(es) sin fecha no aparecen en estos gráficos ({amount} en gastos). Añade una fecha para incluirlas.',
        finVizDaily: 'Diario',
        finVizMonthly: 'Mensual',
        finVizDayTooLong: 'La vista diaria está limitada a 366 días.',
        finVizRangeTotal: 'Total del periodo',
        finVizBarLabel: '{period}: {amount}',
        finVizSliceLabel: '{category}: {amount} ({pct})',
        finVizOther: 'Otros',
        finVizUncategorized: 'Sin categoría',
        finVizNoSpending: 'No hay gastos en este periodo.',
        finVizNoIncome: 'No hay ingresos en este periodo.',
        finVizTotalLabel: 'Total',
        finCatDetailOpen: 'Ver las transacciones de {category}',
        finCatDetailCount: '{n} transacción(es)',
        finCatDetailUntitled: 'Transacción sin título',
        finCatDetailEmpty: 'No hay transacciones en esta categoría para este periodo.',
        finBookOptionsTitle: 'Opciones del libro',
        finOptions: 'Opciones',
        finNewEntry: 'Nueva entrada',
        finExitSelection: 'Salir de la selección',
        finManageMembers: 'Gestionar miembros',
        finMembersWord: 'Miembros',
        finFollowMyPreference: 'Seguir mi preferencia ({currency})',
        finRenameBookMenu: 'Renombrar libro',
        finEliminateBookMenu: 'Eliminar libro',
        finNothingRecorded: 'Todavía no hay nada registrado aquí.',
        finSendReceiptOrPayslip: 'Envía una foto de un recibo o nómina en el chat para registrarlo.',
        finUpdateFailed: 'Error al actualizar',
        finDeleteReceiptAt: '¿Eliminar el recibo de {merchant}?',
        finDeleteFailed: 'Error al eliminar',
        finPulseLabel: 'Pulso financiero',
        finOpenFinance: 'Abrir Finanzas',
        finNoTransactionsLoggedToday: 'No hay transacciones registradas este día.',
        fmActiveCount: '{n} activos',
        fmActualSize: 'Tamaño real',
        fmAudioNotSupported: 'Tu navegador no admite el elemento de audio.',
        fmAudioTranscriptionHint: 'El audio se transcribió con la API de Audio de Gemini. El audio original y la transcripción se muestran a continuación.',
        fmAudioTranscriptionTitle: 'Audio con transcripción generada por IA',
        fmBackToParent: 'Volver a la carpeta superior',
        fmBrowseFiles: 'explorar archivos',
        fmBrowseFolders: 'explorar carpetas',
        fmChooseDifferentLocation: 'Elegir otra ubicación',
        fmClearAll: 'Borrar todo',
        fmCloseEsc: 'Cerrar (Esc)',
        fmConfirmDelete: 'Confirmar eliminación',
        fmContentExtractedLabel: 'Contenido: {size} extraído',
        fmContextMove: 'Mover',
        fmContextNewFolder: 'Nueva carpeta',
        fmCouldNotLoadFolders: 'No se pudieron cargar tus carpetas.',
        fmCreate: 'Crear',
        fmCreateFolderFailed: 'No se pudo crear la carpeta',
        fmCreateFolderTitle: 'Crear nueva carpeta',
        fmCurrent: 'Actual',
        fmDeleteBreakdown: '({files} archivos, {folders} carpetas)',
        fmDeleteFileTitle: "¿Eliminar '{name}'?",
        fmDeleteFolderTitle: "¿Eliminar la carpeta '{name}' y todo su contenido?",
        fmDeleteCannotUndo: 'Esta acción no se puede deshacer.',
        fmDeleteDocumentFailed: 'No se pudo eliminar el documento',
        fmDeleteFoldersWarning: 'Las carpetas y su contenido se eliminarán permanentemente.',
        fmDeleteItemsConfirm: '¿Eliminar {n} elemento{s}?',
        fmDeleteNSelected: 'Eliminar {n} seleccionados',
        fmDeletePermanentlyHint: 'Eliminar este documento de forma permanente',
        fmDeleteToTrash: 'Los archivos se moverán a Eliminados recientemente, donde podrás restaurarlos.',
        fmDeleteVersionConfirm: '¿Eliminar la versión {n} de forma permanente? Esta acción no se puede deshacer.',
        fmDeleteVersionFailed: 'No se pudo eliminar la versión',
        fmDeleteVersionPermanently: 'Eliminar esta versión de forma permanente',
        fmDeletingFiles: 'Eliminando archivos…',
        fmDeletingFilesHint: 'Puedes seguir trabajando; te avisaremos cuando termine.',
        fmDeselectAll: 'Deseleccionar todo',
        fmDiscard: 'Descartar',
        fmDiscardConfirm: 'Esto eliminará el documento de forma permanente. ¿Estás seguro?',
        fmDismiss: 'Descartar',
        fmDone: 'Listo',
        fmDragDropHere: 'Arrastra y suelta archivos o carpetas aquí, o',
        fmDragOutToAttachHint: 'Arrastra un archivo o carpeta hacia afuera para adjuntarlo a tu mensaje',
        fmDropHereToAttach: 'Suelta aquí para añadir a la conversación',
        fmDuplicateName: 'Nombre duplicado',
        fmDuplicateNameMessage: 'Ya existe un documento con este nombre. Indica un nombre único.',
        fmEnterFolderName: 'Ingresa el nombre de la carpeta',
        fmEnterNewName: 'Ingresa el nuevo nombre',
        fmEnterUniqueDocName: 'Ingresa un nombre de documento único',
        fmExitSelection: 'Salir de la selección',
        fmExtractedFiles: 'Se extrajeron {n} archivos',
        fmExtractedTextFallback: 'Mostrando el texto extraído del documento',
        fmExtractedTextOcr: 'Texto extraído (OCR)',
        fmFileTooLarge: 'El archivo {name} es demasiado grande. El tamaño máximo por archivo es 1 GB.',
        fmFilename: 'Nombre del archivo',
        fmLocation: 'Ubicación',
        fmFilesToUpload: '{n} archivo{s} para subir',
        fmFit: 'Ajustar',
        fmFitToWidth: 'Ajustar al ancho',
        fmFitWidth: 'ajustar al ancho',
        fmFolderEmpty: 'Esta carpeta está vacía',
        fmFolderEmptyHint: 'Crea una carpeta o sube archivos para comenzar',
        fmFullFidelityHtmlPreview: 'Vista previa HTML de alta fidelidad',
        fmItemCount: '{n} elemento{s}',
        fmItemsCountPlain: '{n} elementos',
        fmLoadFoldersFailed: 'No se pudieron cargar las carpetas',
        fmLoadPreviewFailed: 'No se pudo cargar la vista previa',
        fmLoadVersionHistoryFailed: 'No se pudo cargar el historial de versiones',
        fmLoadingAudioPlayer: 'Cargando reproductor de audio...',
        fmLoadingEllipsis: 'Cargando...',
        fmLoadingEllipsisAlt: 'Cargando…',
        fmLoadingImage: 'Cargando imagen…',
        fmLoadingPreviewEllipsis: 'Cargando vista previa...',
        fmLoadingVersions: 'Cargando versiones...',
        fmManageDocument: 'Gestionar documento',
        fmMenuAriaLabel: 'Menú del gestor de archivos',
        fmMenuTitle: 'Menú',
        fmMove: 'Mover',
        fmMoveDocumentFailed: 'No se pudo mover el documento',
        fmMoveFailed: 'Error al mover. Inténtalo de nuevo.',
        fmMoveInsideSelection: 'Dentro de la selección',
        fmMoveNDocuments: '{n} documento{s}',
        fmMoveNFolders: '{n} carpeta{s}',
        fmMoveNItems: '{n} elementos ({parts})',
        fmMoveNSelected: 'Mover {n} seleccionados',
        fmMoveNothingMoved: 'No se movió nada.',
        fmMoveProtectedSkipped: '{n} carpeta(s) financiera(s) del sistema no se pueden mover y se dejaron fuera.',
        fmMoveSelectedFolder: 'Seleccionada',
        fmMoveHere: 'Mover aquí',
        fmMoveToFolder: 'Mover a carpeta',
        fmMovedToFolder: 'Movido a {folder}',
        fmMoving: 'Moviendo...',
        fmMyDocuments: 'Mis Documentos',
        fmNameInvalidChars: 'El nombre contiene caracteres no válidos',
        fmNameRequired: 'El nombre es obligatorio',
        fmNameTooLong: 'El nombre es demasiado largo (máx. 255 caracteres)',
        fmNeedAttentionCount: '{n} requieren atención',
        fmNewFolder: 'Nueva carpeta',
        fmNewFolderCap: 'Nueva carpeta',
        fmNewName: 'Nuevo nombre',
        fmNoDocumentsFound: 'No se encontraron documentos',
        fmNoDocumentsFoundPeriod: 'No se encontraron documentos.',
        fmNoDocumentsMatching: 'No hay documentos que coincidan con "{query}"',
        fmNoFoldersMatch: 'Ninguna carpeta coincide con esa búsqueda.',
        fmNoSubfolders: 'No hay subcarpetas aquí',
        fmNoTextExtractedImage: 'No se pudo extraer texto de esta imagen.',
        fmNoTranscriptionAvailable: 'No hay transcripción disponible para este archivo de audio.',
        fmNoVersionHistory: 'No hay historial de versiones disponible',
        fmOcrExtractedHint: 'El texto de abajo fue extraído por Gemini Vision OCR; los bytes de la imagen no se modificaron.',
        fmOpenOriginal: 'Abrir original ↗',
        fmOpenOriginalHint: 'Abrir la imagen original en una pestaña nueva',
        fmOrganize: 'Organizar',
        fmOriginalImageHint: 'Imagen original · haz zoom o ábrela en una pestaña nueva para ver la fidelidad completa',
        fmPartialUploadStatus: '{success}/{total} subidos · {failed} con error',
        fmPdfPreview: 'Vista previa de PDF',
        fmPreviewError: 'Error de vista previa',
        fmProcessingCount: 'Procesando {current}/{total}',
        fmProcessingEllipsis: 'Procesando…',
        fmProtectedFolderCannotDelete: 'Las carpetas financieras del sistema no se pueden eliminar. Puedes renombrarlas y eliminar los archivos que contienen.',
        fmProtectedFolderCannotMove: 'Las carpetas financieras del sistema no se pueden mover. Puedes renombrarlas; permanecen en el árbol de Finanzas.',
        fmProtectedNoDelete: 'Carpeta del sistema: no se puede eliminar',
        fmProtectedNoSelect: 'Carpeta del sistema: no se puede seleccionar ni eliminar',
        fmRenameAndRetry: 'Renombrar y reintentar',
        fmRenameDocumentFailed: 'No se pudo renombrar el documento',
        fmRenameDocumentTitle: 'Renombrar documento',
        fmRenameFolderTitle: 'Renombrar carpeta',
        fmRenamedSuccess: '¡Renombrado!',
        fmRoot: 'Raíz',
        fmRowsByColumns: '{rows} fila{rs} × {cols} columna{cs}',
        fmSaveName: 'Guardar nombre',
        fmSaveWithThisName: 'Guardar con este nombre',
        fmSavingEllipsis: 'Guardando…',
        fmSearchDocumentsEllipsis: 'Buscar documentos…',
        fmSearchDocumentsPlaceholder: 'Buscar documentos...',
        fmSearchFoldersPlaceholder: 'Buscar carpetas...',
        fmSelectAll: 'Seleccionar todo',
        fmSelectedFiles: 'Archivos seleccionados ({n})',
        fmSelectedFolders: 'Carpetas seleccionadas ({n})',
        fmSizeLabel: 'Tamaño: {size}',
        fmSizeLimits: 'Archivos individuales de hasta 1 GB. Archivos comprimidos de hasta 100 GB (máx. 1000 archivos).',
        fmSortAscending: 'Ordenar ascendente',
        fmSortDescending: 'Ordenar descendente',
        fmSpreadsheetPreview: 'Vista previa de la hoja de cálculo',
        fmSpreadsheetPreviewHint: 'Mostrando los datos como tabla. Descarga el archivo para ver fórmulas, formato y gráficos.',
        fmStartFileUploads: '{n} subida{s} de archivo',
        fmStartFolderUploads: '{n} subida{s} de carpeta',
        fmStartFoldersAndFiles: '{folders} carpeta(s) y {files} archivo(s)',
        fmStartUpload: 'Iniciar {rest}',
        fmStatusFailed: 'Error al procesar',
        fmStatusLine: 'Estado: {label}',
        fmStatusPending: 'Pendiente',
        fmStatusProcessing: 'Procesando...',
        fmSupportedExtensions: 'PDF, DOCX, PPTX, XLSX, XLS, TXT, MD, PY, JS, TS, HTML, CSS, JSON, CSV, SQL, ZIP, 7Z, y muchos más...',
        fmSupportsFileTypes: 'Admite documentos, código fuente, archivos de configuración, archivos de datos y archivos comprimidos',
        fmToggleSortDirection: 'Alternar dirección de orden',
        fmTranscription: 'Transcripción',
        fmTypeArchive: 'Archivo comprimido',
        fmTypeAudio: 'Audio',
        fmTypeCode: 'Código',
        fmTypeExcel: 'Excel',
        fmTypeFile: 'Archivo',
        fmTypeFolder: 'Carpeta',
        fmTypeImage: 'Imagen',
        fmTypePdf: 'PDF',
        fmTypePpt: 'PPT',
        fmTypeText: 'Texto',
        fmTypeWord: 'Word',
        fmUnknown: 'Desconocido',
        fmUploadDocuments: 'Subir documentos',
        fmUploadFailed: 'Error al subir',
        fmUploadFiles: 'Subir archivos',
        fmUploadStagingCustom: 'Personalizado',
        fmUploadStagingDestinationFor: 'Destino de {name}',
        fmUploadStagingGlobal: 'Establecer destino global',
        fmUploadStagingReset: 'Usar el destino global',
        fmUploaded: 'Subido',
        fmUploadedCount: 'Subidos {success}/{total} archivos',
        fmUploadingItemsPct: 'Subiendo {n} elemento{s} ({pct}%)',
        fmUploads: 'Subidas',
        fmUploadsBackgroundHint: 'Las subidas se ejecutan en segundo plano; puedes cerrar esto y seguir trabajando.',
        fmUploadsNeedAttention: '{n} subida{s} requieren atención',
        fmVersionHistory: 'Historial de versiones',
        fmVersionN: 'Versión {n}',
        fmVersionsColumn: 'Versiones',
        fmView: 'Vista',
        fmViewGrid: 'Cuadrícula',
        fmViewLinear: 'Lista',
        fmZoomIn: 'Acercar',
        fmZoomOut: 'Alejar',
        msgConvDataOpenDoc: 'Abrir {name}',
        msgConvDataDocumentsTitle: 'Documentos en esta conversación',
        msgConvDataDocsLoadError: 'No se pudieron cargar los documentos.',
        msgConvDataDocsLoading: 'Cargando…',
        msgConvDataDocsEmpty: 'No se han creado ni editado documentos aquí.',
        msgConvDataDocsCreated: 'Creados',
        msgConvDataDocsEdited: 'Editados',
        msgConvDataDialogAria: 'Datos de la conversación',
        msgConvDataHeader: 'Datos de la conversación',
        msgConvDataDefaultTitle: 'Conversación',
        msgConvDataCloseAria: 'Cerrar datos de la conversación',
        msgConvDataLoadError: 'No se pudieron cargar los datos de la conversación.',
        msgConvDataLoading: 'Cargando…',
        msgCtxEmpty: '(vacío)',
        msgCtxShowLess: 'Ver menos',
        msgCtxShowMore: 'Ver más',
        msgCtxSectionSystemPrompt: 'Instrucción del sistema',
        msgCtxSectionWorkspaceContext: 'Contexto del espacio de trabajo',
        msgCtxSectionConversationHistory: 'Historial de la conversación',
        msgCtxSectionRepliedMessages: 'Mensajes respondidos',
        msgCtxSectionDocumentContext: 'Contexto de documentos',
        msgCtxSectionCurrentQuery: 'Consulta actual',
        msgCtxTokensCount: '{n} tokens',
        msgCtxNotAvailable: '(no disponible)',
        msgCtxNotConfigured: '(no configurado)',
        msgCtxNoHistory: '(sin historial)',
        msgCtxNoDocuments: '(sin documentos)',
        msgCtxRoleUser: 'Usuario',
        msgCtxRoleAssistant: 'Asistente',
        msgCtxUnnamedDocument: 'Documento sin nombre',
        msgCtxLegendSystem: 'Sistema',
        msgCtxLegendWorkspace: 'Espacio de trabajo',
        msgCtxLegendHistory: 'Historial',
        msgCtxLegendReply: 'Respuesta',
        msgCtxLegendDocuments: 'Documentos',
        msgCtxLegendQuery: 'Consulta',
        msgCtxAgenticPipeline: 'Flujo agéntico',
        msgCtxIterationsLabel: '{n} iteración(es)',
        msgCtxToolCallsLabel: '{n} llamada(s) a herramienta',
        msgCtxStepLabel: 'paso {n}',
        msgCtxHeaderTitle: 'Contexto del LLM',
        msgCtxTotalTokens: '{n} tokens en total',
        msgCtxGroundingUsed: '🌐 Se usó fundamentación',
        msgCtxGroundingOffered: '🌐 Fundamentación disponible',
        msgCtxShowStructuredView: 'Ver vista estructurada',
        msgCtxShowRawPrompt: 'Ver prompt en bruto',
        msgCtxLoadingContext: 'Cargando contexto...',
        msgCtxFailedToLoad: 'No se pudo cargar el contexto',
        msgCtxNoContextAvailable: 'No hay contexto disponible para este mensaje',
        msgCtxRetentionExpired: 'Los detalles técnicos se conservan durante 90 días.',
        msgCtxFooterNote: 'Este es el prompt y contexto exactos enviados al LLM para esta respuesta.',
        msgThinkingLabel: 'Pensando',
        msgAnalyzingRequest: 'Analizando tu solicitud...',
        msgToolsUsedLabel: '{n} herramienta{s} usada{s}',
        msgWithReasoning: 'con razonamiento',
        msgProcessed: 'Procesado',
        msgWelcomeTitle: 'Bienvenido {name}',
        msgWelcomeTitleGeneric: 'Bienvenido',
        msgWelcomeSubtitle: 'Inicia una conversación con tu inteligencia artificial personal (PAI)',
        msgVoiceAudioUnavailable: 'Audio no disponible (enlace caducado)',
        msgVoicePause: 'Pausar',
        msgVoicePlay: 'Reproducir',
        msgRteHeading: 'Encabezado',
        msgRteBold: 'Negrita',
        msgRteItalic: 'Cursiva',
        msgRteBulletList: 'Lista con viñetas',
        msgRteNumberedList: 'Lista numerada',
        msgRteChecklist: 'Lista de tareas',
        msgRteLink: 'Enlace',
        msgRteAddLink: 'Añadir',
        msgDragToResize: 'Arrastra para redimensionar',
        msgUploadNoDocumentId: 'La subida no devolvió un id de documento',
        attachArchiveFiles: '{n} archivos guardados',
        attachArchiveUnpacking: 'descomprimiendo…',
        msgUploadDuplicateFilename: 'Ya existe un documento con este nombre. Proporciona un nombre único.',
        msgUploadFailed: 'Error al subir el archivo',
        msgAttachmentDefaultFilename: 'Documento',
        msgVoiceInaudible: '[inaudible]',
        msgTranscriptionFailed: 'Error en la transcripción',
        msgTranscriptionInterrupted: 'La transcripción se interrumpió — grábalo de nuevo',
        msgStreamErrorContent: 'Se produjo un error al generar la respuesta: {message}',
        msgScrollToLatest: 'Ir al último mensaje',
        msgDeleteConversationTitle: 'Eliminar conversación',
        msgDeleteConversationMessage: '¿Seguro que quieres eliminar "{name}"?\n\nEsta acción no se puede deshacer.',
        msgDeleteConversationFallbackName: 'esta conversación',
        msgDeleteConversationFailed: 'No se pudo eliminar "{name}". Se ha restaurado; inténtalo de nuevo.',
        msgDeleteConversationsFailed: 'No se pudieron eliminar esas conversaciones. Se han restaurado; inténtalo de nuevo.',
        msgDuplicateBatchSuffix: '{message} ({n} archivos de este lote necesitan un nombre único)',
        msgSelectFromFileManager: 'Seleccionar del gestor de archivos',
        msgDropFilesToUpload: 'Suelta los archivos para subirlos',
        msgDropFilesHint: 'Los archivos se indexarán y se añadirán a tu biblioteca',
        msgUploadingAndIndexing: 'Subiendo e indexando...',
        msgReceiptImageAlt: 'Imagen del recibo',
        msgReceiptFallback: 'Recibo',
        msgKeptCopyLabel: 'Copia conservada',
        msgKeptLabel: 'Conservada',
        msgDuplicateLabel: 'Duplicado',
        msgReceiptImageWithName: 'Imagen de {name}',
        msgPreviewReceipt: 'Vista previa del recibo',
        msgDownloadReceipt: 'Descargar recibo',
        msgThatBookFallback: 'ese libro',
        msgUnassignedFallback: 'Sin asignar',
        msgMovedIntoAnyway: 'Se movió a {target} de todos modos — ahora hay una segunda copia allí.',
        msgCouldNotMoveTransaction: 'No se pudo mover la transacción. Inténtalo de nuevo.',
        msgDeletedDuplicateFrom: 'Se eliminó el duplicado de {source}. {target} conserva el original.',
        msgCouldNotDeleteDuplicate: 'No se pudo eliminar el duplicado. Inténtalo de nuevo.',
        msgKeptDuplicateIn: 'Se conservó el duplicado en {source}. No se movió nada.',
        msgSkippedDiscardedUpload: 'Omitido — se descartó la subida duplicada.',
        msgCouldNotDiscardUpload: 'No se pudo descartar la subida. Inténtalo de nuevo.',
        msgLoggedAnywayIn: 'Se registró de todos modos en {book}.',
        msgCouldNotLogExpense: 'No se pudo registrar el gasto. Inténtalo de nuevo.',
        msgTicketMayExistIn: 'Este ticket puede que ya exista en {target}',
        msgPossibleDuplicateDetected: 'Posible duplicado detectado',
        msgAlreadyInTarget: 'Ya está en {target}',
        msgAlreadyInLedger: 'Ya está en tu libro',
        msgBeingMovedFrom: 'Se está moviendo desde {source}',
        msgNewUpload: 'Subida nueva',
        msgMoreMatches: '+{n} coincidencia(s) más {place}.',
        msgInTargetBook: 'en {target}',
        msgInYourLedger: 'en tu libro',
        msgDeleteDuplicateFromTitle: 'Eliminar el duplicado de {source} — ya existe en {target}',
        msgDeleteDuplicateFromBtn: 'Eliminar duplicado de {source}',
        msgKeepDuplicateInTitle: 'Conservar el duplicado en {source} — cancela el movimiento',
        msgKeepDuplicateInBtn: 'Conservar duplicado en {source}',
        msgMoveAnywayTitle: 'Moverlo a {target} de todos modos — crea una segunda copia',
        msgMoveAnywayBtn: 'Mover de todos modos',
        msgCustomInstructionPrefix: 'O escribe una instrucción personalizada abajo — por ejemplo',
        msgCustomInstructionExample: '"mover todos los duplicados a un libro nuevo llamado Revisar Después"',
        msgDiscardDuplicateUploadTitle: 'Descartar esta subida duplicada',
        msgSkipDiscardUploadBtn: 'Omitir (descartar subida)',
        msgLogAnywaySeparateTitle: 'Registrarlo de todos modos como una transacción separada',
        msgProceedLogAnywayBtn: 'Continuar (registrar de todos modos)',
        msgCompareReceiptsPrefix: 'Compara los recibos de arriba y elige — o responde',
        msgProceedWord: '"continuar"',
        msgSkipWord: '"omitir"',
        msgOcrUnableToExtract: 'No pudimos extraer los datos de {target}',
        msgOcrThisImage: 'esta imagen',
        msgOcrNImages: '{n} imágenes',
        msgOcrNoTransactionLogged: 'No se registró ninguna transacción.',
        msgOcrUnreadableLabel: '{name} (ilegible)',
        msgOcrCouldntReadTotal: '{name} — no se pudo leer el total',
        msgOcrCouldntReadReceipt: 'No se pudo leer este recibo',
        msgEliminatedRedundantFiles: 'Se eliminaron {n} archivo{s} redundante{s} — tu libro conserva una copia de cada uno.',
        msgCouldNotDeleteRedundantFiles: 'No se pudieron eliminar los archivos redundantes. Inténtalo de nuevo.',
        msgAddedDuplicatesAsTransactions: 'Se añadieron {n} duplicado{s} como registro{s2} independiente{s2} en {book}.',
        msgCouldNotAddDuplicates: 'No se pudieron añadir los duplicados. Inténtalo de nuevo.',
        msgBatchProcessedSummary: 'Lote procesado — {unique} ticket{us} único{us} guardado{us}, {dup} duplicado{ds} encontrado{ds}',
        msgBatchOneVersionRecorded: 'Ya hay una versión de cada duplicado registrada en',
        msgBatchChooseRedundant: 'Elige qué hacer con {copies}:',
        msgCopySingular: 'la copia redundante',
        msgCopyPlural: 'las copias redundantes',
        msgEliminateDuplicatesTitle: 'Eliminar permanentemente los archivos duplicados redundantes para ahorrar espacio',
        msgEliminateDuplicatesBtn: 'Eliminar duplicados',
        msgAddRegardlessTitle: 'Guardar las copias redundantes como transacciones separadas adicionales',
        msgAddRegardlessBtn: 'Añadir de todos modos',
        msgNoTransactionsMatched: 'No hay transacciones que coincidan.',
        msgTransactionsCount: '{n} transacciones',
        msgUnknownFallback: 'Desconocido',
        msgNoDateFallback: 'sin fecha',
        msgNoTranscription: '[sin transcripción]',
        msgVoiceMessagesPlural: 'Mensajes de voz',
        msgVoiceMessageSingular: 'Mensaje de voz',
        msgHideTranscription: 'Ocultar transcripción',
        msgShowTranscription: 'Ver transcripción',
        msgVoiceMessageN: 'Mensaje {n}',
        msgDownloadReplyTitle: 'Descargar esta respuesta como archivo (no se guarda en tu gestor de archivos)',
        msgDownloadAsDocument: 'Descargar como documento',
        msgDownloadAsHeader: 'Descargar como',
        msgDownloadFormatPdf: 'PDF (.pdf)',
        msgDownloadFormatWord: 'Word (.docx)',
        msgDownloadFormatText: 'Texto sin formato (.txt)',
        msgDownloadPreparing: 'Preparando…',
        msgDownloadedName: 'Descargado “{name}”',
        msgDownloadFailed: 'Error en la descarga',
        msgVoiceMessageEditedLabel: 'Mensaje de voz {n} editado:',
        msgRemoveFromReply: 'Quitar de la respuesta',
        msgReplyToThisMessage: 'Responder a este mensaje',
        msgEditMessage: 'Editar mensaje',
        msgEditYourMessagePlaceholder: 'Edita tu mensaje...',
        msgNoAudioTextOnly: 'Sin audio — este mensaje se guardará solo como texto.',
        msgVoiceMessageEditedSingular: 'Mensaje de voz editado',
        msgVoiceMessageNEdited: 'Mensaje de voz {n} editado',
        msgRemoveAudio: 'Quitar audio',
        msgRemoveSegmentAria: 'Quitar {label}',
        msgSaveAndRegenerate: 'Guardar y regenerar',
        msgStopAndAddRecording: 'Detener y añadir grabación',
        msgStopAndAddElapsed: 'Detener y añadir ({n}s)',
        msgAddVoice: 'Añadir voz',
        msgEditKeyboardHints: 'Enter para guardar, Shift+Enter para salto de línea, Esc para cancelar',
        msgEditedIndicator: '(editado)',
        msgDocumentUpdated: 'Documento actualizado',
        msgDocumentUpdatedVersion: 'Documento actualizado (v{version})',
        msgChunksIndexed: '{n} fragmentos indexados',
        msgClickToViewDocument: 'Haz clic para ver el documento',
        msgViewVersionHistory: 'Ver historial de versiones',
        msgHistory: 'Historial',
        msgDocumentDiscarded: 'Documento descartado',
        msgDocumentCreated: 'Documento creado',
        msgMovedBadge: 'Movido',
        msgRenamedBadge: 'Renombrado',
        msgRenameMoveDiscardTitle: 'Renombrar, mover o descartar este documento',
        msgManage: 'Gestionar',
        msgEnhancedWithDocContext: 'Mejorado con contexto de documentos',
        msgUsingNDocuments: 'Usando {n} documento{s}',
        msgDocumentsUsedLabel: 'Documentos usados:',
        msgClickToViewOptions: 'Haz clic para ver las opciones',
        msgDocumentNotAvailable: 'Documento no disponible',
        msgUsingDocumentContext: 'Usando contexto de documentos',
        msgStandardAiResponse: 'Respuesta estándar de la IA',
        msgNoDocumentContextUsed: 'No se usó contexto de documentos',
        msgGoogleSearchUsed: 'Se usó Google Search para responder esta consulta',
        msgSearchCount: '({n} búsqueda(s))',
        msgViewEnergy: 'Ver Energía',
        msgViewLlmContext: 'Ver contexto del LLM',
        msgViewDocumentContent: 'Ver el contenido del documento',
        msgSaveToYourDevice: 'Guardar en tu dispositivo',
        msgFailedToDownloadDocument: 'No se pudo descargar el documento. Inténtalo de nuevo.',
        msgClickToJumpOriginal: 'Haz clic para ir al mensaje original',
        navExportFailed: 'No se pudo exportar esta conversación. Inténtalo de nuevo.',
        navNewConversationTitle: 'Nueva Conversación',
        navRenameFailed: 'No se pudo renombrar la conversación',
        navDeleteConversationsConfirm: '¿Eliminar {n} conversación{s}?',
        navBulkDeleteFailed: 'No se pudieron eliminar algunas conversaciones. Inténtalo de nuevo.',
        navUnfinishedDraft: 'Borrador sin terminar',
        navNewResponseReady: 'Nueva respuesta lista',
        navFromUser: 'de {name}',
        navConversationOptions: 'Opciones de la conversación',
        navConversationData: 'Datos de la conversación',
        navExportTraceTitle: 'Descargar un registro completo de depuración (mensajes, pasos de razonamiento, prompts, llamadas al LLM)',
        navExport: 'Exportar',
        navSearchAllChats: 'Buscar en todos los chats...',
        navSearchAndManage: 'Buscar y gestionar',
        navAllCount: 'Todas ({n})',
        navEliminateCount: 'Eliminar ({n})',
        navSearching: 'Buscando...',
        navNoMessagesFound: 'No se encontraron mensajes',
        navSearchTotalOccurrences: '{n} resultados',
        navSearchOccurrences: '{n} apariciones',
        navSearchNameMatch: 'Coincide el nombre',
        navSearchExpandMessages: 'Mostrar mensajes coincidentes',
        navSearchCollapseMessages: 'Ocultar mensajes coincidentes',
        navSearchLoadingMessages: 'Cargando coincidencias...',
        navSearchMessagesError: 'No se pudieron cargar las coincidencias',
        navYou: 'Tú',
        navAi: 'IA',
        navLoadingConversations: 'Cargando conversaciones...',
        navStartNewChat: 'Inicia un chat nuevo para empezar',
        navSharedWithMe: 'Compartido conmigo',
        navHideConversations: 'Ocultar conversaciones',
        navShowConversations: 'Mostrar conversaciones',
        navCloseMainChat: 'Cerrar Chat Principal',
        navHideSidebar: 'Ocultar barra lateral',
        navShowSidebar: 'Mostrar barra lateral',
        navSharedReadOnly: 'Compartido (solo lectura)',
        navSearchInConversation: 'Buscar en la conversación',
        navShareConversation: 'Compartir conversación',
        navFindInConversation: 'Buscar en la conversación...',
        navNoResults: 'Sin resultados',
        navPreviousMatch: 'Coincidencia anterior (Mayús+Intro)',
        navNextMatch: 'Siguiente coincidencia (Intro)',
        navCloseSearchEsc: 'Cerrar búsqueda (Esc)',
        navCollapseToSplitView: 'Contraer a vista dividida',
        navExpandToCoverChat: 'Expandir para cubrir el chat',
        navClosePanelEsc: 'Cerrar panel (Esc)',
        navAccountMenu: 'Menú de la cuenta',
        navTelegramNotConfigured: 'PAI Tools no está configurado',
        navTelegramLinkFailed: 'No se pudo iniciar la vinculación de PAI Tools',
        navTelegramUnlinkFailed: 'No se pudo desvincular Telegram',
        navConvoNotConfigured: 'PAI Conversaciones no está configurado',
        navConvoLinkFailed: 'No se pudo iniciar la vinculación',
        navConvoDisconnectFailed: 'No se pudo desconectar',
        navCloseEsc: 'Cerrar (Esc)',
        navNotifications: 'Notificaciones',
        navNotificationCenter: 'Centro de Notificaciones',
        navNotificationCenterDesc: 'Elige qué alertas recibes y dónde llegan.',
        navSearchTimezone: 'Buscar zona horaria…',
        navAdmin: 'Administración',
        navUsageDashboard: 'Panel de Uso',
        navUsageDashboardDesc: 'Supervisa el uso de tokens, los costes y el desglose por proceso',
        navChannelMainOffice: 'Oficina Central',
        navChannelShortOffice: 'Oficina',
        navChannelShortTg: 'TG',
        navLinkTelegramHint: 'Vincula Telegram en Configuración para activarlo.',
        navNotifLoadError: 'No se pudo cargar la configuración de notificaciones.',
        navNotifSaveError: 'No se pudo guardar. Inténtalo de nuevo.',
        navNotificationCenterTitle: 'Centro de Notificaciones',
        navAlerts: 'Alertas',
        navDeliveryChannels: 'Canales de Entrega',
        navEnableFeatureNotifications: 'Activar notificaciones de {label}',
        navSilenceFeatureAlerts: 'Desactívalo para silenciar cada alerta de {label}.',
        navEnableFeatureNotificationsAria: 'Activar notificaciones de {label}',
        navRequiredAlwaysOn: 'Obligatorio — siempre activo',
        navMinAgo: 'hace {n} min',
        navHourAgo: 'hace {n}h',
        navNow: 'Ahora',
        navInMin: 'En {n} min',
        navTodayAt: 'Hoy {time}',
        navTomorrowAt: 'Mañana {time}',
        navBadgeCalendar: 'Calendario',
        navBadgeInvitation: 'Invitación',
        navBadgeContactRequest: 'Solicitud de contacto',
        navBadgeSystem: 'Notificación del sistema',
        navBadgeFinance: 'Finanzas',
        navBadgeBoards: 'Tableros',
        navBadgeVault: 'Gestor de Archivos',
        navBadgeNotes: 'Notas',
        navBadgeNetwork: 'Red',
        navNotifComingSoon: 'Próximamente',
        navDismissNotification: 'Descartar notificación',
        navLoadRemindersFailed: 'No se pudieron cargar los recordatorios',
        navClearReminderFailed: 'No se pudo descartar el recordatorio',
        navActiveCount: '{n} activos',
        navLoadingEllipsis: 'Cargando…',
        navNoReminders: 'No hay recordatorios',
        navReminderExample: 'Prueba "recuérdame llamar a Charlie a las 3pm"',
        navSharedWithEmail: 'Compartido con {email}',
        navShareConversationTitle: 'Compartir Conversación',
        navShareReferencesDocs: 'Esta conversación hace referencia a {n} documento{s}',
        navSelectDocsPrefix: 'Selecciona qué documentos',
        navSelectDocsSuffix: 'podrá previsualizar y descargar:',
        navOfSelected: '{selected} de {total} seleccionados',
        navSelectAll: 'Seleccionar todo',
        navDeselectAll: 'Deseleccionar todo',
        navSharing: 'Compartiendo...',
        navShareWithDocs: 'Compartir con {n} doc{s}',
        navShareWithoutDocs: 'Compartir sin documentos',
        navShareByEmail: 'Compartir con un usuario (por email)',
        navRecentContacts: 'Contactos Recientes',
        navSharedWithCount: 'Compartido con ({n})',
        navNotSharedWithAnyone: 'No compartido con nadie',
        navRemoveAccess: 'Quitar acceso',
        navShareReadOnlyFooter: 'Los usuarios compartidos tienen acceso de solo lectura. Pueden ver los mensajes, pero no enviar, editar ni eliminar.',
        navShareBoardTitle: 'Compartir Tablero',
        navShareBookTitle: 'Compartir Libro',
        navBoardPermOwnerDesc: 'Gestión total del tablero: añadir y quitar Miembros, renombrar el tablero. Un Propietario no puede cambiar ni quitar a otro Propietario.',
        navBoardPermMemberDesc: 'Autonomía total sobre los tickets: crear, editar, mover, asignar y eliminar tickets. Sin gestión de miembros — la lista de Miembros es de solo lectura.',
        navBoardPermNote: 'Solo el Creador Original puede eliminar el tablero o gestionar los roles de Propietario.',
        navBookPermOwnerDesc: 'Gestión total del libro: añadir y quitar Miembros, editar o eliminar cualquier transacción. Un Propietario no puede cambiar ni quitar a otro Propietario.',
        navBookPermMemberDesc: 'Autonomía total únicamente sobre sus PROPIAS entradas.',
        navBookPermNote: 'Solo el Creador Original puede eliminar el libro o gestionar los roles de Propietario.',
        navMembers: 'Miembros',
        navFailedToLoadMembers: 'No se pudieron cargar los miembros',
        navAddOwner: 'Añadir Propietario',
        navAddMember: 'Añadir Miembro',
        navMembersSkippedNotice: '{n} miembro{s} ya tenía acceso individual y lo conservó — no está gestionado por este acceso de equipo.',
        navFailedToAdd: 'No se pudo añadir.',
        navFailedToRemoveTeam: 'No se pudo quitar el equipo.',
        navFailedToChangeRole: 'No se pudo cambiar el rol',
        navFailedToRemove: 'No se pudo quitar',
        navYouParens: '(tú)',
        navAccessViaTeamHint: 'El acceso proviene de un equipo compartido — gestiónalo en la sección Equipos',
        navViaTeam: 'vía {team}',
        navTeamFallback: 'equipo',
        navRoleLabelOwner: 'propietario',
        navRoleLabelMember: 'miembro',
        navLeave: 'Salir',
        navRemoveMember: 'Quitar miembro',
        navMembersViaThisTeam: '{n} miembro{s} a través de este equipo',
        navRemoveTeamHint: 'Quitar este equipo — todos los que añadió perderán el acceso',
        navRemovingTeamRemovesEveryone: 'Quitar un equipo elimina a todos los que añadió. Para conservar a alguien, invítalo individualmente después.',
        navInviteContact: 'Invitar Contacto',
        navTeamMemberCount: 'Equipo · {n} miembros',
        navAlreadyHereViaTeam: 'Ya está aquí a través de {team}',
        navPermissionInfo: 'Información de permisos',
        navTeamsAddedAsMembers: 'Los equipos se añaden como Miembros, y su acceso sigue la lista del equipo.',
        // ── Legal y consentimiento de cookies (iubenda) ────────────────────
        legalPrivacyPolicy: 'Política de Privacidad',
        legalCookiePolicy: 'Política de Cookies',
        legalTerms: 'Términos y Condiciones',
        // El único documento que aloja PAI. Solo se traduce su interfaz: el
        // texto del anexo es un documento legal incorporado a los Términos y
        // permanece en el inglés en que se firmó.
        legalAddendum: 'Anexo de Datos e IA',
        legalDmca: 'DMCA',
        legalEyebrow: 'Legal',
        legalBackToPai: 'Volver a PAI',
        legalAllRightsReserved: 'Todos los derechos reservados.',
        legalEntityProductOf: 'PAI es un producto de',
        legalEntityEmailLabel: 'Correo:',
        settingsLegalTitle: 'Legal',
        settingsLegalDocuments: 'Políticas y condiciones',
        settingsLegalDesc: 'Consulta los documentos que rigen tu uso de PAI.',
        settingsCookiePrefs: 'Preferencias de cookies',
        settingsCookiePrefsDesc: 'Revisa o cambia las decisiones de seguimiento que tomaste.',
        settingsCookiePrefsAction: 'Gestionar',
        settingsCookiePrefsUnavailable: 'No se pudo cargar el widget de consentimiento.',
        authLegalConsentPrefix: 'Acepto los',
        authLegalConsentMiddle: 'y reconozco la',
        authLegalConsentSuffix: '.',
        authLegalConsentAge: 'Confirmo que tengo 18 años de edad o más.',
        authLegalConsentRequired: 'Acepta los Términos y Condiciones para crear una cuenta.',
        authGoogleConsentPrefix: 'Al continuar con Google aceptas los',
        authGoogleConsentMiddle: 'y reconoces la',
        authGoogleConsentSuffix: '.',
        // ── Two-step verification (2FA #333) ─────────────────────────────────
        mfaFactorLegacyPhone: 'Teléfono (ya no disponible)',
        mfaFactorTotp: 'App de autenticación',
        mfaErrInvalidCode: 'Ese código no es correcto. Revísalo e inténtalo de nuevo.',
        mfaErrExpired: 'Ese código ha caducado. Espera a uno nuevo e inténtalo de nuevo.',
        mfaErrTooMany: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
        mfaErrWrongPassword: 'Contraseña incorrecta.',
        mfaCodesFileTitle: 'Códigos de recuperación de PAI',
        mfaCodesFileNote: 'Cada código funciona una sola vez. Guárdalos en un lugar seguro.',
        mfaRecoveryInvalid: 'Ese código de recuperación no es válido.',
        mfaRecoveryTitle: 'Usar un código de recuperación',
        mfaRecoveryIntro: 'Introduce uno de los códigos de recuperación que guardaste al activar la verificación en dos pasos. Al usarlo, la verificación en dos pasos se desactiva en tu cuenta.',
        mfaRecoveryLabel: 'Código de recuperación',
        mfaRecoverySubmit: 'Usar código de recuperación',
        mfaUseCodeInstead: 'Usar un código de verificación',
        mfaBackToSignIn: 'Volver al inicio de sesión',
        mfaChallengeTitle: 'Verificación en dos pasos',
        mfaChallengeTotpIntro: 'Introduce el código de 6 dígitos de tu app de autenticación.',
        mfaNoTotpRecovery: 'Esta cuenta no tiene una app de autenticación configurada. Usa uno de tus códigos de recuperación para iniciar sesión.',
        mfaNoTotp: 'Esta cuenta no tiene una app de autenticación configurada, así que no se puede confirmar aquí.',
        mfaChooseMethod: 'Método de verificación',
        mfaCodeLabel: 'Código de verificación',
        mfaVerifying: 'Verificando…',
        mfaVerify: 'Verificar',
        mfaUseRecovery: 'Usar un código de recuperación',
        mfaCodesTitle: 'Guarda tus códigos de recuperación',
        mfaCodesIntro: 'Si pierdes tu teléfono, un código de recuperación es la única forma de volver a entrar en tu cuenta. Guárdalos en un lugar seguro, lejos de tu teléfono.',
        mfaCodesCopied: 'Copiados',
        mfaCodesCopy: 'Copiar',
        mfaCodesDownload: 'Descargar (.txt)',
        mfaCodesAck: 'He guardado estos 10 códigos de recuperación. Cada uno funciona una sola vez.',
        mfaContinue: 'Continuar',
        mfaStatusOn: 'Activada. Cada inicio de sesión pide un código de tu segundo paso.',
        mfaStatusOff: 'Desactivada. Añade un segundo paso al inicio de sesión para que una contraseña robada no baste.',
        mfaTotpScanTitle: 'Escanea con tu app de autenticación',
        mfaTotpScanIntro: 'Abre Google Authenticator, 1Password, Authy u otra app similar y escanea este código. O escribe en la app la clave de abajo.',
        mfaTotpQrAlt: 'Código QR para tu app de autenticación',
        mfaTotpKeyLabel: 'Clave de configuración',
        mfaEnrollCodeLabel: 'Introduce el código de 6 dígitos',
        mfaEnrollCodeTotp: 'Tu app de autenticación ya muestra un código para PAI.',
        mfaActivate: 'Activar',
        mfaReauthTitle: 'Confirma que eres tú',
        mfaReauthPasswordIntro: 'Por tu seguridad, introduce tu contraseña para continuar.',
        mfaReauthGoogleIntro: 'Por tu seguridad, vuelve a iniciar sesión con Google para continuar.',
        mfaSecuritySection: 'Seguridad',
        mfaTitle: 'Verificación en dos pasos',
        mfaFactorAdded: 'Añadido el {date}',
        mfaRecoveryCodesTitle: 'Códigos de recuperación',
        mfaCodesRemaining: 'Quedan {n} de 10',
        mfaRegenerateCodes: 'Generar nuevos códigos de recuperación',
        mfaAddTotp: 'Añadir una app de autenticación',
        mfaSetUp: 'Activar la verificación en dos pasos',
        mfaMethodsHint: 'Usa una app de autenticación como Google Authenticator, 1Password o Authy. Tus códigos de recuperación son la copia de seguridad.',
        mfaEnableTotp: 'Configurar app de autenticación',
        mfaEnabledNotice: 'La verificación en dos pasos está activada. Tus otros dispositivos tendrán que volver a iniciar sesión.',
        mfaRemovedNotice: 'Método eliminado.',
        mfaCodesSavedNotice: 'Nuevos códigos de recuperación guardados. Los anteriores ya no funcionan.',
        mfaVerifyEmailNotice: 'Primero verifica tu correo: te hemos enviado un enlace.',
        mfaMessagingNote: 'Las conexiones de mensajería (Telegram) solo se pueden cambiar desde Configuración.',
        mfaSignedOutNotice: 'Tu cuenta ahora usa verificación en dos pasos. Vuelve a iniciar sesión para continuar.',
        mfaRecoveredNotice: 'La verificación en dos pasos se ha desactivado. Vuelve a activarla en Configuración → Seguridad.',
        mfaRecoveredGoogle: 'Pulsa Continuar con Google para volver a iniciar sesión.',
    },
};

/**
 * The language the user explicitly CHOSE, or null when they never picked one.
 * Used to mirror the choice onto the server profile (user-testing hotfix
 * 2026-10-06) so the backend's own Telegram messages speak it too —
 * an unset preference is deliberately not pushed (the server defaults to 'en').
 */
export const storedLanguage = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === 'en' || v === 'es') return v;
    } catch (_) { /* ignore */ }
    return null;
};

const readInitial = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === 'en' || v === 'es') return v;
    } catch (_) { /* ignore */ }
    return 'en';
};

// The ONE resolver. Shared by the provider's `t` and by the default context value
// below, so a component rendered OUTSIDE a provider behaves exactly like English
// inside one — including interpolation.
//
// It used to be inlined in the provider while the default context carried a naive
// `(k, f) => f ?? k`. That stub silently dropped interpolation, so any component
// rendered without a provider (every unit test, and any future subtree mounted
// outside it) printed the RAW placeholder — 'Replying to {n} message{s}' — and the
// object-arg signature `t(key, vars)` returned the vars OBJECT. Sharing one
// implementation removes that whole failure mode by construction.
//
// t(key, fallback?, vars?) — a plain string second arg is the fallback (legacy
// signature); an object second arg is the interpolation vars. Vars replace {name}
// placeholders in the resolved string (used by the ResolutionCard family for
// count/name-driven labels — H-3.2).
export function translate(language, key, fallbackOrVars, maybeVars) {
    let fallback;
    let vars;
    if (fallbackOrVars && typeof fallbackOrVars === 'object') { vars = fallbackOrVars; }
    else { fallback = fallbackOrVars; vars = maybeVars; }
    const dict = STRINGS[language] || STRINGS.en;
    let str = dict[key] ?? STRINGS.en[key] ?? fallback ?? key;
    if (vars && typeof str === 'string') {
        str = str.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
    }
    return str;
}

/**
 * Translate a sentence whose placeholders must render as REACT NODES rather than
 * plain text — e.g. a confirmation line where each value is bold.
 *
 * Concatenating translated fragments around JSX would freeze ENGLISH word order,
 * which is exactly what makes a machine-translated UI read wrong. This splits the
 * resolved template on its `{placeholders}` instead, so a translator is free to
 * reorder them and the styled value still lands wherever they put it.
 *
 *   tNodes(t, 'key', 'Moving {name} to {board}.', { name: <b>A</b>, board: <b>B</b> })
 *
 * Returns an array of strings/nodes suitable for direct JSX rendering; unknown
 * placeholders are left verbatim so a typo is visible rather than silently blank.
 */
export function tNodes(t, key, fallback, nodes) {
    const template = t(key, fallback);
    return String(template)
        .split(/(\{\w+\})/g)
        .filter((part) => part !== '')
        .map((part, i) => {
            const m = part.match(/^\{(\w+)\}$/);
            if (m && m[1] in nodes) {
                const node = nodes[m[1]];
                return React.isValidElement(node)
                    ? React.cloneElement(node, { key: `${key}-${i}` })
                    : node;
            }
            return part;
        });
}

const LanguageContext = createContext({
    language: 'en',
    setLanguage: () => {},
    t: (key, fallbackOrVars, maybeVars) => translate('en', key, fallbackOrVars, maybeVars),
});

export function LanguageProvider({ children }) {
    const [language, setLanguageState] = useState(readInitial);

    const setLanguage = useCallback((lang) => {
        const next = lang === 'es' ? 'es' : 'en';
        setLanguageState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
            window.dispatchEvent(new CustomEvent('omnis:language-changed', { detail: { language: next } }));
        } catch (_) { /* ignore */ }
    }, []);

    // Keep multiple tabs / windows in sync.
    useEffect(() => {
        const onChange = (e) => {
            const next = e?.detail?.language || readInitial();
            setLanguageState(next);
        };
        const onStorage = (e) => { if (e.key === STORAGE_KEY) setLanguageState(readInitial()); };
        window.addEventListener('omnis:language-changed', onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('omnis:language-changed', onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    // Translation anomalies #486: tell the browser which language the page is in.
    // index.html ships `lang="en"`, so a Spanish UI used to be declared English and
    // the browser's auto-translate "translated" it from English — "Mensual" became
    // "Menstrual", "Exportar" became "Exportador". With the real language declared,
    // a Spanish-speaking browser sees nothing to translate.
    //
    // #481 (PO 2026-10-24): browser translation is blocked for the whole app —
    // index.html ships the three opt-out signals; re-assert the attribute + class
    // here so nothing that rewrites <html> can switch the translator back on.
    useEffect(() => {
        try {
            const root = document.documentElement;
            root.lang = language;
            root.setAttribute('translate', 'no');
            root.classList.add('notranslate');
        } catch (_) { /* no DOM */ }
    }, [language]);

    const t = useCallback(
        (key, fallbackOrVars, maybeVars) => translate(language, key, fallbackOrVars, maybeVars),
        [language],
    );

    const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    return useContext(LanguageContext);
}
